import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Calendar } from './components/ui/calendar';
import { Badge } from './components/ui/badge';
import { Textarea } from './components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { toast, Toaster } from 'sonner';
import PrivacyPolicy, { PrivacyNoticeStandalone } from './components/PrivacyPolicy';
import CookieConsent from './components/CookieConsent';
import { WhatsAppPanel } from './components/WhatsAppPanel';
import { DocumentsToSign } from './components/DocumentsToSign';
import { AdminSignedDocuments } from './components/AdminSignedDocuments';
import { MyAssignments } from './components/MyAssignments';
import GDPRSettings from './components/GDPRSettings';
import { GdprCompliancePage } from './components/GdprCompliancePage';
import { DeleteStaffDialog } from './components/DeleteStaffDialog';
import { ManualApproveDialog } from './components/ManualApproveDialog';
import { EditTimesheetDialog } from './components/EditTimesheetDialog';
import { DuplicateShiftDialog } from './components/DuplicateShiftDialog';

const defaultShiftPatterns = () => ([
  { name: 'Long Day', start_time: '08:00', end_time: '20:00' },
  { name: 'Night', start_time: '20:00', end_time: '08:00' },
  { name: 'Early', start_time: '08:00', end_time: '14:00' },
  { name: 'Late', start_time: '14:00', end_time: '20:00' },
]);
import { WorkerProfileDialog } from './components/WorkerProfileDialog';
import { CreateManualTimesheetDialog } from './components/CreateManualTimesheetDialog';
import '@/App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Utility functions
const formatDate = (date) => {
  return date.toISOString().split('T')[0];
};

const formatTime = (date) => {
  return date.toTimeString().slice(0, 5);
};

// Escape user-sourced values before injecting into generated HTML (XSS protection)
const escapeHtml = (v) => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

// Format date as DD/MM/YYYY
const formatDateDMY = (dateStr) => {
  if (!dateStr) return 'N/A';
  if (typeof dateStr === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
  try {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch {
    return dateStr;
  }
};

// Parse DD/MM/YYYY to YYYY-MM-DD for API
const parseDMYtoISO = (dmyStr) => {
  if (!dmyStr) return null;
  const match = dmyStr.match(/^(\d{2})[\/\-](\d{2})[\/\-](\d{4})$/);
  if (match) {
    return `${match[3]}-${match[2]}-${match[1]}`;
  }
  return dmyStr; // Return as-is if already ISO format
};

// Auth Context
const AuthContext = React.createContext();

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    const savedToken = localStorage.getItem('token');
    
    if (savedUser && savedToken) {
      setUser(JSON.parse(savedUser));
      setToken(savedToken);
    }
    setLoading(false);
  }, []); // Only run once on mount

  const login = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('token', authToken);
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// API Helper
const apiCall = async (endpoint, method = 'GET', data = null, token = null) => {
  const config = {
    method,
    url: `${API}${endpoint}`,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    },
    ...(data && { data })
  };

  try {
    const response = await axios(config);
    return response.data;
  } catch (error) {
    console.error('API Error:', error.response?.data || error.message);
    throw error.response?.data || error;
  }
};

// Login Component
const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'staff'
  });
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isLogin) {
        const response = await apiCall('/auth/login', 'POST', {
          email: formData.email,
          password: formData.password
        });
        login(response.user, response.access_token);
        toast.success('Login successful!');
      } else {
        if (!privacyAccepted) {
          toast.error('Please confirm you have read the Privacy Notice');
          return;
        }
        await apiCall('/auth/register', 'POST', { ...formData, privacy_acknowledged: true });
        toast.success('Registration successful! Your account is pending admin approval. You will be notified once approved.', { duration: 6000 });
        setIsLogin(true);
      }
    } catch (error) {
      toast.error(error.detail || 'Authentication failed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img 
              src="/koloth-logo.png" 
              alt="Koloth Care Logo" 
              className="h-16 w-auto"
            />
          </div>
          <p className="text-slate-600 mt-2">Professional Staffing Management</p>
        </CardHeader>
        <CardContent>
          <Tabs value={isLogin ? 'login' : 'register'} onValueChange={(value) => setIsLogin(value === 'login')}>
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
            </TabsList>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    data-testid="name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required={!isLogin}
                  />
                </div>
              )}
              
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>
              
              <div>
                <div className="flex justify-between items-center mb-1">
                  <Label htmlFor="password">Password</Label>
                  {isLogin && (
                    <button
                      type="button"
                      onClick={() => toast.info('Please contact your administrator to reset your password.')}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Forgot Password?
                    </button>
                  )}
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    data-testid="password-input"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>
              
              {!isLogin && (
                <div className="flex items-start gap-2 bg-slate-50 border rounded-md p-3">
                  <input
                    type="checkbox"
                    id="privacy-ack"
                    data-testid="privacy-ack-checkbox"
                    checked={privacyAccepted}
                    onChange={(e) => setPrivacyAccepted(e.target.checked)}
                    className="mt-0.5"
                  />
                  <label htmlFor="privacy-ack" className="text-xs text-slate-600">
                    I have read the{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline" data-testid="register-privacy-link">
                      Privacy Notice
                    </a>{' '}
                    explaining how Koloth Care uses my personal data.
                  </label>
                </div>
              )}
              
              <Button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700" data-testid="submit-button">
                {isLogin ? 'Login' : 'Register'}
              </Button>
              
              {isLogin && (
                <p className="text-center text-xs text-slate-500">
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline" data-testid="login-privacy-link">
                    Privacy Notice
                  </a>
                  {' '}— how we use your personal data
                </p>
              )}
            </form>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

// Dashboard Component
const Dashboard = () => {
  const { user, token } = useAuth();
  const [stats, setStats] = useState({});
  useEffect(() => {
    let mounted = true;
    
    const fetchStats = async () => {
      if (!token || !user) return;
      
      try {
        const data = await apiCall('/dashboard/stats', 'GET', null, token);
        if (mounted) {
          setStats(data);
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };

    fetchStats();
    
    return () => {
      mounted = false;
    };
  }, [token]);

  if (user.role === 'admin') {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Total Shifts</p>
                <p className="text-3xl font-bold text-blue-900" data-testid="total-shifts">{stats.total_shifts || 0}</p>
              </div>
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zM4 7h12v9H4V7z" />
                </svg>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Assigned Shifts</p>
                <p className="text-3xl font-bold text-green-900" data-testid="assigned-shifts">{stats.assigned_shifts || 0}</p>
              </div>
              <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-600">Shifts with Interest</p>
                <p className="text-3xl font-bold text-yellow-900" data-testid="shifts-with-interest">{stats.shifts_with_interest || 0}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600">Pending Timesheets</p>
                <p className="text-3xl font-bold text-orange-900" data-testid="pending-timesheets">{stats.pending_timesheets || 0}</p>
              </div>
              <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">Total Staff</p>
                <p className="text-3xl font-bold text-purple-900" data-testid="total-staff">{stats.total_staff || 0}</p>
              </div>
              <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                </svg>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  } else {
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">My Assigned Shifts</p>
                <p className="text-3xl font-bold text-blue-900" data-testid="my-shifts">{stats.my_shifts || 0}</p>
              </div>
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zM4 7h12v9H4V7z" />
                </svg>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-pink-50 to-pink-100 border-pink-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-pink-600">Interested Shifts</p>
                <p className="text-3xl font-bold text-pink-900" data-testid="interested-shifts">{stats.interested_shifts || 0}</p>
              </div>
              <div className="w-12 h-12 bg-pink-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600">Pending Timesheets</p>
                <p className="text-3xl font-bold text-orange-900" data-testid="my-pending-timesheets">{stats.my_pending_timesheets || 0}</p>
              </div>
              <div className="w-12 h-12 bg-orange-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Available Shifts</p>
                <p className="text-3xl font-bold text-green-900" data-testid="available-shifts">{stats.available_shifts || 0}</p>
              </div>
              <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
};

// Main App Layout
const MainLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showGDPRSettings, setShowGDPRSettings] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-3">
              <img 
                src="/koloth-logo.png" 
                alt="Koloth Care Logo" 
                className="h-12 w-auto"
                style={{ filter: 'brightness(0) invert(1)' }}
              />
              <Badge variant="secondary" className="capitalize">
                {user.role}
              </Badge>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-slate-600 hidden md:block">Welcome, {user.name}</span>
              
              {/* Privacy Settings Button */}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowGDPRSettings(true)}
                data-testid="privacy-settings-button"
                className="text-slate-600 hover:text-slate-800"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Privacy
              </Button>
              
              <Button variant="outline" onClick={logout} data-testid="logout-button">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
      
      {/* Footer */}
      <footer className="bg-white border-t mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-sm text-slate-600">
              © 2024 Koloth Care. All rights reserved.
            </div>
            <div className="flex space-x-4 text-sm">
              <button 
                onClick={() => setShowPrivacyPolicy(true)}
                className="text-blue-600 hover:text-blue-800 underline"
                data-testid="privacy-policy-link"
              >
                Privacy Policy
              </button>
              <button 
                onClick={() => setShowGDPRSettings(true)}
                className="text-blue-600 hover:text-blue-800 underline"
                data-testid="gdpr-settings-link"
              >
                Data Settings
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* GDPR Modals */}
      {showPrivacyPolicy && (
        <PrivacyPolicy onClose={() => setShowPrivacyPolicy(false)} />
      )}
      {showGDPRSettings && (
        <GDPRSettings onClose={() => setShowGDPRSettings(false)} />
      )}
    </div>
  );
};

// Admin Components
const AdminPanel = () => {
  const { token, user } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [clients, setClients] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [archivedStaff, setArchivedStaff] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payrollReport, setPayrollReport] = useState(null);
  const [payrollWeekEnding, setPayrollWeekEnding] = useState('');
  const [payrollPeriodType, setPayrollPeriodType] = useState('weekly'); // 'weekly' or 'monthly'
  const [payrollYear, setPayrollYear] = useState(new Date().getFullYear());
  const [payrollMonth, setPayrollMonth] = useState(new Date().getMonth() + 1);
  const [timesheetFilter, setTimesheetFilter] = useState('all');
  
  // Document upload modal state
  const [showDocUploadModal, setShowDocUploadModal] = useState(false);
  const [docUploadFile, setDocUploadFile] = useState(null);
  const [docUploadStaffId, setDocUploadStaffId] = useState(null);
  const [docUploadType, setDocUploadType] = useState('');
  const [docUploadDate, setDocUploadDate] = useState('');
  const [docUploadIsIssueDate, setDocUploadIsIssueDate] = useState(false);
  const [timesheetSearchTerm, setTimesheetSearchTerm] = useState('');
  const [timesheetDateFrom, setTimesheetDateFrom] = useState('');
  const [timesheetDateTo, setTimesheetDateTo] = useState('');
  const [complianceDashboard, setComplianceDashboard] = useState(null);
  
  // Self-booking state
  const [selfBookings, setSelfBookings] = useState([]);
  const [pendingBookingsCount, setPendingBookingsCount] = useState(0);
  const [showSelfBookingForm, setShowSelfBookingForm] = useState(false);
  const [editingSelfBooking, setEditingSelfBooking] = useState(null);
  const [selfBookingData, setSelfBookingData] = useState({
    client_name: '',
    location: '',
    date: '',
    start_time: '',
    end_time: '',
    role: 'nurse',
    booked_by: '',
    notes: ''
  });
  const [approvalData, setApprovalData] = useState({
    hourly_rate: '',
    admin_notes: ''
  });
  
  // Gmail settings state
  const [gmailStatus, setGmailStatus] = useState(null);
  const [gmailLoading, setGmailLoading] = useState(false);
  const [senderEmail, setSenderEmail] = useState('');
  
  // Mandatory trainings state
  const [mandatoryTrainings, setMandatoryTrainings] = useState([]);
  const [showAddTraining, setShowAddTraining] = useState(false);
  const [newTrainingData, setNewTrainingData] = useState({
    name: '',
    training_type: 'online',
    required_for: 'all',
    default_validity_years: 1
  });
  
  // Assignment Details state
  const [assignments, setAssignments] = useState([]);
  const [showCreateAssignment, setShowCreateAssignment] = useState(false);
  const [editingAssignmentId, setEditingAssignmentId] = useState(null);
  const [signedDocsByUser, setSignedDocsByUser] = useState({});
  const [adminActiveTab, setAdminActiveTab] = useState('shifts');

  const openAssignmentEditor = (assignment, forEdit = true) => {
    setNewAssignment({
      worker_id: assignment.worker_id,
      client_id: assignment.client_id,
      start_date: assignment.start_date || '',
      likely_duration: assignment.likely_duration || 'Ongoing',
      work_type: assignment.work_type || '',
      hours_per_shift: assignment.hours_per_shift || 12,
      break_duration_minutes: assignment.break_duration_minutes || 60,
      pay_rate: assignment.pay_rate || 0,
      pay_type: assignment.pay_type || 'paye',
      pay_interval: assignment.pay_interval || 'weekly',
      client_business_nature: assignment.client_business_nature || '',
      client_contact_on_arrival: assignment.client_contact_on_arrival || 'Reception staff on duty',
      work_location: assignment.work_location || '',
      qualifying_weeks_accrued: assignment.qualifying_weeks_accrued || '0',
      health_safety_risks: assignment.health_safety_risks || 'N/A',
      risk_mitigation_steps: assignment.risk_mitigation_steps || 'N/A',
      collective_facilities: assignment.collective_facilities || 'The same access as regular staff',
      annual_leave_days: assignment.annual_leave_days || 'Added to weekly pay',
      extended_hire_notice_weeks: assignment.extended_hire_notice_weeks || 12,
      extended_hire_period_weeks: assignment.extended_hire_period_weeks || 12,
      agency_contact_name: assignment.agency_contact_name || 'Jobin Joseph',
      agency_contact_company: assignment.agency_contact_company || 'Koloth Care Solutions Ltd',
      agency_contact_email: assignment.agency_contact_email || 'Jobin.joseph@koloth.com',
      agency_contact_phone: assignment.agency_contact_phone || '+447762190973',
      change_reason: ''
    });
    setEditingAssignmentId(forEdit ? assignment.id : null);
    setShowCreateAssignment(true);
    setAdminActiveTab('assignments');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePostAssignFlow = async (shift, staffId, staffName) => {
    try {
      const clientId = shift.client_id || '';
      const query = clientId ? `?client_id=${clientId}` : '';
      const existing = await apiCall(`/assignments/worker/${staffId}/active${query}`, 'GET', null, token);
      
      if (existing && existing.length > 0) {
        const a = existing[0];
        const hasChanges = window.confirm(
          `Repeat booking detected: ${staffName} already has Assignment Details (v${a.version}) for ${a.client_name}.\n\nAre there any changes to the assignment details?\n\n• OK = YES, there are changes (edit the form)\n• Cancel = NO changes (reuse the existing form)`
        );
        if (hasChanges) {
          openAssignmentEditor(a, true);
          toast.info('Update the details and save — a new version will be created and sent for re-confirmation');
        } else {
          await apiCall(`/assignments/${a.id}/link-shift`, 'POST', {
            shift_id: shift.id,
            assignment_id: a.id,
            has_changes: false
          }, token);
          toast.success(`No changes — shift linked to existing Assignment Details (v${a.version})`);
        }
      } else {
        const createNew = window.confirm(
          `${staffName} has no Assignment Details for this client yet.\n\nCreate the Assignment Details form now?`
        );
        if (createNew) {
          const client = clients.find(c => c.id === shift.client_id);
          openAssignmentEditor({
            worker_id: staffId,
            client_id: shift.client_id || '',
            start_date: shift.date || '',
            pay_rate: shift.hourly_pay || 0,
            work_location: client?.address || shift.location || ''
          }, false);
        }
      }
    } catch (error) {
      console.error('Post-assign assignment flow error:', error);
    }
  };
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [assignmentCheckResult, setAssignmentCheckResult] = useState(null);
  const [newAssignment, setNewAssignment] = useState({
    worker_id: '',
    client_id: '',
    start_date: '',
    likely_duration: 'Ongoing',
    work_type: '',
    hours_per_shift: 12,
    break_duration_minutes: 60,
    pay_rate: 0,
    pay_type: 'paye',
    pay_interval: 'weekly',
    client_business_nature: '',
    client_contact_on_arrival: 'Reception staff on duty',
    work_location: '',
    qualifying_weeks_accrued: '0',
    health_safety_risks: 'N/A',
    risk_mitigation_steps: 'N/A',
    collective_facilities: 'The same access as regular staff',
    annual_leave_days: 'Added to weekly pay',
    extended_hire_notice_weeks: 12,
    extended_hire_period_weeks: 12,
    agency_contact_name: 'Jobin Joseph',
    agency_contact_company: 'Koloth Care Solutions Ltd',
    agency_contact_email: 'Jobin.joseph@koloth.com',
    agency_contact_phone: '+447762190973'
  });
  
  // Ltd Company Payroll state
  const [ltdPayrollWeekEnding, setLtdPayrollWeekEnding] = useState('');
  const [ltdPayrollData, setLtdPayrollData] = useState(null);
  const [contractorInvoices, setContractorInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [renumberInvoice, setRenumberInvoice] = useState(null);
  
  const [showCreateShift, setShowCreateShift] = useState(false);
  const [showCreateClient, setShowCreateClient] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [editingStaff, setEditingStaff] = useState(null);
  const [newShift, setNewShift] = useState({
    title: '',
    date: '',
    start_time: '',
    end_time: '',
    location: '',
    client_id: '',
    staff_type: 'nurse',
    hourly_rate: 0,
    description: '',
    booked_by: '',
    booking_notes: ''
  });
  const [newClient, setNewClient] = useState({
    name: '',
    address: '',
    contact_person: '',
    phone: '',
    email: '',
    billing_group_name: '',
    billing_group_address: '',
    shift_patterns: defaultShiftPatterns(),
    pay_rates: {
      // Weekday
      nurses_weekday: 16.43,
      nurses_weeknight: 17.08,
      carers_weekday: 14.50,
      carers_weeknight: 15.25,
      
      // Saturday
      nurses_saturday_day: 17.08,
      nurses_saturday_night: 17.90,
      carers_saturday_day: 15.25,
      carers_saturday_night: 16.00,
      
      // Sunday
      nurses_sunday_day: 17.08,
      nurses_sunday_night: 17.90,
      carers_sunday_day: 15.25,
      carers_sunday_night: 16.00,
      
      // Bank Holiday
      nurses_bank_holiday_day: 28.00,
      nurses_bank_holiday_night: 32.00,
      carers_bank_holiday_day: 23.00,
      carers_bank_holiday_night: 27.00
    },
    required_trainings: []
  });
  const [showAddStaff, setShowAddStaff] = useState(false);
  const [newStaffData, setNewStaffData] = useState({
    name: '',
    email: '',
    password: ''
  });

  const fetchData = useCallback(async () => {
    if (!token || !user) return;
    
    // Fetch each endpoint independently so one failure doesn't break all
    try {
      const shiftsData = await apiCall('/shifts', 'GET', null, token).catch(err => {
        console.warn('Shifts fetch failed:', err);
        return [];
      });
      setShifts(shiftsData || []);
    } catch (e) { console.error('Shifts error:', e); }

    try {
      const timesheetsData = await apiCall('/timesheets', 'GET', null, token).catch(err => {
        console.warn('Timesheets fetch failed:', err);
        return [];
      });
      console.log('Admin fetched timesheets:', timesheetsData?.length || 0, timesheetsData);
      setTimesheets(timesheetsData || []);
    } catch (e) { console.error('Timesheets error:', e); }

    try {
      const clientsData = await apiCall('/clients', 'GET', null, token).catch(err => {
        console.warn('Clients fetch failed:', err);
        return [];
      });
      setClients(clientsData || []);
    } catch (e) { console.error('Clients error:', e); }

    try {
      const staffUsersData = await apiCall('/staff-users', 'GET', null, token).catch(err => {
        console.warn('Staff users fetch failed:', err);
        return [];
      });
      setStaffUsers(staffUsersData || []);
    } catch (e) { console.error('Staff users error:', e); }

    try {
      const signedDocsData = await apiCall('/admin/signed-documents', 'GET', null, token).catch(() => []);
      const byUser = {};
      (signedDocsData || []).forEach(s => { byUser[s.user_id] = s; });
      setSignedDocsByUser(byUser);
    } catch (e) { console.error('Signed docs error:', e); }

    try {
      const archivedStaffData = await apiCall('/admin/archived-staff', 'GET', null, token).catch(err => {
        console.warn('Archived staff fetch failed:', err);
        return [];
      });
      setArchivedStaff(archivedStaffData || []);
    } catch (e) { console.error('Archived staff error:', e); }

    try {
      const complianceData = await apiCall('/compliance/dashboard', 'GET', null, token).catch(err => {
        console.warn('Compliance dashboard fetch failed:', err);
        return null;
      });
      setComplianceDashboard(complianceData);
    } catch (e) { console.error('Compliance error:', e); }

    // Fetch self-bookings
    try {
      const selfBookingsData = await apiCall('/self-bookings', 'GET', null, token).catch(err => {
        console.warn('Self-bookings fetch failed:', err);
        return [];
      });
      setSelfBookings(selfBookingsData || []);
    } catch (e) { console.error('Self-bookings error:', e); }

    // Fetch pending bookings count for admin badge
    if (user?.role === 'admin') {
      try {
        const countData = await apiCall('/self-bookings/pending/count', 'GET', null, token).catch(err => {
          console.warn('Pending count fetch failed:', err);
          return { pending_count: 0 };
        });
        setPendingBookingsCount(countData?.pending_count || 0);
      } catch (e) { console.error('Pending count error:', e); }
      
      // Fetch Gmail status
      try {
        const gmailData = await apiCall('/oauth/gmail/status', 'GET', null, token).catch(err => {
          console.warn('Gmail status fetch failed:', err);
          return null;
        });
        setGmailStatus(gmailData);
        if (gmailData?.sender_email) {
          setSenderEmail(gmailData.sender_email);
        }
      } catch (e) { console.error('Gmail status error:', e); }
      
      // Fetch mandatory trainings
      try {
        const trainingsData = await apiCall('/mandatory-trainings', 'GET', null, token).catch(err => {
          console.warn('Mandatory trainings fetch failed:', err);
          return [];
        });
        setMandatoryTrainings(trainingsData || []);
      } catch (e) { console.error('Mandatory trainings error:', e); }
      
      // Fetch assignments
      try {
        const assignmentsData = await apiCall('/assignments', 'GET', null, token).catch(err => {
          console.warn('Assignments fetch failed:', err);
          return [];
        });
        setAssignments(assignmentsData || []);
      } catch (e) { console.error('Assignments error:', e); }
    }

    try {
      const invoicesData = await apiCall('/invoices', 'GET', null, token).catch(err => {
        console.warn('Invoices fetch failed:', err);
        return [];
      });
      setInvoices(invoicesData || []);
    } catch (e) { console.error('Invoices error:', e); }
  }, [token, user]);

  useEffect(() => {
    if (token && user) {
      fetchData();
    }
  }, [token, user]);

  // Check for Gmail OAuth callback URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const gmailConnected = urlParams.get('gmail_connected');
    const gmailEmail = urlParams.get('email');
    const gmailError = urlParams.get('gmail_error');
    
    if (gmailConnected === 'true' && gmailEmail) {
      toast.success(`Gmail connected successfully as ${gmailEmail}`);
      setGmailStatus({ connected: true, email: gmailEmail, configured: true });
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (gmailError) {
      toast.error(`Gmail connection failed: ${gmailError}`);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Manual refetch function for operations that need to refresh data
  const refetchData = () => {
    if (token && user) {
      fetchData();
    }
  };

  // Refresh the open staff editor in place (keeps the dialog open after training actions)
  const refreshEditingStaff = async (userId) => {
    refetchData();
    try {
      const profile = await apiCall(`/staff-profiles/${userId}`, 'GET', null, token);
      setEditingStaff((prev) => (prev && prev.user.id === userId ? { ...prev, profile } : prev));
    } catch (error) {
      console.error('Failed to refresh staff editor:', error);
    }
  };

  const handleCreateShift = async (e) => {
    e.preventDefault();
    try {
      await apiCall('/shifts', 'POST', { ...newShift, hourly_rate: parseFloat(newShift.hourly_rate) || 0 }, token);
      toast.success('Shift created successfully!');
      setShowCreateShift(false);
      setNewShift({
        title: '',
        date: '',
        start_time: '',
        end_time: '',
        location: '',
        client_id: '',
        staff_type: 'nurse',
        hourly_rate: 0,
        description: '',
        booked_by: '',
        booking_notes: ''
      });
      fetchData();
    } catch (error) {
      const d = error.detail;
      const msg = Array.isArray(d) ? d.map(x => `${x.loc?.[x.loc.length - 1]}: ${x.msg}`).join('; ') : d;
      toast.error(msg || 'Failed to create shift');
    }
  };

  // Self-Booking Handlers
  const handleSubmitSelfBooking = async (e) => {
    e.preventDefault();
    try {
      await apiCall('/self-bookings', 'POST', selfBookingData, token);
      toast.success('Self-booking submitted! Admin will review it shortly.');
      setShowSelfBookingForm(false);
      setSelfBookingData({
        client_name: '',
        client_id: '',
        location: '',
        date: '',
        start_time: '',
        end_time: '',
        role: 'nurse',
        booked_by: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.detail || 'Failed to submit self-booking');
    }
  };

  const handleApproveSelfBooking = async (bookingId) => {
    if (!approvalData.hourly_rate || parseFloat(approvalData.hourly_rate) <= 0) {
      toast.error('Please enter a valid hourly rate');
      return;
    }
    try {
      await apiCall('/self-bookings/review', 'POST', {
        booking_id: bookingId,
        action: 'approve',
        hourly_rate: parseFloat(approvalData.hourly_rate),
        admin_notes: approvalData.admin_notes
      }, token);
      toast.success('Self-booking approved! Shift created.');
      setEditingSelfBooking(null);
      setApprovalData({ hourly_rate: '', admin_notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.detail || 'Failed to approve self-booking');
    }
  };

  const handleRejectSelfBooking = async (bookingId) => {
    try {
      await apiCall('/self-bookings/review', 'POST', {
        booking_id: bookingId,
        action: 'reject',
        admin_notes: approvalData.admin_notes
      }, token);
      toast.success('Self-booking rejected.');
      setEditingSelfBooking(null);
      setApprovalData({ hourly_rate: '', admin_notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.detail || 'Failed to reject self-booking');
    }
  };

  const handleUpdateSelfBooking = async (bookingId, updateData) => {
    try {
      await apiCall(`/self-bookings/${bookingId}`, 'PUT', updateData, token);
      toast.success('Self-booking updated');
      fetchData();
    } catch (error) {
      toast.error(error.detail || 'Failed to update self-booking');
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      const sanitizedRates = Object.fromEntries(
        Object.entries(newClient.pay_rates || {}).map(([k, v]) => [k, parseFloat(v) || 0])
      );
      const payload = { ...newClient, pay_rates: sanitizedRates };
      if (editingClient) {
        // Update existing client
        await apiCall(`/clients/${editingClient.id}`, 'PUT', payload, token);
        toast.success('Client updated successfully!');
      } else {
        // Create new client
        await apiCall('/clients', 'POST', payload, token);
        toast.success('Client created successfully!');
      }
      
      setShowCreateClient(false);
      setEditingClient(null);
      setNewClient({
        name: '',
        address: '',
        contact_person: '',
        phone: '',
        email: '',
        billing_group_name: '',
        billing_group_address: '',
        shift_patterns: defaultShiftPatterns(),
        pay_rates: {
          // Weekday
          nurses_weekday: 16.43,
          nurses_weeknight: 17.08,
          carers_weekday: 14.50,
          carers_weeknight: 15.25,
          
          // Saturday
          nurses_saturday_day: 17.08,
          nurses_saturday_night: 17.90,
          carers_saturday_day: 15.25,
          carers_saturday_night: 16.00,
          
          // Sunday
          nurses_sunday_day: 17.08,
          nurses_sunday_night: 17.90,
          carers_sunday_day: 15.25,
          carers_sunday_night: 16.00,
          
          // Bank Holiday
          nurses_bank_holiday_day: 28.00,
          nurses_bank_holiday_night: 32.00,
          carers_bank_holiday_day: 23.00,
          carers_bank_holiday_night: 27.00
        }
      });
      fetchData();
    } catch (error) {
      toast.error(error.detail || `Failed to ${editingClient ? 'update' : 'create'} client`);
    }
  };

  const handleAddStaff = async (e) => {
    e.preventDefault();
    try {
      const response = await apiCall('/auth/register', 'POST', {
        name: newStaffData.name,
        email: newStaffData.email,
        password: newStaffData.password
      });
      toast.success('Staff member added successfully!');
      setShowAddStaff(false);
      setNewStaffData({ name: '', email: '', password: '' });
      
      // Manually fetch and update staff users
      try {
        const staffUsersData = await apiCall('/staff-users', 'GET', null, token);
        setStaffUsers(staffUsersData || []);
      } catch (fetchError) {
        console.error('Failed to refresh staff list:', fetchError);
        // Still show success since staff was added
      }
    } catch (error) {
      console.error('Add staff error:', error);
      toast.error(error.detail || 'Failed to add staff member');
    }
  };


  const generateInvoice = async (clientId, weekEnding, startDate, invoiceNumber) => {
    try {
      let qs = `?week_ending=${weekEnding}`;
      if (startDate) qs += `&start_date=${startDate}`;
      if (invoiceNumber) qs += `&invoice_number=${invoiceNumber}`;
      await apiCall(`/invoices/generate/${clientId}${qs}`, 'POST', null, token);
      toast.success('Invoice generated successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.detail || 'Failed to generate invoice', { duration: 12000 });
    }
  };

  const handleUpdateStaffProfile = async (userId, profileData) => {
    try {
      await apiCall(`/staff-profiles/${userId}`, 'PUT', profileData, token);
      toast.success('Staff profile updated successfully!');
      refreshEditingStaff(userId);
    } catch (error) {
      toast.error(error.detail || 'Failed to update profile');
    }
  };

  const handleApproveTimesheet = async (timesheetId, status) => {
    try {
      await apiCall('/timesheets/approve', 'POST', {
        timesheet_id: timesheetId,
        status: status
      }, token);
      toast.success(`Timesheet ${status}!`);
      fetchData();
    } catch (error) {
      toast.error(error.detail || 'Failed to update timesheet');
    }
  };

  return (
    <MainLayout>
      <Dashboard />
      
      {/* Pending Approval Alert */}
      {(() => {
        const pendingStaff = staffUsers.filter(s => !s.user.approved);
        if (pendingStaff.length > 0) {
          return (
            <div className="mb-6">
              <Card className="border-blue-300 bg-blue-50">
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-blue-900 mb-2">Pending Staff Approvals ({pendingStaff.length})</h4>
                      <div className="space-y-2">
                        {pendingStaff.map((staff, index) => (
                          <div key={index} className="text-sm p-2 rounded bg-blue-100 text-blue-900">
                            <span className="font-medium">{staff.user.name}</span> ({staff.user.email}) - Registered on {formatDateDMY(staff.user.created_at)}
                          </div>
                        ))}
                      </div>
                      <p className="text-sm mt-2 text-blue-800">Go to Staff Management to approve these accounts.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          );
        }
        return null;
      })()}
      
      {/* Training Expiration Alerts */}
      {(() => {
        const expiringTraining = [];
        staffUsers.forEach(staffData => {
          if (staffData.profile?.training_records) {
            staffData.profile.training_records.forEach(training => {
              if (training.expiry_date) {
                const daysUntilExpiry = Math.ceil((new Date(training.expiry_date) - new Date()) / (1000 * 60 * 60 * 24));
                if (daysUntilExpiry <= 30 && daysUntilExpiry >= 0) {
                  expiringTraining.push({
                    staffName: staffData.user.name,
                    trainingName: training.training_name,
                    expiryDate: training.expiry_date,
                    daysLeft: daysUntilExpiry
                  });
                } else if (daysUntilExpiry < 0) {
                  expiringTraining.push({
                    staffName: staffData.user.name,
                    trainingName: training.training_name,
                    expiryDate: training.expiry_date,
                    daysLeft: daysUntilExpiry,
                    expired: true
                  });
                }
              }
            });
          }
        });
        
        if (expiringTraining.length > 0) {
          return (
            <>
      {/* Compliance Dashboard */}
      {complianceDashboard && complianceDashboard.total_issues > 0 && (
        <div className="mb-6">
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <svg className="w-8 h-8 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-orange-900 mb-3 text-lg">⚠️ Compliance Alerts</h4>
                  
                  {/* Summary Stats */}
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    {complianceDashboard.expired?.length > 0 && (
                      <div className="bg-red-100 p-3 rounded text-center">
                        <div className="text-2xl font-bold text-red-700">{complianceDashboard.expired.length}</div>
                        <div className="text-xs text-red-600">Expired</div>
                      </div>
                    )}
                    {complianceDashboard.critical?.length > 0 && (
                      <div className="bg-red-100 p-3 rounded text-center">
                        <div className="text-2xl font-bold text-red-700">{complianceDashboard.critical.length}</div>
                        <div className="text-xs text-red-600">Critical (&lt;30d)</div>
                      </div>
                    )}
                    {complianceDashboard.warning?.length > 0 && (
                      <div className="bg-orange-100 p-3 rounded text-center">
                        <div className="text-2xl font-bold text-orange-700">{complianceDashboard.warning.length}</div>
                        <div className="text-xs text-orange-600">Warning (30-60d)</div>
                      </div>
                    )}
                    {complianceDashboard.notice?.length > 0 && (
                      <div className="bg-yellow-100 p-3 rounded text-center">
                        <div className="text-2xl font-bold text-yellow-700">{complianceDashboard.notice.length}</div>
                        <div className="text-xs text-yellow-600">Notice (60-90d)</div>
                      </div>
                    )}
                  </div>
                  
                  {/* Expired Items */}
                  {complianceDashboard.expired?.length > 0 && (
                    <div className="mb-3">
                      <h5 className="font-semibold text-red-900 mb-2">🔴 Expired ({complianceDashboard.expired.length})</h5>
                      <div className="space-y-1">
                        {complianceDashboard.expired.map((item, idx) => (
                          <div key={idx} className="text-sm p-2 rounded bg-red-100 text-red-900">
                            <span className="font-medium">{item.staff_name}</span> - {item.item}
                            <span className="ml-2 font-bold">(Overdue by {item.days_overdue} days)</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Critical Items */}
                  {complianceDashboard.critical?.length > 0 && (
                    <div className="mb-3">
                      <h5 className="font-semibold text-red-900 mb-2">🔴 Critical - Expiring Soon ({complianceDashboard.critical.length})</h5>
                      <div className="space-y-1">
                        {complianceDashboard.critical.map((item, idx) => (
                          <div key={idx} className="text-sm p-2 rounded bg-red-100 text-red-900">
                            <span className="font-medium">{item.staff_name}</span> - {item.item}
                            <span className="ml-2 font-bold">(Expires in {item.days_remaining} days)</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Warning Items */}
                  {complianceDashboard.warning?.length > 0 && (
                    <div>
                      <h5 className="font-semibold text-orange-900 mb-2">🟠 Warning ({complianceDashboard.warning.length})</h5>
                      <div className="space-y-1">
                        {complianceDashboard.warning.slice(0, 5).map((item, idx) => (
                          <div key={idx} className="text-sm p-2 rounded bg-orange-100 text-orange-900">
                            <span className="font-medium">{item.staff_name}</span> - {item.item}
                            <span className="ml-2">(Expires in {item.days_remaining} days)</span>
                          </div>
                        ))}
                        {complianceDashboard.warning.length > 5 && (
                          <p className="text-xs text-orange-700 mt-1">...and {complianceDashboard.warning.length - 5} more</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
            </>
          );
        }
        return null;
      })()}
      
      <Tabs value={adminActiveTab} onValueChange={setAdminActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-8 bg-white">
          <TabsTrigger value="shifts" data-testid="shifts-tab">Manage Shifts</TabsTrigger>
          <TabsTrigger value="approvals" data-testid="approvals-tab" className="relative">
            Shift Approvals
            {pendingBookingsCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                {pendingBookingsCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="assignments" data-testid="assignments-tab">Assignments</TabsTrigger>
          <TabsTrigger value="timesheets" data-testid="timesheets-tab">Review Timesheets</TabsTrigger>
          <TabsTrigger value="payroll" data-testid="payroll-tab">Payroll</TabsTrigger>
          <TabsTrigger value="compliance" data-testid="compliance-tab" className="relative">
            Compliance
            {complianceDashboard && complianceDashboard.total_issues > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {complianceDashboard.total_issues}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="new-features" data-testid="new-features-tab">Staff & Clients</TabsTrigger>
          <TabsTrigger value="gdpr" data-testid="gdpr-tab">GDPR</TabsTrigger>
        </TabsList>

        {/* GDPR Compliance Tab */}
        <TabsContent value="gdpr" className="space-y-6">
          <h2 className="text-2xl font-semibold text-slate-800">GDPR Compliance</h2>
          <GdprCompliancePage token={token} apiCall={apiCall} />
        </TabsContent>

        {/* Shift Approvals Tab - New Self-Booking Feature */}
        <TabsContent value="approvals" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold text-slate-800">Shift Approvals</h2>
            <Badge variant={pendingBookingsCount > 0 ? "destructive" : "secondary"}>
              {pendingBookingsCount} Pending
            </Badge>
          </div>

          {selfBookings.filter(b => b.status === 'pending').length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <p className="text-slate-500">No pending self-booking requests.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {selfBookings.filter(b => b.status === 'pending').map((booking) => (
                <Card key={booking.id} className="border-l-4 border-l-orange-500">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-lg">{booking.staff_name}</h3>
                          <Badge variant="outline">{booking.role}</Badge>
                          <Badge variant="secondary" className="bg-orange-100 text-orange-800">Pending</Badge>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-slate-600">
                          <div>
                            <span className="font-medium">Client/Hospital:</span>
                            <p>{booking.client_name}</p>
                          </div>
                          <div>
                            <span className="font-medium">Location:</span>
                            <p>{booking.location}</p>
                          </div>
                          <div>
                            <span className="font-medium">Date:</span>
                            <p>{formatDateDMY(booking.date)}</p>
                          </div>
                          <div>
                            <span className="font-medium">Time:</span>
                            <p>{booking.start_time} - {booking.end_time}</p>
                          </div>
                          {booking.booked_by && (
                            <div>
                              <span className="font-medium">Booked By:</span>
                              <p data-testid={`booking-booked-by-${booking.id}`}>{booking.booked_by}</p>
                            </div>
                          )}
                        </div>
                        {booking.notes && (
                          <p className="text-sm text-slate-500 mt-2">
                            <span className="font-medium">Notes:</span> {booking.notes}
                          </p>
                        )}
                        <p className="text-xs text-slate-400 mt-2">
                          Submitted: {new Date(booking.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    {/* Approval Form */}
                    {editingSelfBooking === booking.id ? (
                      <div className="mt-4 p-4 bg-slate-50 rounded-lg space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor={`rate-${booking.id}`}>Hourly Rate (£) *</Label>
                            <Input
                              id={`rate-${booking.id}`}
                              type="number"
                              step="0.01"
                              placeholder="e.g. 18.50"
                              value={approvalData.hourly_rate}
                              onChange={(e) => setApprovalData({...approvalData, hourly_rate: e.target.value})}
                              data-testid={`approval-rate-${booking.id}`}
                            />
                          </div>
                          <div>
                            <Label htmlFor={`notes-${booking.id}`}>Admin Notes (optional)</Label>
                            <Input
                              id={`notes-${booking.id}`}
                              placeholder="Note for staff member"
                              value={approvalData.admin_notes}
                              onChange={(e) => setApprovalData({...approvalData, admin_notes: e.target.value})}
                              data-testid={`approval-notes-${booking.id}`}
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleApproveSelfBooking(booking.id)}
                            className="bg-green-600 hover:bg-green-700"
                            data-testid={`approve-booking-${booking.id}`}
                          >
                            ✓ Approve
                          </Button>
                          <Button
                            onClick={() => handleRejectSelfBooking(booking.id)}
                            variant="destructive"
                            data-testid={`reject-booking-${booking.id}`}
                          >
                            ✗ Reject
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => {
                              setEditingSelfBooking(null);
                              setApprovalData({ hourly_rate: '', admin_notes: '' });
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-4 flex gap-2">
                        <Button
                          onClick={() => setEditingSelfBooking(booking.id)}
                          className="bg-blue-600 hover:bg-blue-700"
                          data-testid={`review-booking-${booking.id}`}
                        >
                          Review & Decide
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Show approved/rejected bookings */}
          {selfBookings.filter(b => b.status !== 'pending').length > 0 && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-slate-700 mb-4">Recent Decisions</h3>
              <div className="space-y-2">
                {selfBookings.filter(b => b.status !== 'pending').slice(0, 10).map((booking) => (
                  <Card key={booking.id} className={`border-l-4 ${booking.status === 'approved' ? 'border-l-green-500' : 'border-l-red-500'}`}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="font-medium">{booking.staff_name}</span>
                          <span className="text-slate-500 mx-2">•</span>
                          <span>{booking.client_name}</span>
                          <span className="text-slate-500 mx-2">•</span>
                          <span>{formatDateDMY(booking.date)}</span>
                        </div>
                        <Badge variant={booking.status === 'approved' ? 'default' : 'destructive'} 
                               className={booking.status === 'approved' ? 'bg-green-600' : ''}>
                          {booking.status === 'approved' ? `Approved @ £${booking.hourly_rate}/hr` : 'Rejected'}
                        </Badge>
                      </div>
                      {booking.admin_notes && (
                        <p className="text-sm text-slate-500 mt-1">Note: {booking.admin_notes}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Assignments Tab */}
        <TabsContent value="assignments" className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-semibold text-slate-800">Assignment Details</h2>
              <p className="text-sm text-slate-500">Manage worker assignments as per Agency Workers Regulations</p>
            </div>
            <Button 
              onClick={() => setShowCreateAssignment(true)}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
              data-testid="create-assignment-btn"
            >
              + New Assignment
            </Button>
          </div>

          {/* Create/Edit Assignment Modal */}
          {showCreateAssignment && (
            <Card className="border-2 border-purple-200">
              <CardHeader>
                <CardTitle>{editingAssignmentId ? 'Edit Assignment Details (creates a new version)' : 'Create Assignment Details Form'}</CardTitle>
                <p className="text-sm text-slate-500">Complete the assignment details for Agency Workers Regulations compliance</p>
              </CardHeader>
              <CardContent>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    if (editingAssignmentId) {
                      await apiCall(`/assignments/${editingAssignmentId}`, 'PUT', {
                        ...newAssignment,
                        change_reason: newAssignment.change_reason || 'Details updated by admin'
                      }, token);
                      toast.success('Assignment updated — new version created');
                    } else {
                      await apiCall('/assignments', 'POST', newAssignment, token);
                      toast.success('Assignment created successfully');
                    }
                    setShowCreateAssignment(false);
                    setEditingAssignmentId(null);
                    setNewAssignment({
                      worker_id: '',
                      client_id: '',
                      start_date: '',
                      likely_duration: 'Ongoing',
                      work_type: '',
                      hours_per_shift: 12,
                      break_duration_minutes: 60,
                      pay_rate: 0,
                      pay_type: 'paye',
                      pay_interval: 'weekly',
                      client_business_nature: '',
                      client_contact_on_arrival: 'Reception staff on duty',
                      work_location: '',
                      qualifying_weeks_accrued: '0',
                      health_safety_risks: 'N/A',
                      risk_mitigation_steps: 'N/A',
                      collective_facilities: 'The same access as regular staff',
                      annual_leave_days: 'Added to weekly pay',
                      extended_hire_notice_weeks: 12,
                      extended_hire_period_weeks: 12,
                      agency_contact_name: 'Jobin Joseph',
                      agency_contact_company: 'Koloth Care Solutions Ltd',
                      agency_contact_email: 'Jobin.joseph@koloth.com',
                      agency_contact_phone: '+447762190973'
                    });
                    fetchData();
                  } catch (error) {
                    toast.error(error.detail || 'Failed to save assignment');
                  }
                }} className="space-y-6">
                  
                  {editingAssignmentId && (
                    <div>
                      <Label className="text-purple-700 font-semibold">Reason for Change *</Label>
                      <Input
                        value={newAssignment.change_reason || ''}
                        onChange={(e) => setNewAssignment({...newAssignment, change_reason: e.target.value})}
                        placeholder="e.g. Updated health & safety risks"
                        required
                        data-testid="assignment-change-reason"
                      />
                    </div>
                  )}
                  
                  {/* Worker and Client Selection */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Agency Worker *</Label>
                      <Select 
                        value={newAssignment.worker_id} 
                        onValueChange={(v) => setNewAssignment({...newAssignment, worker_id: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select worker" />
                        </SelectTrigger>
                        <SelectContent>
                          {staffUsers.filter(s => s.user.approved).map((staff) => (
                            <SelectItem key={staff.user.id} value={staff.user.id}>
                              {staff.user.name} ({staff.profile?.employment_type === 'limited_company' ? 'Ltd Co' : 'PAYE'})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Hirer (Client) *</Label>
                      <Select 
                        value={newAssignment.client_id} 
                        onValueChange={(v) => {
                          const client = clients.find(c => c.id === v);
                          setNewAssignment({
                            ...newAssignment, 
                            client_id: v,
                            work_location: client?.address || ''
                          });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select client" />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.map((client) => (
                            <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Assignment Details */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-slate-700">Assignment Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label>Start Date *</Label>
                        <Input
                          type="date"
                          value={newAssignment.start_date}
                          onChange={(e) => setNewAssignment({...newAssignment, start_date: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label>Duration</Label>
                        <Select 
                          value={newAssignment.likely_duration} 
                          onValueChange={(v) => setNewAssignment({...newAssignment, likely_duration: v})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Ongoing">Ongoing</SelectItem>
                            <SelectItem value="Fixed term">Fixed Term</SelectItem>
                            <SelectItem value="Temporary">Temporary</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Type of Work *</Label>
                        <Select 
                          value={newAssignment.work_type} 
                          onValueChange={(v) => setNewAssignment({...newAssignment, work_type: v})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select work type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Registered Nurse">Registered Nurse</SelectItem>
                            <SelectItem value="Support Worker">Support Worker</SelectItem>
                            <SelectItem value="Healthcare Assistant">Healthcare Assistant</SelectItem>
                            <SelectItem value="Senior Carer">Senior Carer</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Hours Per Shift</Label>
                        <Input
                          type="number"
                          step="0.25"
                          value={newAssignment.hours_per_shift}
                          onChange={(e) => setNewAssignment({...newAssignment, hours_per_shift: parseFloat(e.target.value)})}
                        />
                      </div>
                      <div>
                        <Label>Break Duration (minutes, unpaid)</Label>
                        <Input
                          type="number"
                          value={newAssignment.break_duration_minutes}
                          onChange={(e) => setNewAssignment({...newAssignment, break_duration_minutes: parseInt(e.target.value)})}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label>Nature of Hirer&apos;s Business</Label>
                      <Input
                        value={newAssignment.client_business_nature}
                        onChange={(e) => setNewAssignment({...newAssignment, client_business_nature: e.target.value})}
                        placeholder="e.g. Neuropsychiatric Service, Care Home"
                      />
                    </div>
                    
                    <div>
                      <Label>Contact to Report to on Arrival</Label>
                      <Input
                        value={newAssignment.client_contact_on_arrival}
                        onChange={(e) => setNewAssignment({...newAssignment, client_contact_on_arrival: e.target.value})}
                        placeholder="e.g. Reception staff on duty"
                      />
                    </div>
                  </div>

                  {/* AWR Details (Agency Workers Regulations) */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-slate-700">Agency Workers Regulations (AWR) Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Location of Work</Label>
                        <Input
                          value={newAssignment.work_location}
                          onChange={(e) => setNewAssignment({...newAssignment, work_location: e.target.value})}
                          placeholder="Leave blank to use client's address"
                          data-testid="assignment-work-location"
                        />
                      </div>
                      <div>
                        <Label>Calendar Weeks Already Accrued (AWR regs 7 & 8)</Label>
                        <Input
                          value={newAssignment.qualifying_weeks_accrued}
                          onChange={(e) => setNewAssignment({...newAssignment, qualifying_weeks_accrued: e.target.value})}
                          placeholder="e.g. 0"
                          data-testid="assignment-qualifying-weeks"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Known Health & Safety Risks</Label>
                      <Textarea
                        value={newAssignment.health_safety_risks}
                        onChange={(e) => setNewAssignment({...newAssignment, health_safety_risks: e.target.value})}
                        rows={2}
                        data-testid="assignment-hs-risks"
                      />
                    </div>
                    <div>
                      <Label>Steps the Hirer Has Taken to Reduce the Risks</Label>
                      <Textarea
                        value={newAssignment.risk_mitigation_steps}
                        onChange={(e) => setNewAssignment({...newAssignment, risk_mitigation_steps: e.target.value})}
                        rows={2}
                        data-testid="assignment-risk-mitigation"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Collective Facilities Available to the Agency Worker</Label>
                        <Input
                          value={newAssignment.collective_facilities}
                          onChange={(e) => setNewAssignment({...newAssignment, collective_facilities: e.target.value})}
                          data-testid="assignment-collective-facilities"
                        />
                      </div>
                      <div>
                        <Label>Annual Leave</Label>
                        <Input
                          value={newAssignment.annual_leave_days}
                          onChange={(e) => setNewAssignment({...newAssignment, annual_leave_days: e.target.value})}
                          data-testid="assignment-annual-leave"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Notice Period for Extended Hire (weeks)</Label>
                        <Input
                          type="number"
                          value={newAssignment.extended_hire_notice_weeks}
                          onChange={(e) => setNewAssignment({...newAssignment, extended_hire_notice_weeks: parseInt(e.target.value) || 12})}
                          data-testid="assignment-extended-hire-notice"
                        />
                      </div>
                      <div>
                        <Label>Extended Hire Period to Avoid Transfer Fee (weeks)</Label>
                        <Input
                          type="number"
                          value={newAssignment.extended_hire_period_weeks}
                          onChange={(e) => setNewAssignment({...newAssignment, extended_hire_period_weeks: parseInt(e.target.value) || 12})}
                          data-testid="assignment-extended-hire-period"
                        />
                      </div>
                    </div>
                    <div>
                      <h5 className="font-medium text-sm text-slate-600 mb-2">Recruitment Consultant Contact Details</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Contact Name</Label>
                          <Input
                            value={newAssignment.agency_contact_name}
                            onChange={(e) => setNewAssignment({...newAssignment, agency_contact_name: e.target.value})}
                            data-testid="assignment-agency-name"
                          />
                        </div>
                        <div>
                          <Label>Company</Label>
                          <Input
                            value={newAssignment.agency_contact_company}
                            onChange={(e) => setNewAssignment({...newAssignment, agency_contact_company: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Email</Label>
                          <Input
                            value={newAssignment.agency_contact_email}
                            onChange={(e) => setNewAssignment({...newAssignment, agency_contact_email: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Phone</Label>
                          <Input
                            value={newAssignment.agency_contact_phone}
                            onChange={(e) => setNewAssignment({...newAssignment, agency_contact_phone: e.target.value})}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pay Details */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-slate-700">Pay Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label>Pay Rate (£/hr) *</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={newAssignment.pay_rate}
                          onChange={(e) => setNewAssignment({...newAssignment, pay_rate: parseFloat(e.target.value)})}
                          required
                        />
                      </div>
                      <div>
                        <Label>Pay Type</Label>
                        <Select 
                          value={newAssignment.pay_type} 
                          onValueChange={(v) => setNewAssignment({...newAssignment, pay_type: v})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="paye">PAYE</SelectItem>
                            <SelectItem value="limited_company">Limited Company Invoice</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Payment Interval</Label>
                        <Select 
                          value={newAssignment.pay_interval} 
                          onValueChange={(v) => setNewAssignment({...newAssignment, pay_interval: v})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-2">
                    <Button type="button" variant="outline" onClick={() => { setShowCreateAssignment(false); setEditingAssignmentId(null); }}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-purple-600 hover:bg-purple-700" data-testid="assignment-form-submit">
                      {editingAssignmentId ? 'Save New Version' : 'Create Assignment'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Assignments List */}
          <div className="space-y-4">
            {assignments.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <p className="text-slate-500">No assignment details created yet.</p>
                  <p className="text-sm text-slate-400 mt-2">Create an assignment when assigning a worker to a new client.</p>
                </CardContent>
              </Card>
            ) : (
              assignments.map((assignment) => (
                <Card key={assignment.id} className="border-l-4 border-l-purple-500">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-lg">{assignment.worker_name}</h3>
                          <Badge variant="outline" className="bg-purple-50">
                            v{assignment.version}
                          </Badge>
                          <Badge variant={assignment.status === 'active' ? 'default' : 'secondary'}
                                 className={assignment.status === 'active' ? 'bg-green-600' : ''}>
                            {assignment.status}
                          </Badge>
                          {assignment.pay_type === 'limited_company' && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                              Ltd Co
                            </Badge>
                          )}
                          {assignment.acknowledged ? (
                            <Badge className="bg-green-600" data-testid={`assignment-ack-badge-${assignment.id}`}>✓ Receipt Confirmed</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300">Awaiting Receipt</Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mt-3">
                          <div>
                            <span className="text-slate-500">Client:</span>
                            <p className="font-medium">{assignment.client_name}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Work Type:</span>
                            <p className="font-medium">{assignment.work_type}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Start Date:</span>
                            <p className="font-medium">{formatDateDMY(assignment.start_date)}</p>
                          </div>
                          <div>
                            <span className="text-slate-500">Pay Rate:</span>
                            <p className="font-medium">£{assignment.pay_rate}/hr ({assignment.pay_type?.toUpperCase()})</p>
                          </div>
                        </div>
                        <div className="mt-2 text-sm text-slate-500">
                          {assignment.linked_shift_ids?.length || 0} shifts linked • 
                          Duration: {assignment.likely_duration} • 
                          {assignment.hours_per_shift}hrs/shift
                        </div>
                      </div>
                      <div className="flex flex-col space-y-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={async () => {
                            try {
                              const docData = await apiCall(`/assignments/${assignment.id}/document`, 'GET', null, token);
                              setSelectedAssignment({ ...assignment, document: docData });
                            } catch (error) {
                              toast.error('Failed to load document');
                            }
                          }}
                        >
                          View Document
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={async () => {
                            try {
                              const auditData = await apiCall(`/assignments/${assignment.id}/audit-trail`, 'GET', null, token);
                              setSelectedAssignment({ ...assignment, audit: auditData });
                            } catch (error) {
                              toast.error('Failed to load audit trail');
                            }
                          }}
                        >
                          Audit Trail
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-purple-700 border-purple-300"
                          data-testid={`assignment-edit-btn-${assignment.id}`}
                          onClick={() => openAssignmentEditor(assignment, true)}
                        >
                          ✏️ Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`assignment-pdf-btn-${assignment.id}`}
                          onClick={async () => {
                            try {
                              const res = await apiCall(`/assignments/${assignment.id}/pdf`, 'GET', null, token);
                              window.open(`${BACKEND_URL}${res.pdf_url}`, '_blank');
                            } catch (error) {
                              toast.error('Failed to generate PDF');
                            }
                          }}
                        >
                          📄 PDF
                        </Button>
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          data-testid={`assignment-send-whatsapp-${assignment.id}`}
                          onClick={async () => {
                            try {
                              const res = await apiCall(`/assignments/${assignment.id}/send`, 'POST', { channels: ['whatsapp'] }, token);
                              const status = res.results?.whatsapp || 'unknown';
                              if (status === 'sent') toast.success('Sent to staff on WhatsApp');
                              else toast.error(`WhatsApp: ${status}`);
                            } catch (error) {
                              toast.error('Failed to send');
                            }
                          }}
                        >
                          Send WhatsApp
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-blue-700 border-blue-300"
                          data-testid={`assignment-send-email-${assignment.id}`}
                          onClick={async () => {
                            try {
                              const res = await apiCall(`/assignments/${assignment.id}/send`, 'POST', { channels: ['email'] }, token);
                              if (res.results?.email === 'sent') toast.success('Emailed to staff');
                              else toast.error(`Email: ${res.results?.email || 'failed'}`);
                            } catch (error) {
                              toast.error('Failed to send email');
                            }
                          }}
                        >
                          Send Email
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Assignment Document Preview Modal */}
          {selectedAssignment?.document && (
            <Card className="border-2 border-purple-300 mt-4">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Assignment Details Document</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setSelectedAssignment(null)}>✕</Button>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="text-center border-b pb-4">
                  <h2 className="text-xl font-bold">ADDITIONAL DOCUMENT - ASSIGNMENT DETAILS FORM</h2>
                  <p className="text-sm text-slate-500">Koloth Care Solutions Limited</p>
                </div>
                
                {selectedAssignment.document.sections?.map((section, idx) => (
                  <div key={idx} className="border-b pb-4">
                    <h3 className="font-semibold text-purple-700 mb-3">{section.heading}</h3>
                    <div className="space-y-2">
                      {section.fields?.map((field, fidx) => (
                        <div key={fidx} className="grid grid-cols-3 gap-2">
                          <span className="text-slate-600">{field.label}:</span>
                          <span className="col-span-2 font-medium">{field.value || 'N/A'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => window.print()}>
                    Print Document
                  </Button>
                  <Button onClick={() => setSelectedAssignment(null)}>
                    Close
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Audit Trail Modal */}
          {selectedAssignment?.audit && (
            <Card className="border-2 border-blue-300 mt-4">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Assignment Audit Trail</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setSelectedAssignment(null)}>✕</Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-sm text-slate-500">
                    Total Versions: {selectedAssignment.audit.total_versions}
                  </p>
                  
                  {selectedAssignment.audit.versions?.map((version, idx) => (
                    <div key={idx} className={`p-4 rounded-lg border ${version.is_current ? 'bg-green-50 border-green-300' : 'bg-slate-50'}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="font-semibold">Version {version.version_number}</span>
                          {version.is_current && <Badge className="ml-2 bg-green-600">Current</Badge>}
                        </div>
                        <span className="text-sm text-slate-500">
                          {new Date(version.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm mt-1">
                        Created by: {version.created_by}
                        {version.change_reason && (
                          <span className="block text-slate-600 mt-1">
                            Reason: {version.change_reason}
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {version.linked_shifts?.length || 0} shifts covered
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>


        <TabsContent value="shifts" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold text-slate-800">Shift Management</h2>
            <Button 
              onClick={() => setShowCreateShift(true)}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              data-testid="create-shift-button"
            >
              Create New Shift
            </Button>
          </div>

          {showCreateShift && (
            <Card>
              <CardHeader>
                <CardTitle>Create New Shift</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateShift} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="title">Shift Title</Label>
                      <Input
                        id="title"
                        data-testid="shift-title-input"
                        value={newShift.title}
                        onChange={(e) => setNewShift({...newShift, title: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="client">Client</Label>
                      <Select value={newShift.client_id} onValueChange={(value) => setNewShift({...newShift, client_id: value})}>
                        <SelectTrigger data-testid="shift-client-select">
                          <SelectValue placeholder="Select client" />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.map((client) => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="shift_pattern">Shift Pattern (quick pick)</Label>
                      <select
                        id="shift_pattern"
                        data-testid="shift-pattern-select"
                        className="w-full h-10 border border-input rounded-md px-3 text-sm bg-background"
                        value={(() => {
                          if (!newShift.client_id) return '';
                          const selClient = clients.find(c => c.id === newShift.client_id);
                          const patterns = selClient?.shift_patterns?.length ? selClient.shift_patterns : defaultShiftPatterns();
                          const i = patterns.findIndex(p => p.start_time === newShift.start_time && p.end_time === newShift.end_time);
                          return i >= 0 ? String(i) : '';
                        })()}
                        disabled={!newShift.client_id}
                        onChange={(e) => {
                          if (e.target.value === '') return;
                          const selClient = clients.find(c => c.id === newShift.client_id);
                          const patterns = selClient?.shift_patterns?.length ? selClient.shift_patterns : defaultShiftPatterns();
                          const p = patterns[parseInt(e.target.value, 10)];
                          if (p) setNewShift({ ...newShift, start_time: p.start_time, end_time: p.end_time });
                        }}
                      >
                        <option value="">{newShift.client_id ? 'Pick a pattern to fill times…' : 'Select a client first'}</option>
                        {newShift.client_id && (() => {
                          const selClient = clients.find(c => c.id === newShift.client_id);
                          const patterns = selClient?.shift_patterns?.length ? selClient.shift_patterns : defaultShiftPatterns();
                          return patterns.map((p, i) => (
                            <option key={i} value={i}>{p.name} ({p.start_time} – {p.end_time})</option>
                          ));
                        })()}
                      </select>
                      <p className="text-xs text-slate-500 mt-1">Fills start/end time below — still editable for ASAP/emergency cover</p>
                    </div>
                    <div>
                      <Label htmlFor="staff_type">Staff Type</Label>
                      <Select value={newShift.staff_type} onValueChange={(value) => setNewShift({...newShift, staff_type: value})}>
                        <SelectTrigger data-testid="shift-staff-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="nurse">Nurse</SelectItem>
                          <SelectItem value="carer">Carer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="hourly_rate">Hourly Pay Rate (£/hr) - For Payroll</Label>
                      <Input
                        id="hourly_rate"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        data-testid="shift-hourly-rate-input"
                        value={newShift.hourly_rate}
                        onChange={(e) => setNewShift({...newShift, hourly_rate: e.target.value === '' ? '' : parseFloat(e.target.value)})}
                        required
                      />
                      <p className="text-xs text-slate-500 mt-1">This rate is used for payroll calculations</p>
                    </div>
                    <div>
                      <Label htmlFor="date">Date</Label>
                      <Input
                        id="date"
                        type="date"
                        data-testid="shift-date-input"
                        value={newShift.date}
                        onChange={(e) => setNewShift({...newShift, date: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="start_time">Start Time</Label>
                      <Input
                        id="start_time"
                        type="time"
                        data-testid="shift-start-time-input"
                        value={newShift.start_time}
                        onChange={(e) => setNewShift({...newShift, start_time: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="end_time">End Time</Label>
                      <Input
                        id="end_time"
                        type="time"
                        data-testid="shift-end-time-input"
                        value={newShift.end_time}
                        onChange={(e) => setNewShift({...newShift, end_time: e.target.value})}
                        required
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="location">Location</Label>
                      <Input
                        id="location"
                        data-testid="shift-location-input"
                        value={newShift.location}
                        onChange={(e) => setNewShift({...newShift, location: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="booked_by">Booked By</Label>
                      <Input
                        id="booked_by"
                        data-testid="shift-booked-by-input"
                        value={newShift.booked_by}
                        onChange={(e) => setNewShift({...newShift, booked_by: e.target.value})}
                        required
                        placeholder="Name of person making booking"
                      />
                    </div>
                    <div>
                      <Label htmlFor="booking_notes">Booking Notes (Optional)</Label>
                      <Input
                        id="booking_notes"
                        data-testid="shift-booking-notes-input"
                        value={newShift.booking_notes}
                        onChange={(e) => setNewShift({...newShift, booking_notes: e.target.value})}
                        placeholder="Any additional notes"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="description">Description (Optional)</Label>
                      <Textarea
                        id="description"
                        data-testid="shift-description-input"
                        value={newShift.description}
                        onChange={(e) => setNewShift({...newShift, description: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button type="submit" data-testid="save-shift-button">Create Shift</Button>
                    <Button type="button" variant="outline" onClick={() => setShowCreateShift(false)} data-testid="cancel-shift-button">
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          <div className="grid gap-4">
            {shifts.map((shift) => {
              const client = clients.find(c => c.id === shift.client_id);
              return (
                <Card key={shift.id} className={shift.status === "assigned" ? "border-green-200 bg-green-50" : "border-gray-200"}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-lg" data-testid={`shift-title-${shift.id}`}>{shift.title}</h3>
                        <p className="text-slate-600 mt-1">{shift.location}</p>
                        <div className="flex items-center space-x-4 mt-2 text-sm text-slate-500">
                          <span data-testid={`shift-date-${shift.id}`}>{formatDateDMY(shift.date)}</span>
                          <span data-testid={`shift-time-${shift.id}`}>{shift.start_time} - {shift.end_time}</span>
                          <span>£{shift.hourly_pay}/hr</span>
                          <span className="capitalize">{shift.staff_type}</span>
                        </div>
                        {client && (
                          <p className="text-sm text-blue-600 mt-1">Client: {client.name}</p>
                        )}
                        {shift.booking_info && (
                          <div className="text-xs text-slate-500 mt-2">
                            <div>Booked by: {shift.booking_info.booked_by}</div>
                            <div>Booking date: {formatDateDMY(shift.booking_info.booking_date)}</div>
                            {shift.booking_info.notes && (
                              <div>Notes: {shift.booking_info.notes}</div>
                            )}
                          </div>
                        )}
                        {shift.description && (
                          <p className="text-sm text-slate-600 mt-2">{shift.description}</p>
                        )}
                        {shift.interested_staff.length > 0 && (
                          <div className="mt-2 text-sm">
                            <p className="font-medium text-green-700">{shift.interested_staff.length} staff interested:</p>
                            <div className="mt-1 space-y-1">
                              {shift.interested_staff.map((staffId) => {
                                const staff = staffUsers.find(s => s.user.id === staffId);
                                if (!staff) {
                                  // Staff not found in list - show ID
                                  return (
                                    <div key={staffId} className="flex items-center justify-between bg-yellow-50 p-2 rounded border border-yellow-200">
                                      <span className="text-yellow-800">Staff ID: {staffId.slice(0, 8)}... (Details loading...)</span>
                                    </div>
                                  );
                                }
                                return (
                                  <div key={staffId} className="flex items-center justify-between bg-green-50 p-2 rounded border border-green-200">
                                    <span className="text-green-900">{staff.user.name} ({staff.user.email})</span>
                                    {shift.status === "open" && (
                                      <Button 
                                        size="sm" 
                                        className="bg-blue-600 hover:bg-blue-700 text-white"
                                        onClick={async () => {
                                          if (window.confirm(`Assign this shift to ${staff.user.name}?`)) {
                                            try {
                                              console.log('Assigning shift:', shift.id, 'to staff:', staffId);
                                              await apiCall('/shifts/assign', 'POST', {
                                                shift_id: shift.id,
                                                staff_id: staffId
                                              }, token);
                                              toast.success(`Shift assigned to ${staff.user.name}`);
                                              refetchData();
                                              await handlePostAssignFlow(shift, staffId, staff.user.name);
                                            } catch (error) {
                                              console.error('Assign error:', error);
                                              toast.error(`Failed to assign: ${error.detail || error.message || 'Unknown error'}`);
                                            }
                                          }
                                        }}
                                      >
                                        Assign Shift
                                      </Button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        
                        {/* Manual Assignment Section */}
                        {shift.status === "open" && (
                          <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200">
                            <p className="text-sm font-medium text-blue-900 mb-2">Manual Assignment:</p>
                            <div className="flex space-x-2">
                              <select 
                                id={`staff-select-${shift.id}`}
                                className="flex-1 p-2 border rounded text-sm"
                              >
                                <option value="">Select staff member...</option>
                                {staffUsers
                                  .filter(s => s.user.approved)
                                  .map(staffData => (
                                    <option key={staffData.user.id} value={staffData.user.id}>
                                      {staffData.user.name} ({staffData.user.email})
                                    </option>
                                  ))
                                }
                              </select>
                              <Button 
                                size="sm" 
                                className="bg-purple-600 hover:bg-purple-700"
                                onClick={async () => {
                                  const selectEl = document.getElementById(`staff-select-${shift.id}`);
                                  const staffId = selectEl.value;
                                  if (!staffId) {
                                    toast.error('Please select a staff member');
                                    return;
                                  }
                                  const staff = staffUsers.find(s => s.user.id === staffId);
                                  if (window.confirm(`Assign this shift to ${staff.user.name}?`)) {
                                    try {
                                      await apiCall('/shifts/assign', 'POST', {
                                        shift_id: shift.id,
                                        staff_id: staffId
                                      }, token);
                                      toast.success(`Shift assigned to ${staff.user.name}`);
                                      refetchData();
                                      await handlePostAssignFlow(shift, staffId, staff.user.name);
                                    } catch (error) {
                                      toast.error(`Failed to assign: ${error.detail || 'Unknown error'}`);
                                    }
                                  }
                                }}
                              >
                                Assign
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="text-right flex flex-col items-end space-y-2">
                        {(() => {
                          const awaitingTimesheet = shift.status === 'assigned' && new Date(`${shift.date}T${shift.end_time || '23:59'}`) < new Date();
                          return (
                            <Badge variant={
                              awaitingTimesheet ? undefined :
                              shift.status === "assigned" ? "default" : 
                              shift.status === "completed" ? "secondary" : 
                              "outline"
                            } className={awaitingTimesheet ? 'bg-amber-500 hover:bg-amber-500' : undefined}
                              data-testid={`shift-status-${shift.id}`}>
                              {awaitingTimesheet ? 'completed — awaiting timesheet' : shift.status}
                            </Badge>
                          );
                        })()}
                        {!shift.client_id && (
                          <select
                            className="block mt-1 h-7 text-xs border border-amber-300 rounded px-1 bg-amber-50 text-amber-800 max-w-[180px]"
                            value=""
                            data-testid={`link-client-select-${shift.id}`}
                            onChange={async (e) => {
                              if (!e.target.value) return;
                              try {
                                const res = await apiCall(`/shifts/${shift.id}/link-client`, 'PUT', { client_id: e.target.value }, token);
                                toast.success(res.message);
                                refetchData();
                              } catch (error) {
                                toast.error('Failed to link client');
                              }
                            }}
                          >
                            <option value="">⚠ Link to client…</option>
                            {clients.map((c) => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                          </select>
                        )}
                        <DuplicateShiftDialog shift={shift} token={token} apiCall={apiCall} onDone={refetchData} />
                        <Button 
                          variant="destructive" 
                          size="sm"
                          onClick={async () => {
                            const confirmMessage = shift.assigned_to 
                              ? `This shift is assigned to staff. Deleting will notify them of cancellation. Are you sure?`
                              : `Delete this shift? This action cannot be undone.`;
                            
                            if (window.confirm(confirmMessage)) {
                              try {
                                const result = await apiCall(`/shifts/${shift.id}`, 'DELETE', null, token);
                                if (result.notification_sent) {
                                  toast.success(`Shift deleted and staff member notified of cancellation`);
                                } else {
                                  toast.success('Shift deleted successfully');
                                }
                                refetchData();
                              } catch (error) {
                                console.error('Delete shift error:', error);
                                toast.error('Failed to delete shift');
                              }
                            }
                          }}
                        >
                          Cancel Shift
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="timesheets" className="space-y-6">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-2xl font-semibold text-slate-800">Timesheet Reviews & Audit</h2>
            <CreateManualTimesheetDialog shifts={shifts} timesheets={timesheets} staffUsers={staffUsers} token={token} apiCall={apiCall} onDone={refetchData} />
          </div>
          
          {/* Timesheet Filters */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <h3 className="font-semibold mb-3">🔍 Search & Filter Timesheets</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Status Filter */}
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={timesheetFilter} onValueChange={setTimesheetFilter}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Search Box */}
                <div>
                  <Label className="text-xs">Search (Staff/Client/Shift)</Label>
                  <Input
                    placeholder="Type to search..."
                    value={timesheetSearchTerm}
                    onChange={(e) => setTimesheetSearchTerm(e.target.value)}
                    className="bg-white"
                  />
                </div>
                
                {/* Date From */}
                <div>
                  <Label className="text-xs">Date From</Label>
                  <Input
                    type="date"
                    value={timesheetDateFrom}
                    onChange={(e) => setTimesheetDateFrom(e.target.value)}
                    className="bg-white"
                  />
                </div>
                
                {/* Date To */}
                <div>
                  <Label className="text-xs">Date To</Label>
                  <Input
                    type="date"
                    value={timesheetDateTo}
                    onChange={(e) => setTimesheetDateTo(e.target.value)}
                    className="bg-white"
                  />
                </div>
              </div>
              
              {/* Clear Filters Button */}
              {(timesheetFilter !== 'all' || timesheetSearchTerm || timesheetDateFrom || timesheetDateTo) && (
                <div className="mt-3 flex space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setTimesheetFilter('all');
                      setTimesheetSearchTerm('');
                      setTimesheetDateFrom('');
                      setTimesheetDateTo('');
                    }}
                  >
                    Clear All Filters
                  </Button>
                  
                  <Button
                    variant="default"
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => {
                      // Export filtered timesheets to CSV
                      const filteredTimesheets = timesheets.filter(t => {
                        if (timesheetFilter !== 'all' && t.status !== timesheetFilter) return false;
                        if (timesheetSearchTerm) {
                          const searchLower = timesheetSearchTerm.toLowerCase();
                          const matchesStaff = t.staff_name?.toLowerCase().includes(searchLower);
                          const matchesClient = t.client_name?.toLowerCase().includes(searchLower);
                          const matchesShift = t.shift_title?.toLowerCase().includes(searchLower);
                          if (!matchesStaff && !matchesClient && !matchesShift) return false;
                        }
                        if (timesheetDateFrom && t.date < timesheetDateFrom) return false;
                        if (timesheetDateTo && t.date > timesheetDateTo) return false;
                        return true;
                      });
                      
                      const csvRows = [];
                      csvRows.push([
                        'Date',
                        'Staff Name',
                        'Client Name',
                        'Shift Title',
                        'Start Time',
                        'End Time',
                        'Break (min)',
                        'Total Hours',
                        'Hourly Rate',
                        'Total Pay',
                        'Status',
                        'Submitted Date'
                      ]);
                      
                      filteredTimesheets.forEach(ts => {
                        csvRows.push([
                          ts.date,
                          ts.staff_name || 'N/A',
                          ts.client_name || 'N/A',
                          ts.shift_title || 'N/A',
                          ts.start_time,
                          ts.end_time,
                          ts.break_minutes || 0,
                          ts.total_hours,
                          ts.hourly_rate || 0,
                          ts.total_pay || 0,
                          ts.status,
                          formatDateDMY(ts.created_at)
                        ]);
                      });
                      
                      const csvContent = csvRows.map(row => row.join(',')).join('\n');
                      const blob = new Blob([csvContent], { type: 'text/csv' });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      const dateStr = new Date().toISOString().split('T')[0];
                      a.download = `Timesheet_Audit_${dateStr}.csv`;
                      a.click();
                      toast.success('Timesheet audit exported to CSV');
                    }}
                  >
                    📊 Export to CSV (Audit)
                  </Button>
                </div>
              )}
              
              {/* Results Count */}
              <div className="mt-3 text-sm text-slate-600">
                <p>
                  Showing: <strong>
                    {timesheets.filter(t => {
                      // Status filter
                      if (timesheetFilter !== 'all' && t.status !== timesheetFilter) return false;
                      
                      // Search filter
                      if (timesheetSearchTerm) {
                        const searchLower = timesheetSearchTerm.toLowerCase();
                        const matchesStaff = t.staff_name?.toLowerCase().includes(searchLower);
                        const matchesClient = t.client_name?.toLowerCase().includes(searchLower);
                        const matchesShift = t.shift_title?.toLowerCase().includes(searchLower);
                        if (!matchesStaff && !matchesClient && !matchesShift) return false;
                      }
                      
                      // Date range filter
                      if (timesheetDateFrom && t.date < timesheetDateFrom) return false;
                      if (timesheetDateTo && t.date > timesheetDateTo) return false;
                      
                      return true;
                    }).length}
                  </strong> of <strong>{timesheets.length}</strong> timesheets
                </p>
              </div>
            </CardContent>
          </Card>
          
          <div className="grid gap-4">
            {timesheets
              .filter(t => {
                // Status filter
                if (timesheetFilter !== 'all' && t.status !== timesheetFilter) return false;
                
                // Search filter
                if (timesheetSearchTerm) {
                  const searchLower = timesheetSearchTerm.toLowerCase();
                  const matchesStaff = t.staff_name?.toLowerCase().includes(searchLower);
                  const matchesClient = t.client_name?.toLowerCase().includes(searchLower);
                  const matchesShift = t.shift_title?.toLowerCase().includes(searchLower);
                  if (!matchesStaff && !matchesClient && !matchesShift) return false;
                }
                
                // Date range filter
                if (timesheetDateFrom && t.date < timesheetDateFrom) return false;
                if (timesheetDateTo && t.date > timesheetDateTo) return false;
                
                return true;
              })
              .sort((a, b) => new Date(b.date) - new Date(a.date))  // Sort by date desc
              .map((timesheet) => (
              <Card key={timesheet.id}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <h3 className="font-semibold">Timesheet #{timesheet.id.slice(-8)}</h3>
                        <Badge variant={timesheet.status === 'pending' ? 'secondary' : timesheet.status === 'approved' ? 'default' : 'destructive'}>
                          {timesheet.status}
                        </Badge>
                      </div>
                      <div className="flex items-center space-x-4 text-sm mb-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-slate-500">Staff:</span>
                          <span className="font-medium text-blue-700">{timesheet.staff_name || 'Unknown'}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-slate-500">Role:</span>
                          <span className="font-medium capitalize">{timesheet.staff_role || 'Staff'}</span>
                        </div>
                        {timesheet.client_name && (
                          <div className="flex items-center space-x-2">
                            <span className="text-slate-500">Client:</span>
                            <span className="font-medium text-green-700">{timesheet.client_name}</span>
                          </div>
                        )}
                        {timesheet.location && (
                          <div className="flex items-center space-x-2">
                            <span className="text-slate-500">Location:</span>
                            <span className="font-medium">{timesheet.location}</span>
                          </div>
                        )}
                      </div>
                      {timesheet.client_linked === false && (
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-xs text-amber-700 font-medium">⚠ Not linked to a client (needed for invoicing):</span>
                          <select
                            className="h-7 text-xs border border-amber-300 rounded px-1 bg-amber-50 text-amber-800 max-w-[220px]"
                            value=""
                            data-testid={`ts-link-client-select-${timesheet.id}`}
                            onChange={async (e) => {
                              if (!e.target.value) return;
                              try {
                                const res = await apiCall(`/shifts/${timesheet.shift_id}/link-client`, 'PUT', { client_id: e.target.value }, token);
                                toast.success(res.message);
                                refetchData();
                              } catch (error) {
                                toast.error('Failed to link client');
                              }
                            }}
                          >
                            <option value="">Link to client…</option>
                            {clients.map((c) => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-slate-500">Date:</span>
                          <p className="font-medium">{formatDateDMY(timesheet.date)}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Time:</span>
                          <p className="font-medium">{timesheet.start_time} - {timesheet.end_time}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Break:</span>
                          <p className="font-medium">{timesheet.break_minutes} mins</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Total Hours:</span>
                          <p className="font-medium">{timesheet.total_hours}h</p>
                        </div>
                      </div>
                      {timesheet.comments && (
                        <div className="mb-2">
                          <span className="text-slate-500 text-sm">Comments:</span>
                          <p className="text-sm bg-amber-50 border border-amber-200 rounded p-2 mt-1">{timesheet.comments}</p>
                        </div>
                      )}
                      {timesheet.client_signature && (
                        <div>
                          <span className="text-slate-500 text-sm">Client Signature:</span>
                          {(timesheet.signer_name || timesheet.signer_designation) && (
                            <p className="text-sm font-medium">
                              Signed by: {timesheet.signer_name || 'N/A'}{timesheet.signer_designation ? ` — ${timesheet.signer_designation}` : ''}
                            </p>
                          )}
                          <div className="mt-1">
                            <img 
                              src={timesheet.client_signature} 
                              alt="Client Signature" 
                              className="border rounded max-h-20"
                            />
                          </div>
                        </div>
                      )}
                      {timesheet.manual_approval && (
                        <div className="mt-2 bg-amber-50 border border-amber-300 rounded p-2 text-sm" data-testid={`manual-approval-info-${timesheet.id}`}>
                          ⚠️ <span className="font-medium">Approved without signature</span> — authorised by {timesheet.manual_approver_name} ({timesheet.manual_approver_designation})
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end space-y-2">
                      {timesheet.status === 'pending' && (
                        <div className="flex space-x-2">
                          <Button 
                            size="sm"
                            onClick={() => handleApproveTimesheet(timesheet.id, 'approved')}
                            className="bg-green-600 hover:bg-green-700"
                            data-testid={`approve-timesheet-${timesheet.id}`}
                          >
                            Approve
                          </Button>
                          <ManualApproveDialog timesheet={timesheet} token={token} apiCall={apiCall} onDone={refetchData} />
                          <Button 
                            size="sm"
                            variant="destructive"
                            onClick={() => handleApproveTimesheet(timesheet.id, 'rejected')}
                            data-testid={`reject-timesheet-${timesheet.id}`}
                          >
                            Reject
                          </Button>
                        </div>
                      )}
                      <div className="flex space-x-2">
                        <EditTimesheetDialog timesheet={timesheet} token={token} apiCall={apiCall} onDone={refetchData} />
                        <Button 
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          data-testid={`delete-timesheet-${timesheet.id}`}
                        onClick={async () => {
                          if (!window.confirm(`Delete timesheet #${timesheet.id.slice(-8)} for ${timesheet.staff_name || 'staff'} on ${formatDateDMY(timesheet.date)}? This cannot be undone.`)) return;
                          try {
                            await apiCall(`/timesheets/${timesheet.id}`, 'DELETE', null, token);
                            toast.success('Timesheet deleted');
                            refetchData();
                          } catch (error) {
                            toast.error('Failed to delete timesheet');
                          }
                        }}
                      >
                        🗑️ Delete
                      </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {timesheets.length === 0 && (
              <Card>
                <CardContent className="p-8 text-center">
                  <p className="text-slate-500">No timesheets submitted yet.</p>
                  <p className="text-xs text-slate-400 mt-1">Timesheets will appear here after staff submit them.</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="payroll" className="space-y-6">
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-slate-800">
              {payrollPeriodType === 'weekly' ? 'Weekly' : 'Monthly'} Payroll Report
            </h2>
            
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4">Generate Payroll Report</h3>
                
                {/* Period Type Toggle */}
                <div className="flex items-center space-x-4 mb-4">
                  <Label className="font-medium">Report Period:</Label>
                  <div className="flex rounded-lg border border-blue-300 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => {
                        setPayrollPeriodType('weekly');
                        setPayrollReport(null);
                      }}
                      className={`px-4 py-2 text-sm font-medium transition-colors ${
                        payrollPeriodType === 'weekly'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-blue-600 hover:bg-blue-50'
                      }`}
                      data-testid="weekly-period-btn"
                    >
                      Weekly
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setPayrollPeriodType('monthly');
                        setPayrollReport(null);
                      }}
                      className={`px-4 py-2 text-sm font-medium transition-colors ${
                        payrollPeriodType === 'monthly'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-blue-600 hover:bg-blue-50'
                      }`}
                      data-testid="monthly-period-btn"
                    >
                      Monthly
                    </button>
                  </div>
                </div>
                
                <p className="text-sm text-slate-600 mb-4">
                  {payrollPeriodType === 'weekly' 
                    ? 'Select the week ending date to generate a payroll report showing hours worked and pay for each staff member based on approved timesheets.'
                    : 'Select the year and month to generate a monthly payroll report showing hours worked and pay for each staff member based on approved timesheets.'
                  }
                </p>
                
                <div className="flex space-x-3 items-end flex-wrap gap-y-3">
                  {payrollPeriodType === 'weekly' ? (
                    <div className="flex-1 min-w-48">
                      <Label>Week Ending Date</Label>
                      <Input
                        type="date"
                        value={payrollWeekEnding}
                        onChange={(e) => setPayrollWeekEnding(e.target.value)}
                        className="max-w-xs"
                        data-testid="week-ending-input"
                      />
                    </div>
                  ) : (
                    <>
                      <div className="min-w-32">
                        <Label>Year</Label>
                        <select
                          value={payrollYear}
                          onChange={(e) => setPayrollYear(parseInt(e.target.value))}
                          className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                          data-testid="payroll-year-select"
                        >
                          {[...Array(5)].map((_, i) => {
                            const year = new Date().getFullYear() - 2 + i;
                            return <option key={year} value={year}>{year}</option>;
                          })}
                        </select>
                      </div>
                      <div className="min-w-40">
                        <Label>Month</Label>
                        <select
                          value={payrollMonth}
                          onChange={(e) => setPayrollMonth(parseInt(e.target.value))}
                          className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                          data-testid="payroll-month-select"
                        >
                          {[
                            'January', 'February', 'March', 'April', 'May', 'June',
                            'July', 'August', 'September', 'October', 'November', 'December'
                          ].map((month, i) => (
                            <option key={i + 1} value={i + 1}>{month}</option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                  
                  <Button
                    onClick={async () => {
                      try {
                        let report;
                        if (payrollPeriodType === 'weekly') {
                          if (!payrollWeekEnding) {
                            toast.error('Please select a week ending date');
                            return;
                          }
                          report = await apiCall(`/admin/payroll-report?week_ending=${payrollWeekEnding}`, 'GET', null, token);
                        } else {
                          report = await apiCall(`/admin/payroll-report/monthly?year=${payrollYear}&month=${payrollMonth}`, 'GET', null, token);
                        }
                        setPayrollReport(report);
                        toast.success(`${payrollPeriodType === 'weekly' ? 'Weekly' : 'Monthly'} payroll report generated`);
                      } catch (error) {
                        console.error('Payroll report error:', error);
                        toast.error('Failed to generate payroll report');
                      }
                    }}
                    className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                    data-testid="generate-report-btn"
                  >
                    Generate Report
                  </Button>
                  {payrollReport && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        // Export to CSV in the exact format requested
                        const csvRows = [];
                        
                        // Header row
                        csvRows.push([
                          'Name',
                          'Surname',
                          'Rate 1 Weekday',
                          'Hours',
                          'Rate 2 Weekend',
                          'Hours',
                          'Rate 3 Bank Holiday',
                          'Hours',
                          'DBS Deduction',
                          'Transport Deduction',
                          'Training Deduction',
                          'Reimbursement',
                          'Gross Payment'
                        ]);
                        
                        // Data rows with calculated net payment
                        payrollReport.staff_payroll.forEach(staff => {
                          const basePay = (staff.hours_weekday * staff.rate_weekday) + 
                                         (staff.hours_weekend * staff.rate_weekend) + 
                                         (staff.hours_bank_holiday * staff.rate_bank_holiday);
                          const netPayment = basePay - staff.dbs_deduction - staff.transport_deduction - staff.training_deduction + staff.reimbursement;
                          
                          csvRows.push([
                            staff.first_name,
                            staff.surname,
                            staff.rate_weekday.toFixed(2),
                            staff.hours_weekday.toFixed(2),
                            staff.rate_weekend.toFixed(2),
                            staff.hours_weekend.toFixed(2),
                            staff.rate_bank_holiday.toFixed(2),
                            staff.hours_bank_holiday.toFixed(2),
                            staff.dbs_deduction.toFixed(2),
                            staff.transport_deduction.toFixed(2),
                            staff.training_deduction.toFixed(2),
                            staff.reimbursement.toFixed(2),
                            `£${netPayment.toFixed(2)}`
                          ]);
                        });
                        
                        // Total row
                        const totalWeekdayHours = payrollReport.staff_payroll.reduce((sum, s) => sum + s.hours_weekday, 0);
                        const totalWeekendHours = payrollReport.staff_payroll.reduce((sum, s) => sum + s.hours_weekend, 0);
                        const totalBankHolidayHours = payrollReport.staff_payroll.reduce((sum, s) => sum + s.hours_bank_holiday, 0);
                        const totalDbsDeduction = payrollReport.staff_payroll.reduce((sum, s) => sum + s.dbs_deduction, 0);
                        const totalTransportDeduction = payrollReport.staff_payroll.reduce((sum, s) => sum + s.transport_deduction, 0);
                        const totalTrainingDeduction = payrollReport.staff_payroll.reduce((sum, s) => sum + s.training_deduction, 0);
                        const totalReimbursement = payrollReport.staff_payroll.reduce((sum, s) => sum + s.reimbursement, 0);
                        const totalGrossPay = payrollReport.staff_payroll.reduce((sum, s) => {
                          const basePay = (s.hours_weekday * s.rate_weekday) + 
                                         (s.hours_weekend * s.rate_weekend) + 
                                         (s.hours_bank_holiday * s.rate_bank_holiday);
                          return sum + basePay - s.dbs_deduction - s.transport_deduction - s.training_deduction + s.reimbursement;
                        }, 0);
                        
                        csvRows.push(['', '', '', '', '', '', '', '', '', '', '', '', '']);
                        csvRows.push([
                          'TOTAL',
                          '',
                          '',
                          totalWeekdayHours.toFixed(2),
                          '',
                          totalWeekendHours.toFixed(2),
                          '',
                          totalBankHolidayHours.toFixed(2),
                          totalDbsDeduction.toFixed(2),
                          totalTransportDeduction.toFixed(2),
                          totalTrainingDeduction.toFixed(2),
                          totalReimbursement.toFixed(2),
                          `£${totalGrossPay.toFixed(2)}`
                        ]);
                        
                        const csvContent = csvRows.map(row => row.join(',')).join('\n');
                        const blob = new Blob([csvContent], { type: 'text/csv' });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `Koloth_Care_Week_ending_${payrollWeekEnding}.csv`;
                        a.click();
                        toast.success('Payroll exported to CSV');
                      }}
                    >
                      Export CSV
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {payrollReport && (
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle>Payroll Report</CardTitle>
                      <p className="text-sm text-slate-600 mt-1">
                        Week: {payrollReport.week_starting} to {payrollReport.week_ending}
                      </p>
                      <p className="text-xs text-blue-600 mt-1">
                        💡 Tip: Click on deduction/reimbursement cells to edit amounts
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-600">Total Staff</p>
                      <p className="text-2xl font-bold text-blue-600">{payrollReport.total_staff}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead className="bg-slate-200">
                        <tr>
                          <th className="p-2 text-left border">Name</th>
                          <th className="p-2 text-left border">Surname</th>
                          <th className="p-2 text-center border" colSpan="2">Rate 1 - Weekday</th>
                          <th className="p-2 text-center border" colSpan="2">Rate 2 - Weekend</th>
                          <th className="p-2 text-center border" colSpan="2">Rate 3 - Bank Holiday</th>
                          <th className="p-2 text-right border bg-yellow-50">DBS Deduction</th>
                          <th className="p-2 text-right border bg-yellow-50">Transport Deduction</th>
                          <th className="p-2 text-right border bg-yellow-50">Training Deduction</th>
                          <th className="p-2 text-right border bg-blue-50">Reimbursement</th>
                          <th className="p-2 text-right border bg-green-100">Gross Payment</th>
                        </tr>
                        <tr className="bg-slate-100">
                          <th className="p-2 border"></th>
                          <th className="p-2 border"></th>
                          <th className="p-2 text-right border">Rate (£)</th>
                          <th className="p-2 text-right border">Hours</th>
                          <th className="p-2 text-right border">Rate (£)</th>
                          <th className="p-2 text-right border">Hours</th>
                          <th className="p-2 text-right border">Rate (£)</th>
                          <th className="p-2 text-right border">Hours</th>
                          <th className="p-2 text-center border text-xs">Click to edit</th>
                          <th className="p-2 text-center border text-xs">Click to edit</th>
                          <th className="p-2 text-center border text-xs">Click to edit</th>
                          <th className="p-2 text-center border text-xs">Click to edit</th>
                          <th className="p-2 border"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {payrollReport.staff_payroll.map((staff, idx) => {
                          // Calculate base pay from hours * rates
                          const basePay = (staff.hours_weekday * staff.rate_weekday) + 
                                         (staff.hours_weekend * staff.rate_weekend) + 
                                         (staff.hours_bank_holiday * staff.rate_bank_holiday);
                          
                          // Calculate net payment with deductions and reimbursements
                          const netPayment = basePay - staff.dbs_deduction - staff.transport_deduction - staff.training_deduction + staff.reimbursement;
                          
                          return (
                            <tr key={idx} className="border-b hover:bg-slate-50">
                              <td className="p-2 border">{staff.first_name}</td>
                              <td className="p-2 border font-medium">{staff.surname}</td>
                              <td className="p-2 text-right border">{staff.rate_weekday > 0 ? `£${staff.rate_weekday.toFixed(2)}` : '-'}</td>
                              <td className="p-2 text-right border font-medium">{staff.hours_weekday > 0 ? staff.hours_weekday.toFixed(2) : '-'}</td>
                              <td className="p-2 text-right border">{staff.rate_weekend > 0 ? `£${staff.rate_weekend.toFixed(2)}` : '-'}</td>
                              <td className="p-2 text-right border font-medium">{staff.hours_weekend > 0 ? staff.hours_weekend.toFixed(2) : '-'}</td>
                              <td className="p-2 text-right border">{staff.rate_bank_holiday > 0 ? `£${staff.rate_bank_holiday.toFixed(2)}` : '-'}</td>
                              <td className="p-2 text-right border font-medium">{staff.hours_bank_holiday > 0 ? staff.hours_bank_holiday.toFixed(2) : '-'}</td>
                              
                              {/* Editable DBS Deduction */}
                              <td className="p-2 text-right border bg-yellow-50 cursor-pointer hover:bg-yellow-100" 
                                  onClick={() => {
                                    const newValue = prompt('Enter DBS Deduction amount (£):', staff.dbs_deduction.toFixed(2));
                                    if (newValue !== null) {
                                      const updated = [...payrollReport.staff_payroll];
                                      updated[idx].dbs_deduction = parseFloat(newValue) || 0;
                                      setPayrollReport({...payrollReport, staff_payroll: updated});
                                    }
                                  }}>
                                {staff.dbs_deduction > 0 ? `£${staff.dbs_deduction.toFixed(2)}` : '£0.00'}
                              </td>
                              
                              {/* Editable Transport Deduction */}
                              <td className="p-2 text-right border bg-yellow-50 cursor-pointer hover:bg-yellow-100"
                                  onClick={() => {
                                    const newValue = prompt('Enter Transport Deduction amount (£):', staff.transport_deduction.toFixed(2));
                                    if (newValue !== null) {
                                      const updated = [...payrollReport.staff_payroll];
                                      updated[idx].transport_deduction = parseFloat(newValue) || 0;
                                      setPayrollReport({...payrollReport, staff_payroll: updated});
                                    }
                                  }}>
                                {staff.transport_deduction > 0 ? `£${staff.transport_deduction.toFixed(2)}` : '£0.00'}
                              </td>
                              
                              {/* Editable Training Deduction */}
                              <td className="p-2 text-right border bg-yellow-50 cursor-pointer hover:bg-yellow-100"
                                  onClick={() => {
                                    const newValue = prompt('Enter Training Deduction amount (£):', staff.training_deduction.toFixed(2));
                                    if (newValue !== null) {
                                      const updated = [...payrollReport.staff_payroll];
                                      updated[idx].training_deduction = parseFloat(newValue) || 0;
                                      setPayrollReport({...payrollReport, staff_payroll: updated});
                                    }
                                  }}>
                                {staff.training_deduction > 0 ? `£${staff.training_deduction.toFixed(2)}` : '£0.00'}
                              </td>
                              
                              {/* Editable Reimbursement */}
                              <td className="p-2 text-right border bg-blue-50 cursor-pointer hover:bg-blue-100"
                                  onClick={() => {
                                    const newValue = prompt('Enter Reimbursement amount (£):', staff.reimbursement.toFixed(2));
                                    if (newValue !== null) {
                                      const updated = [...payrollReport.staff_payroll];
                                      updated[idx].reimbursement = parseFloat(newValue) || 0;
                                      setPayrollReport({...payrollReport, staff_payroll: updated});
                                    }
                                  }}>
                                {staff.reimbursement > 0 ? `£${staff.reimbursement.toFixed(2)}` : '£0.00'}
                              </td>
                              
                              {/* Calculated Gross Payment */}
                              <td className="p-2 text-right border font-bold text-green-600 bg-green-50">
                                £{netPayment.toFixed(2)}
                              </td>
                            </tr>
                          );
                        })}
                        <tr className="bg-slate-100 font-bold">
                          <td className="p-2 border" colSpan="2">TOTALS</td>
                          <td className="p-2 border"></td>
                          <td className="p-2 text-right border">
                            {payrollReport.staff_payroll.reduce((sum, s) => sum + s.hours_weekday, 0).toFixed(2)}
                          </td>
                          <td className="p-2 border"></td>
                          <td className="p-2 text-right border">
                            {payrollReport.staff_payroll.reduce((sum, s) => sum + s.hours_weekend, 0).toFixed(2)}
                          </td>
                          <td className="p-2 border"></td>
                          <td className="p-2 text-right border">
                            {payrollReport.staff_payroll.reduce((sum, s) => sum + s.hours_bank_holiday, 0).toFixed(2)}
                          </td>
                          <td className="p-2 text-right border bg-yellow-100">
                            £{payrollReport.staff_payroll.reduce((sum, s) => sum + s.dbs_deduction, 0).toFixed(2)}
                          </td>
                          <td className="p-2 text-right border bg-yellow-100">
                            £{payrollReport.staff_payroll.reduce((sum, s) => sum + s.transport_deduction, 0).toFixed(2)}
                          </td>
                          <td className="p-2 text-right border bg-yellow-100">
                            £{payrollReport.staff_payroll.reduce((sum, s) => sum + s.training_deduction, 0).toFixed(2)}
                          </td>
                          <td className="p-2 text-right border bg-blue-100">
                            £{payrollReport.staff_payroll.reduce((sum, s) => sum + s.reimbursement, 0).toFixed(2)}
                          </td>
                          <td className="p-2 text-right border text-xl text-green-600 bg-green-100">
                            £{payrollReport.staff_payroll.reduce((sum, s) => {
                              const basePay = (s.hours_weekday * s.rate_weekday) + 
                                             (s.hours_weekend * s.rate_weekend) + 
                                             (s.hours_bank_holiday * s.rate_bank_holiday);
                              return sum + basePay - s.dbs_deduction - s.transport_deduction - s.training_deduction + s.reimbursement;
                            }, 0).toFixed(2)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}

            {!payrollReport && (
              <Card>
                <CardContent className="p-8 text-center">
                  <p className="text-slate-500">
                    {payrollPeriodType === 'weekly' 
                      ? 'Select a week ending date and click "Generate Report" to view payroll details.'
                      : 'Select a year and month, then click "Generate Report" to view payroll details.'
                    }
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Payroll Export Section */}
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-6">
                <h3 className="font-semibold mb-2">Export Timesheet for Payroll Team</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Download an Excel spreadsheet with staff hours broken down by rate type, 
                  ready to send to your payroll team.
                </p>
                
                <div className="flex flex-wrap gap-4 items-end">
                  <div>
                    <Label className="text-sm">Export Type</Label>
                    <div className="flex rounded-lg border border-green-300 overflow-hidden mt-1">
                      <button
                        type="button"
                        onClick={() => setPayrollPeriodType('weekly')}
                        className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                          payrollPeriodType === 'weekly'
                            ? 'bg-green-600 text-white'
                            : 'bg-white text-green-600 hover:bg-green-50'
                        }`}
                      >
                        Weekly Staff
                      </button>
                      <button
                        type="button"
                        onClick={() => setPayrollPeriodType('monthly')}
                        className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                          payrollPeriodType === 'monthly'
                            ? 'bg-green-600 text-white'
                            : 'bg-white text-green-600 hover:bg-green-50'
                        }`}
                      >
                        Monthly Staff
                      </button>
                    </div>
                  </div>
                  
                  {payrollPeriodType === 'weekly' ? (
                    <div>
                      <Label className="text-sm">Week Ending</Label>
                      <Input
                        type="date"
                        value={payrollWeekEnding}
                        onChange={(e) => setPayrollWeekEnding(e.target.value)}
                        className="mt-1 w-40"
                        data-testid="export-week-ending"
                      />
                    </div>
                  ) : (
                    <>
                      <div>
                        <Label className="text-sm">Year</Label>
                        <select
                          value={payrollYear}
                          onChange={(e) => setPayrollYear(parseInt(e.target.value))}
                          className="mt-1 w-24 h-10 rounded-md border border-input bg-background px-3 py-2"
                        >
                          {[2024, 2025, 2026, 2027].map(y => (
                            <option key={y} value={y}>{y}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-sm">Month</Label>
                        <select
                          value={payrollMonth}
                          onChange={(e) => setPayrollMonth(parseInt(e.target.value))}
                          className="mt-1 w-32 h-10 rounded-md border border-input bg-background px-3 py-2"
                        >
                          {['January', 'February', 'March', 'April', 'May', 'June', 
                            'July', 'August', 'September', 'October', 'November', 'December'].map((m, i) => (
                            <option key={i+1} value={i+1}>{m}</option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                  
                  <Button
                    onClick={async () => {
                      try {
                        const API_URL = process.env.REACT_APP_BACKEND_URL;
                        let url = `${API_URL}/api/admin/payroll-export?period_type=${payrollPeriodType}`;
                        
                        if (payrollPeriodType === 'weekly') {
                          if (!payrollWeekEnding) {
                            toast.error('Please select a week ending date');
                            return;
                          }
                          url += `&week_ending=${payrollWeekEnding}`;
                        } else {
                          url += `&year=${payrollYear}&month=${payrollMonth}`;
                        }
                        
                        const response = await fetch(url, {
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        
                        if (!response.ok) {
                          const err = await response.json();
                          throw new Error(err.detail || 'Export failed');
                        }
                        
                        const blob = await response.blob();
                        const downloadUrl = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = downloadUrl;
                        
                        // Extract filename from Content-Disposition header
                        const contentDisposition = response.headers.get('Content-Disposition');
                        let filename = payrollPeriodType === 'weekly' 
                          ? `Koloth Care Week ending - ${payrollWeekEnding}.xlsx`
                          : `Koloth Care ${['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][payrollMonth-1]} ${payrollYear}.xlsx`;
                        
                        if (contentDisposition) {
                          const match = contentDisposition.match(/filename="(.+)"/);
                          if (match) filename = match[1];
                        }
                        
                        a.download = filename;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(downloadUrl);
                        a.remove();
                        
                        toast.success('Payroll export downloaded!');
                      } catch (error) {
                        toast.error(error.message || 'Failed to export payroll');
                      }
                    }}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="download-payroll-export"
                  >
                    Download Excel
                  </Button>
                </div>
                
                <p className="text-xs text-slate-500 mt-3">
                  Only includes staff with approved timesheets set to {payrollPeriodType} pay frequency.
                </p>
              </CardContent>
            </Card>

            {/* Limited Company Contractor Payroll */}
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="p-6">
                <h3 className="font-semibold mb-2 text-purple-800">Limited Company Contractor Payroll</h3>
                <p className="text-sm text-slate-600 mb-4">
                  Generate self-billing invoices for Limited Company contractors. 
                  Work week: Monday to Sunday. Payment: Next Friday.
                </p>
                
                <div className="flex items-end gap-4 flex-wrap">
                  <div className="min-w-48">
                    <Label>Week Ending (Sunday)</Label>
                    <Input
                      type="date"
                      value={ltdPayrollWeekEnding}
                      onChange={(e) => setLtdPayrollWeekEnding(e.target.value)}
                      className="max-w-xs"
                      data-testid="ltd-week-ending-input"
                    />
                  </div>
                  <Button
                    onClick={async () => {
                      if (!ltdPayrollWeekEnding) {
                        toast.error('Please select a week ending date');
                        return;
                      }
                      try {
                        const data = await apiCall(`/admin/ltd-company-payroll?week_ending=${ltdPayrollWeekEnding}`, 'GET', null, token);
                        setLtdPayrollData(data);
                        if (data.contractors.length === 0) {
                          toast.info('No Limited Company contractors with approved timesheets for this period');
                        }
                      } catch (error) {
                        toast.error(error.detail || 'Failed to load Ltd Company payroll');
                      }
                    }}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="load-ltd-payroll-btn"
                  >
                    Load Contractor Payroll
                  </Button>
                </div>

                {ltdPayrollData && (
                  <div className="mt-6 space-y-4">
                    <div className="flex justify-between items-center p-4 bg-white rounded-lg border">
                      <div>
                        <p className="text-sm text-slate-500">Period: {formatDateDMY(ltdPayrollData.week_start)} to {formatDateDMY(ltdPayrollData.week_end)}</p>
                        <p className="text-sm text-slate-500">Payment Date: <strong className="text-purple-700">{formatDateDMY(ltdPayrollData.payment_date)}</strong> (Friday)</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-500">{ltdPayrollData.contractor_count} Contractor(s)</p>
                        <p className="text-xl font-bold text-purple-700">£{ltdPayrollData.total_amount?.toFixed(2)}</p>
                      </div>
                    </div>

                    {ltdPayrollData.contractors.length === 0 ? (
                      <p className="text-center text-slate-500 py-4">No contractors with approved timesheets this week.</p>
                    ) : (
                      <div className="space-y-4">
                        {ltdPayrollData.contractors.map((contractor) => (
                          <Card key={contractor.staff_id} className="border-l-4 border-l-purple-500">
                            <CardContent className="p-4">
                              <div className="flex justify-between items-start">
                                <div>
                                  <h4 className="font-semibold">{contractor.name}</h4>
                                  <p className="text-sm text-purple-600">{contractor.company_name}</p>
                                  <p className="text-xs text-slate-500">Company No: {contractor.company_number}</p>
                                  <div className="mt-2 text-sm">
                                    <span className="text-slate-500">Rate: </span>
                                    <span className="font-medium">£{contractor.promised_rate?.toFixed(2)}/hr</span>
                                    <span className="mx-2">•</span>
                                    <span className="text-slate-500">Hours: </span>
                                    <span className="font-medium">{contractor.total_hours?.toFixed(2)}</span>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-2xl font-bold text-green-600">£{contractor.total_amount?.toFixed(2)}</p>
                                  <Button
                                    size="sm"
                                    className="mt-2 bg-purple-600 hover:bg-purple-700"
                                    onClick={async () => {
                                      try {
                                        const invoice = await apiCall(`/admin/ltd-company-invoice/${contractor.staff_id}?week_ending=${ltdPayrollWeekEnding}`, 'GET', null, token);
                                        setSelectedInvoice(invoice);
                                        toast.success('Invoice generated');
                                      } catch (error) {
                                        toast.error(error.detail || 'Failed to generate invoice');
                                      }
                                    }}
                                    data-testid={`generate-invoice-${contractor.staff_id}`}
                                  >
                                    Generate Invoice
                                  </Button>
                                </div>
                              </div>
                              
                              {/* Shift breakdown */}
                              <div className="mt-3 pt-3 border-t">
                                <p className="text-xs text-slate-500 mb-2">Shift Breakdown:</p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                  {contractor.shifts?.map((shift, idx) => (
                                    <div key={idx} className="bg-slate-50 p-2 rounded">
                                      <p className="font-medium">{formatDateDMY(shift.date)}</p>
                                      <p className="text-slate-500">{shift.type}</p>
                                      <p>{shift.hours}hrs × £{shift.rate} = <strong>£{shift.amount?.toFixed(2)}</strong></p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Invoice Preview Modal */}
                {selectedInvoice && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                      <CardHeader className="flex flex-row items-center justify-between border-b">
                        <CardTitle>Self-Billing Invoice</CardTitle>
                        <Button variant="ghost" size="sm" onClick={() => setSelectedInvoice(null)}>✕</Button>
                      </CardHeader>
                      <CardContent className="p-6 space-y-4">
                        <div className="text-center border-b pb-4">
                          <h2 className="text-xl font-bold">SELF BILLING INVOICE / REMITTANCE ADVICE</h2>
                          <p className="text-sm text-slate-500">Remittance No: {selectedInvoice.remittance_number}</p>
                        </div>

                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="font-semibold text-slate-600">Self Billing Invoice To:</p>
                            <p>{selectedInvoice.agency_name}</p>
                            <p className="text-slate-500">{selectedInvoice.agency_address}</p>
                          </div>
                          <div className="text-right">
                            <p><span className="text-slate-500">Date:</span> {formatDateDMY(selectedInvoice.remittance_date)}</p>
                            <p><span className="text-slate-500">Period Ending:</span> {selectedInvoice.period_ending_display}</p>
                          </div>
                        </div>

                        <div className="bg-purple-50 p-4 rounded-lg">
                          <p className="font-semibold text-purple-800">Contractor:</p>
                          <p className="text-lg">{selectedInvoice.contractor_company_name}</p>
                          <p className="text-sm text-slate-500">Reg: {selectedInvoice.contractor_company_number}</p>
                        </div>

                        <table className="w-full text-sm border">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="p-2 text-left border">Worker</th>
                              <th className="p-2 text-left border">Period</th>
                              <th className="p-2 text-left border">Type</th>
                              <th className="p-2 text-right border">Hours</th>
                              <th className="p-2 text-right border">Rate</th>
                              <th className="p-2 text-right border">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedInvoice.line_items?.map((item, idx) => (
                              <tr key={idx}>
                                <td className="p-2 border">{item.worker_name}</td>
                                <td className="p-2 border">{item.period_ending}</td>
                                <td className="p-2 border">{item.type}</td>
                                <td className="p-2 text-right border">{item.quantity?.toFixed(2)}</td>
                                <td className="p-2 text-right border">£{item.rate?.toFixed(2)}</td>
                                <td className="p-2 text-right border font-medium">£{item.total?.toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-slate-50">
                            <tr>
                              <td colSpan="5" className="p-2 text-right border font-medium">Net Total:</td>
                              <td className="p-2 text-right border">£{selectedInvoice.net_total?.toFixed(2)}</td>
                            </tr>
                            <tr>
                              <td colSpan="5" className="p-2 text-right border">VAT @ {selectedInvoice.vat_rate}%:</td>
                              <td className="p-2 text-right border">£{selectedInvoice.vat_amount?.toFixed(2)}</td>
                            </tr>
                            <tr className="font-bold">
                              <td colSpan="5" className="p-2 text-right border">TOTAL:</td>
                              <td className="p-2 text-right border text-green-600">£{selectedInvoice.total?.toFixed(2)}</td>
                            </tr>
                          </tfoot>
                        </table>

                        <div className="bg-green-50 p-4 rounded-lg">
                          <p className="font-semibold text-green-800">Payment Details</p>
                          <div className="grid grid-cols-2 gap-2 text-sm mt-2">
                            <p><span className="text-slate-500">Pay to:</span> {selectedInvoice.contractor_company_name}</p>
                            <p><span className="text-slate-500">Bank:</span> {selectedInvoice.bank_name || 'Not provided'}</p>
                            <p><span className="text-slate-500">Account:</span> {selectedInvoice.account_number || 'N/A'}</p>
                            <p><span className="text-slate-500">Sort Code:</span> {selectedInvoice.sort_code || 'N/A'}</p>
                            <p className="col-span-2"><span className="text-slate-500">Payment Date:</span> <strong>{selectedInvoice.payment_date_display}</strong></p>
                          </div>
                        </div>

                        <div className="flex justify-end gap-2 pt-4 border-t">
                          <Button variant="outline" onClick={() => window.print()}>
                            Print
                          </Button>
                          <Button
                            className="bg-green-600 hover:bg-green-700"
                            onClick={async () => {
                              try {
                                const API_URL = process.env.REACT_APP_BACKEND_URL;
                                const response = await fetch(`${API_URL}/api/admin/ltd-company-invoice/${selectedInvoice.id}/export`, {
                                  headers: { 'Authorization': `Bearer ${token}` }
                                });
                                if (!response.ok) throw new Error('Export failed');
                                const blob = await response.blob();
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `Invoice_${selectedInvoice.remittance_number}.xlsx`;
                                a.click();
                                toast.success('Invoice exported');
                              } catch (error) {
                                toast.error('Failed to export invoice');
                              }
                            }}
                          >
                            Download Excel
                          </Button>
                          <Button onClick={() => setSelectedInvoice(null)}>
                            Close
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Compliance Alerts Tab */}
        <TabsContent value="compliance" className="space-y-6">
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-semibold text-slate-800">Compliance Alerts</h2>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      const data = await apiCall('/compliance/dashboard', 'GET', null, token);
                      setComplianceDashboard(data);
                      toast.success('Compliance data refreshed');
                    } catch (error) {
                      toast.error('Failed to refresh compliance data');
                    }
                  }}
                >
                  🔄 Refresh
                </Button>
                <Button
                  variant="outline"
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  onClick={async () => {
                    if (!window.confirm('Send expiry alert emails to all staff with expiring/expired items?')) return;
                    try {
                      const result = await apiCall('/admin/check-expiry-alerts', 'POST', null, token);
                      toast.success(`Alerts sent to ${result.staff_alerted} staff members`);
                    } catch (error) {
                      toast.error(error.detail || 'Failed to send alerts');
                    }
                  }}
                >
                  📧 Send Expiry Alerts
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={async () => {
                    try {
                      const response = await fetch(
                        `${process.env.REACT_APP_BACKEND_URL}/api/admin/training-report/export`,
                        { headers: { 'Authorization': `Bearer ${token}` } }
                      );
                      if (!response.ok) throw new Error('Export failed');
                      
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `Training_Compliance_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success('Training report downloaded');
                    } catch (error) {
                      toast.error('Failed to download report');
                    }
                  }}
                >
                  📊 Download Training Report
                </Button>
              </div>
            </div>
            
            <p className="text-slate-600">
              Review all staff compliance issues in one place. Take action on expired or expiring documents daily.
            </p>
            
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className={`${complianceDashboard?.expired?.length > 0 ? 'bg-red-50 border-red-300' : 'bg-slate-50'}`}>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-red-600">{complianceDashboard?.expired?.length || 0}</p>
                  <p className="text-sm text-slate-600">⛔ Expired</p>
                </CardContent>
              </Card>
              <Card className={`${complianceDashboard?.critical?.length > 0 ? 'bg-red-50 border-red-200' : 'bg-slate-50'}`}>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-red-500">{complianceDashboard?.critical?.length || 0}</p>
                  <p className="text-sm text-slate-600">🔴 Critical (&lt;30 days)</p>
                </CardContent>
              </Card>
              <Card className={`${complianceDashboard?.warning?.length > 0 ? 'bg-orange-50 border-orange-200' : 'bg-slate-50'}`}>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-orange-500">{complianceDashboard?.warning?.length || 0}</p>
                  <p className="text-sm text-slate-600">🟠 Warning (30-60 days)</p>
                </CardContent>
              </Card>
              <Card className={`${complianceDashboard?.notice?.length > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-slate-50'}`}>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-yellow-600">{complianceDashboard?.notice?.length || 0}</p>
                  <p className="text-sm text-slate-600">🟡 Notice (60-90 days)</p>
                </CardContent>
              </Card>
            </div>
            
            {/* Expired Documents - URGENT */}
            {complianceDashboard?.expired?.length > 0 && (
              <Card className="border-red-300 bg-red-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-red-700 flex items-center gap-2">
                    ⛔ EXPIRED - Immediate Action Required ({complianceDashboard.expired.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {complianceDashboard.expired.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-white rounded border border-red-200">
                        <div>
                          <p className="font-semibold">{item.staff_name}</p>
                          <p className="text-sm text-slate-600">
                            <span className="capitalize">{item.type?.replace('_', ' ')}</span>
                            {item.item && `: ${item.item}`}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-red-600 font-semibold">
                            Expired {item.days_overdue} days ago
                          </p>
                          <p className="text-xs text-slate-500">
                            Expiry: {formatDateDMY(item.expiry_date)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Critical - Less than 30 days */}
            {complianceDashboard?.critical?.length > 0 && (
              <Card className="border-red-200 bg-red-50/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-red-600 flex items-center gap-2">
                    🔴 Critical - Expiring Within 30 Days ({complianceDashboard.critical.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {complianceDashboard.critical.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-white rounded border">
                        <div>
                          <p className="font-semibold">{item.staff_name}</p>
                          <p className="text-sm text-slate-600">
                            <span className="capitalize">{item.type?.replace('_', ' ')}</span>
                            {item.item && `: ${item.item}`}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-red-600 font-semibold">
                            {item.days_remaining} days remaining
                          </p>
                          <p className="text-xs text-slate-500">
                            Expiry: {formatDateDMY(item.expiry_date)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Warning - 30-60 days */}
            {complianceDashboard?.warning?.length > 0 && (
              <Card className="border-orange-200 bg-orange-50/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-orange-600 flex items-center gap-2">
                    🟠 Warning - Expiring Within 30-60 Days ({complianceDashboard.warning.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {complianceDashboard.warning.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-white rounded border">
                        <div>
                          <p className="font-semibold">{item.staff_name}</p>
                          <p className="text-sm text-slate-600">
                            <span className="capitalize">{item.type?.replace('_', ' ')}</span>
                            {item.item && `: ${item.item}`}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-orange-600 font-semibold">
                            {item.days_remaining} days remaining
                          </p>
                          <p className="text-xs text-slate-500">
                            Expiry: {formatDateDMY(item.expiry_date)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Notice - 60-90 days */}
            {complianceDashboard?.notice?.length > 0 && (
              <Card className="border-yellow-200 bg-yellow-50/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-yellow-700 flex items-center gap-2">
                    🟡 Notice - Expiring Within 60-90 Days ({complianceDashboard.notice.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {complianceDashboard.notice.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-white rounded border">
                        <div>
                          <p className="font-semibold">{item.staff_name}</p>
                          <p className="text-sm text-slate-600">
                            <span className="capitalize">{item.type?.replace('_', ' ')}</span>
                            {item.item && `: ${item.item}`}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-yellow-700 font-semibold">
                            {item.days_remaining} days remaining
                          </p>
                          <p className="text-xs text-slate-500">
                            Expiry: {formatDateDMY(item.expiry_date)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* All Clear Message */}
            {(!complianceDashboard || complianceDashboard.total_issues === 0) && (
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-8 text-center">
                  <p className="text-4xl mb-2">✅</p>
                  <p className="text-green-700 font-semibold text-lg">All Staff Compliant</p>
                  <p className="text-slate-600">No expired or expiring documents found. Great job!</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="new-features" className="space-y-6">
          <Tabs defaultValue="clients" className="space-y-4">
            <TabsList className="grid w-full grid-cols-7">
              <TabsTrigger value="clients">Clients</TabsTrigger>
              <TabsTrigger value="staff">Staff Profiles</TabsTrigger>
              <TabsTrigger value="archived">Archived Staff</TabsTrigger>
              <TabsTrigger value="invoices">Invoices</TabsTrigger>
              <TabsTrigger value="documents" data-testid="admin-documents-tab">Documents</TabsTrigger>
              <TabsTrigger value="whatsapp" data-testid="whatsapp-tab">WhatsApp</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
            </TabsList>

            {/* Signed Documents Tab */}
            <TabsContent value="documents" className="space-y-4">
              <AdminSignedDocuments token={token} apiCall={apiCall} />
            </TabsContent>

            {/* WhatsApp Tab */}
            <TabsContent value="whatsapp" className="space-y-4">
              <WhatsAppPanel token={token} apiCall={apiCall} />
            </TabsContent>

            {/* Settings Tab - Gmail Integration */}
            <TabsContent value="settings" className="space-y-4">
              <h3 className="text-xl font-semibold">Email Settings</h3>
              
              <Card className={gmailStatus?.connected ? 'border-green-200 bg-green-50' : 'border-orange-200 bg-orange-50'}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-semibold text-lg mb-2">
                        {gmailStatus?.connected ? '✅ Gmail Connected' : '📧 Gmail Integration'}
                      </h4>
                      <p className="text-sm text-slate-600 mb-4">
                        {gmailStatus?.connected 
                          ? `Connected as: ${gmailStatus.email}`
                          : 'Connect your Google Workspace email to send automatic notifications for self-booking approvals/rejections.'
                        }
                      </p>
                      
                      {gmailStatus?.connected && (
                        <div className="mt-4 space-y-3">
                          <div>
                            <Label htmlFor="sender-email" className="text-sm font-medium">Sender Email (Alias)</Label>
                            <div className="flex gap-2 mt-1">
                              <Input
                                id="sender-email"
                                type="email"
                                placeholder="e.g. shifts@koloth.com"
                                value={senderEmail}
                                onChange={(e) => setSenderEmail(e.target.value)}
                                className="max-w-xs"
                              />
                              <Button
                                size="sm"
                                onClick={async () => {
                                  if (!senderEmail) {
                                    toast.error('Please enter a sender email');
                                    return;
                                  }
                                  try {
                                    await apiCall(`/oauth/gmail/sender?sender_email=${encodeURIComponent(senderEmail)}`, 'POST', null, token);
                                    toast.success(`Sender email set to ${senderEmail}`);
                                  } catch (error) {
                                    toast.error(error.detail || 'Failed to set sender email');
                                  }
                                }}
                              >
                                Save
                              </Button>
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                              Set up the alias in Gmail Settings → Accounts → Send mail as, then enter it here.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-col gap-2">
                      {gmailStatus?.connected ? (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={gmailLoading}
                            onClick={async () => {
                              setGmailLoading(true);
                              try {
                                await apiCall('/oauth/gmail/test', 'POST', null, token);
                                toast.success('Test email sent! Check your inbox.');
                              } catch (error) {
                                toast.error(error.detail || 'Failed to send test email');
                              }
                              setGmailLoading(false);
                            }}
                            data-testid="test-gmail-btn"
                          >
                            {gmailLoading ? 'Sending...' : 'Send Test Email'}
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            disabled={gmailLoading}
                            onClick={async () => {
                              if (window.confirm('Disconnect Gmail? Email notifications will stop working.')) {
                                setGmailLoading(true);
                                try {
                                  await apiCall('/oauth/gmail/disconnect', 'POST', null, token);
                                  toast.success('Gmail disconnected');
                                  setGmailStatus({ connected: false, configured: true });
                                } catch (error) {
                                  toast.error(error.detail || 'Failed to disconnect');
                                }
                                setGmailLoading(false);
                              }
                            }}
                            data-testid="disconnect-gmail-btn"
                          >
                            Disconnect
                          </Button>
                        </>
                      ) : (
                        <Button
                          className="bg-blue-600 hover:bg-blue-700"
                          disabled={gmailLoading || !gmailStatus?.configured}
                          onClick={async () => {
                            setGmailLoading(true);
                            try {
                              const response = await apiCall('/oauth/gmail/login', 'GET', null, token);
                              if (response.authorization_url) {
                                window.location.href = response.authorization_url;
                              }
                            } catch (error) {
                              toast.error(error.detail || 'Failed to start Gmail connection');
                              setGmailLoading(false);
                            }
                          }}
                          data-testid="connect-gmail-btn"
                        >
                          {gmailLoading ? 'Connecting...' : 'Connect Gmail'}
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {!gmailStatus?.connected && !gmailStatus?.configured && (
                    <div className="mt-4 p-3 bg-yellow-100 rounded text-sm text-yellow-800">
                      ⚠️ Gmail credentials not configured in backend. Please add GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET to your environment.
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Mandatory Trainings Section */}
              <h3 className="text-xl font-semibold mt-8">Mandatory Trainings</h3>
              <p className="text-sm text-slate-600">Tick the trainings that are mandatory for staff. Practical trainings cannot be completed online.</p>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-semibold">Training Requirements</h4>
                    <Button size="sm" onClick={() => setShowAddTraining(true)}>+ Add Training</Button>
                  </div>
                  
                  {showAddTraining && (
                    <div className="mb-4 p-4 bg-slate-50 rounded-lg space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <Input
                          placeholder="Training name"
                          value={newTrainingData.name}
                          onChange={(e) => setNewTrainingData({...newTrainingData, name: e.target.value})}
                        />
                        <Select value={newTrainingData.training_type} onValueChange={(v) => setNewTrainingData({...newTrainingData, training_type: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="online">Online</SelectItem>
                            <SelectItem value="practical">Practical</SelectItem>
                            <SelectItem value="both">Both (Online + Practical)</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={newTrainingData.required_for} onValueChange={(v) => setNewTrainingData({...newTrainingData, required_for: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Staff</SelectItem>
                            <SelectItem value="nurse">Nurses Only</SelectItem>
                            <SelectItem value="carer">Carers Only</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={String(newTrainingData.default_validity_years)} onValueChange={(v) => setNewTrainingData({...newTrainingData, default_validity_years: parseInt(v)})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">Valid 1 year</SelectItem>
                            <SelectItem value="2">Valid 2 years</SelectItem>
                            <SelectItem value="3">Valid 3 years</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={async () => {
                          if (!newTrainingData.name) {
                            toast.error('Training name is required');
                            return;
                          }
                          try {
                            await apiCall('/mandatory-trainings', 'POST', {...newTrainingData, is_active: true}, token);
                            toast.success('Training added');
                            setShowAddTraining(false);
                            setNewTrainingData({ name: '', training_type: 'online', required_for: 'all', default_validity_years: 1 });
                            fetchData();
                          } catch (error) {
                            toast.error(error.detail || 'Failed to add training');
                          }
                        }}>Save</Button>
                        <Button size="sm" variant="outline" onClick={() => setShowAddTraining(false)}>Cancel</Button>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    {mandatoryTrainings.map((training) => (
                      <div key={training.id} className={`flex items-center justify-between p-3 rounded border-l-4 transition-colors ${
                        training.is_active ? 'bg-green-50 border-l-green-500 hover:bg-green-100' : 'bg-slate-50 border-l-slate-300 hover:bg-slate-100'
                      }`}>
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={training.is_active}
                            onChange={async (e) => {
                              try {
                                await apiCall(`/mandatory-trainings/${training.id}`, 'PUT', { is_active: e.target.checked }, token);
                                toast.success(e.target.checked ? 'Training is now MANDATORY' : 'Training is now OPTIONAL');
                                fetchData();
                              } catch (error) {
                                toast.error('Failed to update training');
                              }
                            }}
                            className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer"
                            title={training.is_active ? 'Click to make optional' : 'Click to make mandatory'}
                          />
                          <div className={!training.is_active ? 'opacity-60' : ''}>
                            <span className="font-medium">{training.name}</span>
                            {training.is_active && <Badge className="ml-2 bg-green-600 text-xs">Mandatory</Badge>}
                            {!training.is_active && <Badge variant="outline" className="ml-2 text-xs">Optional</Badge>}
                            <div className="flex gap-2 mt-1">
                              <Badge variant="outline" className={
                                training.training_type === 'practical' ? 'bg-orange-100 text-orange-800' :
                                training.training_type === 'both' ? 'bg-purple-100 text-purple-800' :
                                'bg-blue-100 text-blue-800'
                              }>
                                {training.training_type === 'practical' ? '🏋️ Practical' : 
                                 training.training_type === 'both' ? '📚 Both' : '💻 Online'}
                              </Badge>
                              <Badge variant="outline">{training.default_validity_years} yr validity</Badge>
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-800"
                          onClick={async () => {
                            if (window.confirm(`Delete "${training.name}" permanently?`)) {
                              try {
                                await apiCall(`/mandatory-trainings/${training.id}`, 'DELETE', null, token);
                                toast.success('Training deleted');
                                fetchData();
                              } catch (error) {
                                toast.error('Failed to delete training');
                              }
                            }
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    ))}
                  </div>
                  
                  <p className="text-xs text-slate-500 mt-4">
                    ✅ Ticked = Mandatory for all staff | ⬜ Unticked = Optional (not required for compliance)
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="clients" className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold">Client Management</h3>
                <Button onClick={() => setShowCreateClient(true)}>Add New Client</Button>
              </div>
              
              {(showCreateClient || editingClient) && (
                <Card>
                  <CardHeader>
                    <CardTitle>{editingClient ? 'Edit Client' : 'Add New Client'}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleCreateClient} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          placeholder="Client Name"
                          value={newClient.name}
                          onChange={(e) => setNewClient({...newClient, name: e.target.value})}
                          required
                        />
                        <Input
                          placeholder="Contact Person"
                          value={newClient.contact_person}
                          onChange={(e) => setNewClient({...newClient, contact_person: e.target.value})}
                        />
                        <Textarea
                          placeholder="Address"
                          className="md:col-span-2"
                          value={newClient.address}
                          onChange={(e) => setNewClient({...newClient, address: e.target.value})}
                          required
                        />
                        <Input
                          placeholder="Phone"
                          value={newClient.phone}
                          onChange={(e) => setNewClient({...newClient, phone: e.target.value})}
                        />
                        <Input
                          placeholder="Email"
                          type="email"
                          value={newClient.email}
                          onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                        />
                      </div>

                      <div className="border rounded-lg p-4 bg-purple-50 border-purple-200 space-y-3">
                        <h4 className="font-semibold text-sm text-purple-800">Billing Group (optional)</h4>
                        <p className="text-xs text-slate-600">If this location belongs to a parent group (e.g. St Peters is part of Iris Care Group), invoices will be addressed to the group instead.</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <Input
                            placeholder="Billing Group Name (e.g. Iris Care Group)"
                            data-testid="client-billing-group-name-input"
                            value={newClient.billing_group_name}
                            onChange={(e) => setNewClient({...newClient, billing_group_name: e.target.value})}
                          />
                          <Textarea
                            placeholder="Billing Group Address"
                            data-testid="client-billing-group-address-input"
                            value={newClient.billing_group_address}
                            onChange={(e) => setNewClient({...newClient, billing_group_address: e.target.value})}
                            rows={2}
                          />
                        </div>
                      </div>

                      <div className="border rounded-lg p-4 bg-teal-50 border-teal-200 space-y-3">
                        <h4 className="font-semibold text-sm text-teal-800">Shift Patterns</h4>
                        <p className="text-xs text-slate-600">These appear as quick picks when creating shifts for this client. Times stay editable on each shift.</p>
                        {(newClient.shift_patterns || []).map((p, idx) => (
                          <div key={idx} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center">
                            <Input
                              placeholder="Pattern name (e.g. Long Day)"
                              data-testid={`pattern-name-${idx}`}
                              value={p.name}
                              onChange={(e) => {
                                const next = [...newClient.shift_patterns];
                                next[idx] = { ...next[idx], name: e.target.value };
                                setNewClient({ ...newClient, shift_patterns: next });
                              }}
                            />
                            <Input
                              type="time"
                              className="w-28"
                              data-testid={`pattern-start-${idx}`}
                              value={p.start_time}
                              onChange={(e) => {
                                const next = [...newClient.shift_patterns];
                                next[idx] = { ...next[idx], start_time: e.target.value };
                                setNewClient({ ...newClient, shift_patterns: next });
                              }}
                            />
                            <Input
                              type="time"
                              className="w-28"
                              data-testid={`pattern-end-${idx}`}
                              value={p.end_time}
                              onChange={(e) => {
                                const next = [...newClient.shift_patterns];
                                next[idx] = { ...next[idx], end_time: e.target.value };
                                setNewClient({ ...newClient, shift_patterns: next });
                              }}
                            />
                            <Button type="button" variant="outline" size="sm" className="text-red-600"
                              data-testid={`pattern-remove-${idx}`}
                              onClick={() => setNewClient({ ...newClient, shift_patterns: newClient.shift_patterns.filter((_, i) => i !== idx) })}>
                              ✕
                            </Button>
                          </div>
                        ))}
                        <Button type="button" variant="outline" size="sm" data-testid="pattern-add-btn"
                          onClick={() => setNewClient({ ...newClient, shift_patterns: [...(newClient.shift_patterns || []), { name: '', start_time: '08:00', end_time: '20:00' }] })}>
                          + Add pattern
                        </Button>
                      </div>

                      <div className="space-y-3">
                        <h4 className="font-semibold text-sm">Pay Rates (£/hour)</h4>
                        
                        {/* NURSES SECTION */}
                        <div className="border rounded-lg p-4 space-y-3">
                          <h5 className="font-medium text-sm text-blue-700">Nurses</h5>
                          
                          {/* Weekday */}
                          <div className="bg-slate-50 p-2 rounded">
                            <p className="text-xs font-semibold mb-2">Monday - Friday</p>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs">Day</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="0.00"
                                  value={newClient.pay_rates?.nurses_weekday || ''}
                                  onChange={(e) => setNewClient({
                                    ...newClient,
                                    pay_rates: {
                                      ...newClient.pay_rates,
                                      nurses_weekday: e.target.value === '' ? '' : parseFloat(e.target.value)
                                    }
                                  })}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Night</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="0.00"
                                  value={newClient.pay_rates?.nurses_weeknight || ''}
                                  onChange={(e) => setNewClient({
                                    ...newClient,
                                    pay_rates: {
                                      ...newClient.pay_rates,
                                      nurses_weeknight: e.target.value === '' ? '' : parseFloat(e.target.value)
                                    }
                                  })}
                                />
                              </div>
                            </div>
                          </div>
                          
                          {/* Saturday */}
                          <div className="bg-blue-50 p-2 rounded">
                            <p className="text-xs font-semibold mb-2">Saturday</p>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs">Day</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="0.00"
                                  value={newClient.pay_rates?.nurses_saturday_day || ''}
                                  onChange={(e) => setNewClient({
                                    ...newClient,
                                    pay_rates: {
                                      ...newClient.pay_rates,
                                      nurses_saturday_day: e.target.value === '' ? '' : parseFloat(e.target.value)
                                    }
                                  })}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Night</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="0.00"
                                  value={newClient.pay_rates?.nurses_saturday_night || ''}
                                  onChange={(e) => setNewClient({
                                    ...newClient,
                                    pay_rates: {
                                      ...newClient.pay_rates,
                                      nurses_saturday_night: e.target.value === '' ? '' : parseFloat(e.target.value)
                                    }
                                  })}
                                />
                              </div>
                            </div>
                          </div>
                          
                          {/* Sunday */}
                          <div className="bg-blue-50 p-2 rounded">
                            <p className="text-xs font-semibold mb-2">Sunday</p>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs">Day</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="0.00"
                                  value={newClient.pay_rates?.nurses_sunday_day || ''}
                                  onChange={(e) => setNewClient({
                                    ...newClient,
                                    pay_rates: {
                                      ...newClient.pay_rates,
                                      nurses_sunday_day: e.target.value === '' ? '' : parseFloat(e.target.value)
                                    }
                                  })}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Night</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="0.00"
                                  value={newClient.pay_rates?.nurses_sunday_night || ''}
                                  onChange={(e) => setNewClient({
                                    ...newClient,
                                    pay_rates: {
                                      ...newClient.pay_rates,
                                      nurses_sunday_night: e.target.value === '' ? '' : parseFloat(e.target.value)
                                    }
                                  })}
                                />
                              </div>
                            </div>
                          </div>
                          
                          {/* Bank Holiday */}
                          <div className="bg-yellow-50 p-2 rounded">
                            <p className="text-xs font-semibold mb-2">Bank Holiday</p>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs">Day</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="0.00"
                                  value={newClient.pay_rates?.nurses_bank_holiday_day || ''}
                                  onChange={(e) => setNewClient({
                                    ...newClient,
                                    pay_rates: {
                                      ...newClient.pay_rates,
                                      nurses_bank_holiday_day: e.target.value === '' ? '' : parseFloat(e.target.value)
                                    }
                                  })}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Night</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="0.00"
                                  value={newClient.pay_rates?.nurses_bank_holiday_night || ''}
                                  onChange={(e) => setNewClient({
                                    ...newClient,
                                    pay_rates: {
                                      ...newClient.pay_rates,
                                      nurses_bank_holiday_night: e.target.value === '' ? '' : parseFloat(e.target.value)
                                    }
                                  })}
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* CARERS SECTION */}
                        <div className="border rounded-lg p-4 space-y-3">
                          <h5 className="font-medium text-sm text-green-700">Carers</h5>
                          
                          {/* Weekday */}
                          <div className="bg-slate-50 p-2 rounded">
                            <p className="text-xs font-semibold mb-2">Monday - Friday</p>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs">Day</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="0.00"
                                  value={newClient.pay_rates?.carers_weekday || ''}
                                  onChange={(e) => setNewClient({
                                    ...newClient,
                                    pay_rates: {
                                      ...newClient.pay_rates,
                                      carers_weekday: e.target.value === '' ? '' : parseFloat(e.target.value)
                                    }
                                  })}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Night</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="0.00"
                                  value={newClient.pay_rates?.carers_weeknight || ''}
                                  onChange={(e) => setNewClient({
                                    ...newClient,
                                    pay_rates: {
                                      ...newClient.pay_rates,
                                      carers_weeknight: e.target.value === '' ? '' : parseFloat(e.target.value)
                                    }
                                  })}
                                />
                              </div>
                            </div>
                          </div>
                          
                          {/* Saturday */}
                          <div className="bg-green-50 p-2 rounded">
                            <p className="text-xs font-semibold mb-2">Saturday</p>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs">Day</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="0.00"
                                  value={newClient.pay_rates?.carers_saturday_day || ''}
                                  onChange={(e) => setNewClient({
                                    ...newClient,
                                    pay_rates: {
                                      ...newClient.pay_rates,
                                      carers_saturday_day: e.target.value === '' ? '' : parseFloat(e.target.value)
                                    }
                                  })}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Night</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="0.00"
                                  value={newClient.pay_rates?.carers_saturday_night || ''}
                                  onChange={(e) => setNewClient({
                                    ...newClient,
                                    pay_rates: {
                                      ...newClient.pay_rates,
                                      carers_saturday_night: e.target.value === '' ? '' : parseFloat(e.target.value)
                                    }
                                  })}
                                />
                              </div>
                            </div>
                          </div>
                          
                          {/* Sunday */}
                          <div className="bg-green-50 p-2 rounded">
                            <p className="text-xs font-semibold mb-2">Sunday</p>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs">Day</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="0.00"
                                  value={newClient.pay_rates?.carers_sunday_day || ''}
                                  onChange={(e) => setNewClient({
                                    ...newClient,
                                    pay_rates: {
                                      ...newClient.pay_rates,
                                      carers_sunday_day: e.target.value === '' ? '' : parseFloat(e.target.value)
                                    }
                                  })}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Night</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="0.00"
                                  value={newClient.pay_rates?.carers_sunday_night || ''}
                                  onChange={(e) => setNewClient({
                                    ...newClient,
                                    pay_rates: {
                                      ...newClient.pay_rates,
                                      carers_sunday_night: e.target.value === '' ? '' : parseFloat(e.target.value)
                                    }
                                  })}
                                />
                              </div>
                            </div>
                          </div>
                          
                          {/* Bank Holiday */}
                          <div className="bg-yellow-50 p-2 rounded">
                            <p className="text-xs font-semibold mb-2">Bank Holiday</p>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs">Day</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="0.00"
                                  value={newClient.pay_rates?.carers_bank_holiday_day || ''}
                                  onChange={(e) => setNewClient({
                                    ...newClient,
                                    pay_rates: {
                                      ...newClient.pay_rates,
                                      carers_bank_holiday_day: e.target.value === '' ? '' : parseFloat(e.target.value)
                                    }
                                  })}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Night</Label>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="0.00"
                                  value={newClient.pay_rates?.carers_bank_holiday_night || ''}
                                  onChange={(e) => setNewClient({
                                    ...newClient,
                                    pay_rates: {
                                      ...newClient.pay_rates,
                                      carers_bank_holiday_night: e.target.value === '' ? '' : parseFloat(e.target.value)
                                    }
                                  })}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Client-Specific Required Trainings */}
                      <div className="space-y-3">
                        <h4 className="font-semibold text-sm">Additional Required Trainings</h4>
                        <p className="text-xs text-slate-500">Select any extra trainings staff must have to work with this client (beyond mandatory trainings)</p>
                        
                        <div className="border rounded-lg p-4 space-y-2 max-h-48 overflow-y-auto">
                          {mandatoryTrainings.filter(t => t.is_active).map((training) => (
                            <div key={training.id} className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id={`client-training-${training.id}`}
                                checked={newClient.required_trainings?.some(t => t.training_name === training.name) || false}
                                onChange={(e) => {
                                  const current = newClient.required_trainings || [];
                                  if (e.target.checked) {
                                    setNewClient({
                                      ...newClient,
                                      required_trainings: [...current, { training_name: training.name, training_type: training.training_type }]
                                    });
                                  } else {
                                    setNewClient({
                                      ...newClient,
                                      required_trainings: current.filter(t => t.training_name !== training.name)
                                    });
                                  }
                                }}
                                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <label htmlFor={`client-training-${training.id}`} className="text-sm cursor-pointer flex-1">
                                {training.name}
                                {training.training_type === 'practical' && (
                                  <span className="text-xs text-orange-600 ml-2">(Practical)</span>
                                )}
                              </label>
                            </div>
                          ))}
                        </div>
                        
                        {(newClient.required_trainings?.length || 0) > 0 && (
                          <p className="text-xs text-blue-600">
                            {newClient.required_trainings.length} additional training(s) required for this client
                          </p>
                        )}
                      </div>

                      <div className="flex space-x-2">
                        <Button type="submit">{editingClient ? 'Update Client' : 'Create Client'}</Button>
                        <Button type="button" variant="outline" onClick={() => {
                          setShowCreateClient(false);
                          setEditingClient(null);
                        }}>
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              <div className="grid gap-4">
                {clients.map((client) => (
                  <Card key={client.id}>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold">{client.name}</h4>
                          <p className="text-slate-600 text-sm">{client.address}</p>
                          {client.billing_group_name && (
                            <p className="text-xs text-purple-700 font-medium mt-1" data-testid={`client-billing-group-${client.id}`}>
                              🏢 Bills to: {client.billing_group_name}
                            </p>
                          )}
                          <div className="flex space-x-4 mt-2 text-xs text-slate-500">
                            {client.contact_person && <span>Contact: {client.contact_person}</span>}
                            {client.phone && <span>Phone: {client.phone}</span>}
                            {client.email && <span>Email: {client.email}</span>}
                          </div>
                          {client.required_trainings && client.required_trainings.length > 0 && (
                            <div className="mt-2">
                              <span className="text-xs font-medium text-slate-600">Required Trainings: </span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {client.required_trainings.map((t, i) => (
                                  <Badge key={i} variant="outline" className="text-xs bg-blue-50">
                                    {t.training_name}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => {
                              setEditingClient(client);
                              setNewClient({
                                name: client.name,
                                address: client.address,
                                contact_person: client.contact_person || '',
                                phone: client.phone || '',
                                email: client.email || '',
                                billing_group_name: client.billing_group_name || '',
                                billing_group_address: client.billing_group_address || '',
                                shift_patterns: client.shift_patterns?.length ? client.shift_patterns : defaultShiftPatterns(),
                                pay_rates: client.pay_rates || {},
                                required_trainings: client.required_trainings || []
                              });
                            }}
                          >
                            Edit
                          </Button>
                          <Button variant="destructive" size="sm" onClick={async () => {
                            if (window.confirm(`Delete ${client.name}? This will permanently remove the client and all associated data.`)) {
                              try {
                                console.log('Deleting client:', client.id);
                                await apiCall(`/clients/${client.id}`, 'DELETE', null, token);
                                console.log('Client deleted successfully');
                                toast.success(`${client.name} deleted successfully`);
                                // Update clients list immediately
                                const updatedClients = clients.filter(c => c.id !== client.id);
                                console.log('Updated clients list:', updatedClients.length);
                                setClients(updatedClients);
                              } catch (error) {
                                console.error('Delete client error:', error);
                                toast.error('Failed to delete client');
                              }
                            }
                          }}>
                            Delete
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {clients.length === 0 && (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <p className="text-slate-500">No clients added yet.</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            <TabsContent value="staff" className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold">Staff Management</h3>
                <Button 
                  onClick={() => setShowAddStaff(true)}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                >
                  Add New Staff Member
                </Button>
              </div>
              
              <div className="grid gap-4">
                {/* Sort: Pending approval first, then by name */}
                {[...staffUsers].sort((a, b) => {
                  // Pending approval first
                  if (!a.user.approved && b.user.approved) return -1;
                  if (a.user.approved && !b.user.approved) return 1;
                  // Then alphabetically by name
                  return (a.user.name || '').localeCompare(b.user.name || '');
                }).map((staffData) => (
                  <Card key={staffData.user.id} className={!staffData.user.approved ? 'border-l-4 border-l-orange-500' : ''}>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-3">
                            <h4 className="font-semibold text-lg">{staffData.user.name}</h4>
                            <Badge className={staffData.profile?.staff_type === 'nurse' ? 'bg-blue-600' : staffData.profile?.staff_type === 'carer' ? 'bg-teal-600' : 'bg-slate-400'} data-testid={`job-role-badge-${staffData.user.id}`}>
                              {staffData.profile?.staff_type === 'nurse' ? 'Nurse' : staffData.profile?.staff_type === 'carer' ? 'Support Worker / Care Assistant' : 'Role Not Set'}
                            </Badge>
                            <select
                              value={staffData.profile?.staff_type || ''}
                              data-testid={`quick-role-select-${staffData.user.id}`}
                              className="h-7 text-xs border border-slate-300 rounded px-1 bg-white text-slate-600"
                              onChange={async (e) => {
                                try {
                                  await apiCall(`/staff-profiles/${staffData.user.id}`, 'PUT', { user_id: staffData.user.id, staff_type: e.target.value }, token);
                                  toast.success(`Job role updated to ${e.target.value === 'nurse' ? 'Nurse' : 'Support Worker / Care Assistant'}`);
                                  refetchData();
                                } catch (error) {
                                  toast.error('Failed to update job role');
                                }
                              }}
                            >
                              <option value="" disabled>Set role…</option>
                              <option value="nurse">Nurse</option>
                              <option value="carer">Support Worker / Care Assistant</option>
                            </select>
                            <Badge variant={staffData.profile ? "default" : "secondary"}>
                              {staffData.profile ? "Profile Complete" : "Profile Needed"}
                            </Badge>
                            {!staffData.user.approved && (
                              <Badge variant="destructive">Pending Approval</Badge>
                            )}
                            {staffData.user.approved && (
                              <Badge variant="success" className="bg-green-600">Approved</Badge>
                            )}
                            
                            {/* Overall Compliance Status */}
                            {staffData.compliance_alerts && staffData.compliance_alerts.is_compliant === false && (
                              <Badge variant="destructive" className="bg-red-700 animate-pulse">⛔ NOT COMPLIANT</Badge>
                            )}
                            {staffData.compliance_alerts && staffData.compliance_alerts.is_compliant === true && staffData.profile && (
                              <Badge className="bg-emerald-600">✓ Compliant</Badge>
                            )}
                            
                            {/* Missing Mandatory Trainings Alert */}
                            {staffData.compliance_alerts?.missing_mandatory_trainings > 0 && (
                              <Badge variant="destructive" className="bg-purple-700">
                                📋 {staffData.compliance_alerts.missing_mandatory_trainings}/{staffData.compliance_alerts.total_mandatory_trainings} Trainings Missing
                              </Badge>
                            )}
                            
                            {/* Individual Compliance Alerts */}
                            {staffData.compliance_alerts && (
                              <>
                                {staffData.compliance_alerts.training?.status === 'expired' && (
                                  <Badge variant="destructive">🔴 Training Expired</Badge>
                                )}
                                {staffData.compliance_alerts.training?.status === 'critical' && (
                                  <Badge variant="destructive">🔴 Training Expiring</Badge>
                                )}
                                {staffData.compliance_alerts.training?.status === 'warning' && (
                                  <Badge className="bg-orange-500">🟠 Training Due Soon</Badge>
                                )}
                                {staffData.compliance_alerts.dbs?.status === 'expired' && (
                                  <Badge variant="destructive">🔴 DBS Expired</Badge>
                                )}
                                {staffData.compliance_alerts.dbs?.status === 'critical' && (
                                  <Badge variant="destructive">🔴 DBS Expiring</Badge>
                                )}
                                {staffData.compliance_alerts.dbs?.status === 'warning' && (
                                  <Badge className="bg-orange-500">🟠 DBS Due Soon</Badge>
                                )}
                                {staffData.compliance_alerts.right_to_work?.status === 'expired' && (
                                  <Badge variant="destructive">🔴 Right to Work Expired</Badge>
                                )}
                                {staffData.compliance_alerts.right_to_work?.status === 'critical' && (
                                  <Badge variant="destructive">🔴 Right to Work Expiring</Badge>
                                )}
                                {staffData.compliance_alerts.right_to_work?.status === 'warning' && (
                                  <Badge className="bg-orange-500">🟠 Right to Work Due Soon</Badge>
                                )}
                                {staffData.compliance_alerts.passport?.status === 'expired' && (
                                  <Badge variant="destructive">🔴 Passport Expired</Badge>
                                )}
                                {staffData.compliance_alerts.passport?.status === 'critical' && (
                                  <Badge variant="destructive">🔴 Passport Expiring</Badge>
                                )}
                                {staffData.compliance_alerts.passport?.status === 'warning' && (
                                  <Badge className="bg-orange-500">🟠 Passport Due Soon</Badge>
                                )}
                              </>
                            )}
                          </div>
                          <p className="text-slate-600 text-sm">{staffData.user.email}</p>
                          <p className="text-xs text-slate-500">Joined: {formatDateDMY(staffData.user.created_at)}</p>
                          
                          {/* Compliance Details */}
                          {staffData.compliance_alerts && (
                            staffData.compliance_alerts.training?.expired?.length > 0 || 
                            staffData.compliance_alerts.training?.expiring_soon?.length > 0 ||
                            (staffData.compliance_alerts.dbs?.status && !['ok', 'none'].includes(staffData.compliance_alerts.dbs.status)) ||
                            (staffData.compliance_alerts.right_to_work?.status && !['ok', 'none'].includes(staffData.compliance_alerts.right_to_work.status)) ||
                            (staffData.compliance_alerts.passport?.status && !['ok', 'none'].includes(staffData.compliance_alerts.passport.status))
                          ) && (
                            <div className={`mt-3 p-3 rounded border ${staffData.compliance_alerts.is_compliant === false ? 'bg-red-50 border-red-300' : 'bg-yellow-50 border-yellow-200'}`}>
                              <h5 className="font-semibold text-sm mb-2">
                                {staffData.compliance_alerts.is_compliant === false ? '⛔ Compliance Issues - ACTION REQUIRED' : '⚠️ Compliance Alerts'}
                              </h5>
                              {staffData.compliance_alerts.training?.expired?.map((item, idx) => (
                                <div key={idx} className="text-xs text-red-600">
                                  🔴 {item.training_name} expired {item.days_overdue} days ago
                                </div>
                              ))}
                              {staffData.compliance_alerts.training?.expiring_soon?.map((item, idx) => (
                                <div key={idx} className={`text-xs ${item.urgency === 'critical' ? 'text-red-600' : item.urgency === 'warning' ? 'text-orange-600' : 'text-yellow-600'}`}>
                                  {item.urgency === 'critical' ? '🔴' : item.urgency === 'warning' ? '🟠' : '🟡'} {item.training_name} expires in {item.days_remaining} days
                                </div>
                              ))}
                              {staffData.compliance_alerts.dbs?.status === 'expired' && (
                                <div className="text-xs text-red-600">
                                  🔴 DBS Check expired (overdue by {Math.abs(staffData.compliance_alerts.dbs.days_remaining)} days)
                                </div>
                              )}
                              {staffData.compliance_alerts.dbs?.status !== 'expired' && staffData.compliance_alerts.dbs?.status !== 'ok' && staffData.compliance_alerts.dbs?.status !== 'none' && (
                                <div className={`text-xs ${staffData.compliance_alerts.dbs.status === 'critical' ? 'text-red-600' : 'text-orange-600'}`}>
                                  {staffData.compliance_alerts.dbs.status === 'critical' ? '🔴' : '🟠'} DBS Check expires in {staffData.compliance_alerts.dbs.days_remaining} days
                                </div>
                              )}
                              {staffData.compliance_alerts.right_to_work?.status === 'expired' && (
                                <div className="text-xs text-red-600">
                                  🔴 Right to Work expired (overdue by {Math.abs(staffData.compliance_alerts.right_to_work.days_remaining)} days)
                                </div>
                              )}
                              {staffData.compliance_alerts.right_to_work?.status !== 'expired' && staffData.compliance_alerts.right_to_work?.status !== 'ok' && staffData.compliance_alerts.right_to_work?.status !== 'none' && (
                                <div className={`text-xs ${staffData.compliance_alerts.right_to_work.status === 'critical' ? 'text-red-600' : 'text-orange-600'}`}>
                                  {staffData.compliance_alerts.right_to_work.status === 'critical' ? '🔴' : '🟠'} Right to Work expires in {staffData.compliance_alerts.right_to_work.days_remaining} days
                                </div>
                              )}
                              {staffData.compliance_alerts.passport?.status === 'expired' && (
                                <div className="text-xs text-red-600">
                                  🔴 Passport expired (overdue by {Math.abs(staffData.compliance_alerts.passport.days_remaining)} days)
                                </div>
                              )}
                              {staffData.compliance_alerts.passport?.status !== 'expired' && staffData.compliance_alerts.passport?.status !== 'ok' && staffData.compliance_alerts.passport?.status !== 'none' && (
                                <div className={`text-xs ${staffData.compliance_alerts.passport.status === 'critical' ? 'text-red-600' : 'text-orange-600'}`}>
                                  {staffData.compliance_alerts.passport.status === 'critical' ? '🔴' : '🟠'} Passport expires in {staffData.compliance_alerts.passport.days_remaining} days
                                </div>
                              )}
                            </div>
                          )}
                          
                          {staffData.profile ? (
                            <div className="mt-4 space-y-3">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                <div>
                                  <span className="text-slate-500">Address:</span>
                                  <p className="font-medium">{staffData.profile.address || 'Not provided'}</p>
                                </div>
                                <div>
                                  <span className="text-slate-500">Phone:</span>
                                  <p className="font-medium">{staffData.profile.phone_number || 'Not provided'}</p>
                                </div>
                                <div>
                                  <span className="text-slate-500">DOB:</span>
                                  <p className="font-medium">{staffData.profile.date_of_birth ? formatDateDMY(staffData.profile.date_of_birth) : 'Not provided'}</p>
                                </div>
                              </div>
                              
                              <div className="p-3 bg-slate-50 rounded">
                                <div className="flex justify-between items-center mb-2">
                                  <h5 className="font-medium">Documents</h5>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                  <div>Passport: {staffData.profile.documents?.passport_number || 'Not provided'}</div>
                                  <div>Right to Work: {staffData.profile.documents?.right_to_work ? '✅ Yes' : '❌ No'}</div>
                                  <div>DBS: {staffData.profile.documents?.dbs_number || 'Not provided'}</div>
                                  <div>NMC: {staffData.profile.documents?.nmc_registration || 'Not provided'}</div>
                                </div>
                                
                                {/* Signed Documents Section */}
                                <div className="mt-4 pt-3 border-t">
                                  <h6 className="font-medium text-sm mb-2">Signed Documents</h6>
                                  {signedDocsByUser[staffData.user.id] ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`signed-docs-${staffData.user.id}`}>
                                      {signedDocsByUser[staffData.user.id].documents.map((doc) => (
                                        <div
                                          key={doc.document_type}
                                          className={`flex items-center justify-between p-2 rounded border text-xs ${doc.signed ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}
                                        >
                                          <div>
                                            <p className="font-medium">{doc.name}</p>
                                            {doc.signed ? (
                                              <p className="text-green-700">✓ Signed {formatDateDMY(doc.signed_at?.split('T')[0])}</p>
                                            ) : (
                                              <p className="text-orange-700">⏳ Pending</p>
                                            )}
                                          </div>
                                          {doc.signed && doc.pdf_url && (
                                            <a
                                              href={`${BACKEND_URL}${doc.pdf_url}?token=${token}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-teal-700 underline font-medium ml-2"
                                            >
                                              PDF
                                            </a>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-xs text-slate-500">Loading signing status...</p>
                                  )}
                                </div>
                                
                                {/* Uploaded Documents Section */}
                                <div className="mt-4 pt-3 border-t">
                                  <div className="flex justify-between items-center mb-2">
                                    <h6 className="font-medium text-sm">Uploaded Files</h6>
                                    <button
                                      onClick={() => {
                                        setDocUploadStaffId(staffData.user.id);
                                        setDocUploadFile(null);
                                        setDocUploadType('');
                                        setDocUploadDate('');
                                        setDocUploadIsIssueDate(false);
                                        setShowDocUploadModal(true);
                                      }}
                                      className="text-xs bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
                                    >
                                      📤 Upload
                                    </button>
                                  </div>
                                  
                                  {staffData.profile.uploaded_documents && staffData.profile.uploaded_documents.length > 0 ? (
                                    <div className="space-y-2">
                                      {staffData.profile.uploaded_documents.map((doc, idx) => {
                                        // Calculate days until expiry for display
                                        let expiryStatus = null;
                                        if (doc.expiry_date) {
                                          const today = new Date();
                                          const expiry = new Date(doc.expiry_date);
                                          const daysRemaining = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
                                          if (daysRemaining < 0) {
                                            expiryStatus = { status: 'expired', days: Math.abs(daysRemaining) };
                                          } else if (daysRemaining <= 30) {
                                            expiryStatus = { status: 'critical', days: daysRemaining };
                                          } else if (daysRemaining <= 60) {
                                            expiryStatus = { status: 'warning', days: daysRemaining };
                                          } else if (daysRemaining <= 90) {
                                            expiryStatus = { status: 'notice', days: daysRemaining };
                                          } else {
                                            expiryStatus = { status: 'ok', days: daysRemaining };
                                          }
                                        }
                                        
                                        return (
                                        <div key={idx} className={`flex items-center justify-between p-2 border rounded text-xs ${
                                          expiryStatus?.status === 'expired' ? 'bg-red-50 border-red-300' :
                                          expiryStatus?.status === 'critical' ? 'bg-red-50 border-red-200' :
                                          expiryStatus?.status === 'warning' ? 'bg-orange-50 border-orange-200' :
                                          expiryStatus?.status === 'notice' ? 'bg-yellow-50 border-yellow-200' :
                                          'bg-white'
                                        }`}>
                                          <div className="flex items-center space-x-2">
                                            <span className="text-lg">
                                              {doc.filename?.endsWith('.pdf') ? '📄' : '🖼️'}
                                            </span>
                                            <div>
                                              <p className="font-medium capitalize">{doc.document_type?.replace('_', ' ')}</p>
                                              <p className="text-slate-500">{doc.original_filename}</p>
                                              {doc.expiry_date && (
                                                <p className={`text-xs ${
                                                  expiryStatus?.status === 'expired' ? 'text-red-600 font-semibold' :
                                                  expiryStatus?.status === 'critical' ? 'text-red-600' :
                                                  expiryStatus?.status === 'warning' ? 'text-orange-600' :
                                                  expiryStatus?.status === 'notice' ? 'text-yellow-700' :
                                                  'text-green-600'
                                                }`}>
                                                  {expiryStatus?.status === 'expired' 
                                                    ? `⛔ EXPIRED ${expiryStatus.days} days ago`
                                                    : expiryStatus?.status === 'critical'
                                                    ? `🔴 Expires in ${expiryStatus.days} days`
                                                    : expiryStatus?.status === 'warning'
                                                    ? `🟠 Expires in ${expiryStatus.days} days`
                                                    : expiryStatus?.status === 'notice'
                                                    ? `🟡 Expires in ${expiryStatus.days} days`
                                                    : `✓ Expires: ${formatDateDMY(doc.expiry_date)}`
                                                  }
                                                </p>
                                              )}
                                              {doc.issue_date && !doc.expiry_date && (
                                                <p className="text-xs text-slate-500">Issued: {formatDateDMY(doc.issue_date)}</p>
                                              )}
                                              {doc.pending_admin_review ? (
                                                <span className="inline-block mt-1 px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 font-semibold" data-testid={`doc-pending-badge-${doc.document_type}`}>⏳ Pending Verification</span>
                                              ) : doc.verified_by_admin ? (
                                                <span className="inline-block mt-1 px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-semibold" data-testid={`doc-verified-badge-${doc.document_type}`}>✓ Verified</span>
                                              ) : null}
                                            </div>
                                          </div>
                                          <div className="flex items-center space-x-2">
                                            {doc.pending_admin_review && (
                                              <button
                                                data-testid={`confirm-doc-btn-${doc.document_type}`}
                                                onClick={async () => {
                                                  try {
                                                    await apiCall(
                                                      `/staff-profiles/${staffData.user.id}/documents/${doc.document_type}/verify`,
                                                      'PUT',
                                                      {},
                                                      token
                                                    );
                                                    toast.success('Document verified');
                                                    const updatedData = await apiCall('/staff-users', 'GET', null, token);
                                                    setStaffUsers(updatedData || []);
                                                  } catch (error) {
                                                    toast.error('Failed to verify document');
                                                  }
                                                }}
                                                className="px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 font-semibold"
                                              >
                                                ✓ Confirm
                                              </button>
                                            )}
                                            <a
                                              href={`${BACKEND_URL}${doc.file_path?.startsWith('/api/') ? doc.file_path : '/api' + doc.file_path}?token=${token}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-blue-600 hover:underline"
                                            >
                                              View
                                            </a>
                                            <button
                                              onClick={async () => {
                                                if (!window.confirm(`Delete ${doc.document_type} document?`)) return;
                                                try {
                                                  await apiCall(
                                                    `/staff-profiles/${staffData.user.id}/documents/${doc.document_type}`,
                                                    'DELETE',
                                                    null,
                                                    token
                                                  );
                                                  toast.success('Document deleted');
                                                  const updatedData = await apiCall('/staff-users', 'GET', null, token);
                                                  setStaffUsers(updatedData || []);
                                                } catch (error) {
                                                  toast.error('Failed to delete document');
                                                }
                                              }}
                                              className="text-red-600 hover:underline"
                                            >
                                              Delete
                                            </button>
                                          </div>
                                        </div>
                                        );
                                      })}
                                    </div>
                                  ) : (
                                    <p className="text-xs text-slate-500 italic">No documents uploaded yet</p>
                                  )}
                                </div>
                              </div>

                              <div className="p-3 bg-blue-50 rounded">
                                <h5 className="font-medium mb-2">Employment</h5>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                                  <div>Interview: {staffData.profile.employment?.interview_date ? formatDateDMY(staffData.profile.employment.interview_date) : 'Not scheduled'}</div>
                                  <div>Start Date: {staffData.profile.employment?.start_date ? formatDateDMY(staffData.profile.employment.start_date) : 'Not set'}</div>
                                  <div>References: {staffData.profile.employment?.references_collected ? '✅ Collected' : '⏳ Pending'}</div>
                                </div>
                              </div>

                              {staffData.profile.training_records && staffData.profile.training_records.length > 0 && (
                                <div className="p-3 bg-green-50 rounded">
                                  <h5 className="font-medium mb-2">Training Records ({staffData.profile.training_records.length} completed)</h5>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                                    {staffData.profile.training_records.slice(0, 6).map((training, index) => {
                                      const daysLeft = training.expiry_date ? Math.ceil((new Date(training.expiry_date) - new Date()) / 86400000) : null;
                                      return (
                                      <div key={index} className="text-sm">
                                        <span className={`${
                                          !training.certificate_path ? 'text-orange-600' :
                                          (training.pending_admin_review || training.pending_admin_verification) ? 'text-orange-600' :
                                          daysLeft !== null && daysLeft < 0 ? 'text-red-600 font-semibold' :
                                          daysLeft !== null && daysLeft <= 30 ? 'text-amber-600 font-medium' :
                                          daysLeft !== null && daysLeft <= 60 ? 'text-yellow-600' :
                                          'text-green-700'
                                        }`}>
                                          {!training.certificate_path ? '⚠️' : 
                                           (training.pending_admin_review || training.pending_admin_verification) ? '⏳' :
                                           daysLeft !== null && daysLeft < 0 ? '❌' :
                                           daysLeft !== null && daysLeft <= 60 ? '⏰' : '✓'} {training.training_name}
                                          {daysLeft !== null && daysLeft >= 0 && daysLeft <= 60 && ` (${daysLeft}d left)`}
                                        </span>
                                      </div>
                                      );
                                    })}
                                    {staffData.profile.training_records.length > 6 && (
                                      <div className="text-sm text-slate-500">+{staffData.profile.training_records.length - 6} more...</div>
                                    )}
                                  </div>
                                </div>
                              )}
                              
                              {/* Show missing mandatory trainings */}
                              {mandatoryTrainings.length > 0 && (
                                <div className="p-3 bg-amber-50 rounded border border-amber-200">
                                  <h5 className="font-medium mb-2 text-amber-800">
                                    Training Compliance: {staffData.profile?.training_records?.filter(t => t.certificate_path && !t.pending_admin_review && !t.pending_admin_verification).length || 0} / {mandatoryTrainings.length}
                                  </h5>
                                  <div className="text-xs text-amber-700">
                                    Missing: {mandatoryTrainings.filter(mt => 
                                      !staffData.profile?.training_records?.some(tr => 
                                        tr.training_name === mt.name && tr.certificate_path
                                      )
                                    ).slice(0, 3).map(mt => mt.name).join(', ')}
                                    {mandatoryTrainings.filter(mt => 
                                      !staffData.profile?.training_records?.some(tr => 
                                        tr.training_name === mt.name && tr.certificate_path
                                      )
                                    ).length > 3 && '...'}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
                              <p className="text-yellow-800 text-sm">Profile information needed - click &quot;Create Profile&quot; to add details.</p>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col space-y-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setEditingStaff(staffData)}
                          >
                            {staffData.profile ? 'Edit Profile' : 'Create Profile'}
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="border-purple-300 text-purple-700 hover:bg-purple-50"
                            onClick={() => {
                              setEditingStaff(staffData);
                              // Scroll to training section after opening
                              setTimeout(() => {
                                document.getElementById('training-records-section')?.scrollIntoView({ behavior: 'smooth' });
                              }, 300);
                            }}
                          >
                            📋 Manage Training
                          </Button>
                          <Button 
                            variant="secondary" 
                            size="sm"
                            onClick={async () => {
                              const newPassword = window.prompt(`Enter new password for ${staffData.user.name}:`);
                              if (newPassword && newPassword.length >= 6) {
                                try {
                                  await apiCall(`/admin/reset-password/${staffData.user.id}?new_password=${encodeURIComponent(newPassword)}`, 'PUT', null, token);
                                  toast.success(`Password reset successfully for ${staffData.user.name}`);
                                } catch (error) {
                                  toast.error('Failed to reset password');
                                }
                              } else if (newPassword) {
                                toast.error('Password must be at least 6 characters');
                              }
                            }}
                          >
                            Reset Password
                          </Button>
                          {!staffData.user.approved && (
                            <Button 
                              variant="default"
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={async () => {
                                console.log('Approve button clicked for:', staffData.user.name, staffData.user.id);
                                if (window.confirm(`Approve ${staffData.user.name}? They will be able to login after approval.`)) {
                                  try {
                                    console.log('Calling approve API...');
                                    await apiCall(`/admin/approve-staff/${staffData.user.id}`, 'PUT', null, token);
                                    console.log('Approval successful');
                                    toast.success(`${staffData.user.name} approved successfully`);
                                    // Update staff list immediately
                                    const updatedStaff = staffUsers.map(s => 
                                      s.user.id === staffData.user.id 
                                        ? { ...s, user: { ...s.user, approved: true } }
                                        : s
                                    );
                                    console.log('Updated staff list');
                                    setStaffUsers(updatedStaff);
                                  } catch (error) {
                                    console.error('Approve error:', error);
                                    toast.error(`Failed to approve: ${error.detail || error.message || 'Unknown error'}`);
                                  }
                                }
                              }}
                            >
                              Approve Staff
                            </Button>
                          )}
                          <WorkerProfileDialog
                            staffData={staffData}
                            token={token}
                            apiCall={apiCall}
                          />
                          <DeleteStaffDialog
                            staffData={staffData}
                            token={token}
                            apiCall={apiCall}
                            onDone={refetchData}
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {staffUsers.length === 0 && (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <p className="text-slate-500">No staff registered yet.</p>
                      <p className="text-xs text-slate-400 mt-1">Staff will appear here when they create accounts.</p>
                    </CardContent>
                  </Card>
                )}
              </div>

              {editingStaff && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                  <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                    <Card>
                      <CardHeader>
                        <div className="flex justify-between items-center">
                          <CardTitle>Staff Profile - {editingStaff.user.name}</CardTitle>
                          <Button variant="ghost" onClick={() => setEditingStaff(null)}>×</Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <form onSubmit={(e) => {
                          e.preventDefault();
                          const formData = new FormData(e.target);
                          
                          // Build individual rates object (only include if values provided)
                          const individualRates = {};
                          const weekdayRate = formData.get('weekday_rate');
                          const weekendRate = formData.get('weekend_rate');
                          const bankHolidayRate = formData.get('bank_holiday_rate');
                          if (weekdayRate) individualRates.weekday_rate = parseFloat(weekdayRate);
                          if (weekendRate) individualRates.weekend_rate = parseFloat(weekendRate);
                          if (bankHolidayRate) individualRates.bank_holiday_rate = parseFloat(bankHolidayRate);
                          
                          // Get employment type and limited company details
                          const employmentType = formData.get('employment_type') || 'paye';
                          const limitedCompanyName = formData.get('limited_company_name');
                          const limitedCompanyNumber = formData.get('limited_company_number');
                          const promisedRate = formData.get('promised_rate');
                          
                          // Get bank details for Ltd Company contractors
                          const bankDetails = employmentType === 'limited_company' ? {
                            bank_name: formData.get('bank_name') || '',
                            account_number: formData.get('account_number') || '',
                            sort_code: formData.get('sort_code') || '',
                            account_holder_name: formData.get('account_holder_name') || ''
                          } : null;
                          
                          const profileData = {
                            user_id: editingStaff.user.id,
                            staff_type: formData.get('staff_type') || null,
                            address: formData.get('address'),
                            phone_number: formData.get('phone'),
                            date_of_birth: formData.get('dob'),
                            pay_frequency: formData.get('pay_frequency') || 'weekly',
                            employment_type: employmentType,
                            limited_company_name: employmentType === 'limited_company' ? limitedCompanyName : null,
                            limited_company_number: employmentType === 'limited_company' ? limitedCompanyNumber : null,
                            promised_rate: employmentType === 'limited_company' && promisedRate ? parseFloat(promisedRate) : null,
                            bank_details: bankDetails,
                            individual_rates: Object.keys(individualRates).length > 0 ? individualRates : null,
                            documents: {
                              passport_number: formData.get('passport'),
                              passport_expiry: formData.get('passport_expiry'),
                              right_to_work: formData.get('right_to_work') === 'on',
                              dbs_number: formData.get('dbs'),
                              dbs_date: formData.get('dbs_date'),
                              dbs_expiry: formData.get('dbs_expiry'),
                              nmc_registration: formData.get('nmc'),
                              nmc_expiry: formData.get('nmc_expiry')
                            },
                            employment: {
                              interview_date: formData.get('interview_date'),
                              start_date: formData.get('start_date'),
                              references_collected: formData.get('references') === 'on'
                            }
                          };
                          handleUpdateStaffProfile(editingStaff.user.id, profileData);
                        }} className="space-y-6">
                          
                          <div>
                            <h4 className="text-lg font-semibold mb-4">Personal Information</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor="staff_type">Job Role</Label>
                                <select
                                  name="staff_type"
                                  defaultValue={editingStaff.profile?.staff_type || ''}
                                  className="w-full h-10 px-3 border border-slate-200 rounded-md text-sm bg-white"
                                  data-testid="profile-job-role-select"
                                  required
                                >
                                  <option value="" disabled>Select job role...</option>
                                  <option value="nurse">Nurse</option>
                                  <option value="carer">Support Worker / Care Assistant</option>
                                </select>
                              </div>
                              <div>
                                <Label htmlFor="address">Address</Label>
                                <Textarea
                                  name="address"
                                  defaultValue={editingStaff.profile?.address || ''}
                                  placeholder="Full address including postcode"
                                  required
                                />
                              </div>
                              <div>
                                <Label htmlFor="phone">Phone Number</Label>
                                <Input
                                  name="phone"
                                  defaultValue={editingStaff.profile?.phone_number || ''}
                                  placeholder="07XXX XXXXXX"
                                  required
                                />
                              </div>
                              <div>
                                <Label htmlFor="dob">Date of Birth</Label>
                                <Input
                                  name="dob"
                                  type="date"
                                  defaultValue={editingStaff.profile?.date_of_birth || ''}
                                  required
                                />
                              </div>
                              <div>
                                <Label htmlFor="pay_frequency">Pay Frequency</Label>
                                <Select name="pay_frequency" defaultValue={editingStaff.profile?.pay_frequency || 'weekly'}>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="weekly">Weekly</SelectItem>
                                    <SelectItem value="monthly">Monthly</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label htmlFor="employment_type">Employment Type</Label>
                                <Select name="employment_type" defaultValue={editingStaff.profile?.employment_type || 'paye'}>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="paye">PAYE (Employee)</SelectItem>
                                    <SelectItem value="limited_company">Limited Company Contractor</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            
                            {/* Limited Company Fields - shown conditionally */}
                            <div className="mt-4 p-4 border rounded-lg bg-blue-50 border-blue-200" id="limited-company-fields">
                              <h5 className="font-medium text-blue-800 mb-3">Limited Company Details (for contractors)</h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <Label htmlFor="limited_company_name">Company Name</Label>
                                  <Input
                                    name="limited_company_name"
                                    defaultValue={editingStaff.profile?.limited_company_name || ''}
                                    placeholder="e.g. Smith Healthcare Ltd"
                                  />
                                </div>
                                <div>
                                  <Label htmlFor="limited_company_number">Company Number</Label>
                                  <Input
                                    name="limited_company_number"
                                    defaultValue={editingStaff.profile?.limited_company_number || ''}
                                    placeholder="e.g. 12345678"
                                  />
                                </div>
                                <div>
                                  <Label htmlFor="promised_rate">Promised Rate (£/hr)</Label>
                                  <Input
                                    name="promised_rate"
                                    type="number"
                                    step="0.01"
                                    defaultValue={editingStaff.profile?.promised_rate || ''}
                                    placeholder="e.g. 25.00"
                                  />
                                  <p className="text-xs text-blue-600 mt-1">Rate paid to contractor&apos;s company</p>
                                </div>
                              </div>
                              
                              {/* Bank Details for Payment */}
                              <div className="mt-4 pt-4 border-t border-blue-200">
                                <h6 className="font-medium text-blue-700 mb-3">Bank Details for Payment</h6>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  <div>
                                    <Label htmlFor="bank_name">Bank Name</Label>
                                    <Input
                                      name="bank_name"
                                      defaultValue={editingStaff.profile?.bank_details?.bank_name || ''}
                                      placeholder="e.g. Natwest, Barclays"
                                    />
                                  </div>
                                  <div>
                                    <Label htmlFor="account_number">Account Number</Label>
                                    <Input
                                      name="account_number"
                                      defaultValue={editingStaff.profile?.bank_details?.account_number || ''}
                                      placeholder="e.g. 12345678"
                                      maxLength={8}
                                    />
                                  </div>
                                  <div>
                                    <Label htmlFor="sort_code">Sort Code</Label>
                                    <Input
                                      name="sort_code"
                                      defaultValue={editingStaff.profile?.bank_details?.sort_code || ''}
                                      placeholder="e.g. 60-10-14"
                                    />
                                  </div>
                                  <div className="md:col-span-2">
                                    <Label htmlFor="account_holder_name">Account Holder Name</Label>
                                    <Input
                                      name="account_holder_name"
                                      defaultValue={editingStaff.profile?.bank_details?.account_holder_name || ''}
                                      placeholder="e.g. Smith Healthcare Ltd"
                                    />
                                    <p className="text-xs text-blue-600 mt-1">Name exactly as it appears on the bank account</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div>
                            <h4 className="text-lg font-semibold mb-4">Individual Pay Rates (Override Client Rates)</h4>
                            <p className="text-sm text-slate-500 mb-4">Leave blank to use client default rates</p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <Label htmlFor="weekday_rate">Weekday Rate (£/hr)</Label>
                                <Input
                                  name="weekday_rate"
                                  type="number"
                                  step="0.01"
                                  defaultValue={editingStaff.profile?.individual_rates?.weekday_rate || ''}
                                  placeholder="e.g. 18.50"
                                />
                              </div>
                              <div>
                                <Label htmlFor="weekend_rate">Weekend Rate (£/hr)</Label>
                                <Input
                                  name="weekend_rate"
                                  type="number"
                                  step="0.01"
                                  defaultValue={editingStaff.profile?.individual_rates?.weekend_rate || ''}
                                  placeholder="e.g. 22.00"
                                />
                              </div>
                              <div>
                                <Label htmlFor="bank_holiday_rate">Bank Holiday Rate (£/hr)</Label>
                                <Input
                                  name="bank_holiday_rate"
                                  type="number"
                                  step="0.01"
                                  defaultValue={editingStaff.profile?.individual_rates?.bank_holiday_rate || ''}
                                  placeholder="e.g. 32.00"
                                />
                              </div>
                            </div>
                          </div>

                          <div>
                            <h4 className="text-lg font-semibold mb-4">Documents</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor="passport">Passport Number</Label>
                                <Input
                                  name="passport"
                                  defaultValue={editingStaff.profile?.documents?.passport_number || ''}
                                  placeholder="GB123456789"
                                />
                              </div>
                              <div>
                                <Label htmlFor="passport_expiry">Passport Expiry</Label>
                                <Input
                                  name="passport_expiry"
                                  type="date"
                                  defaultValue={editingStaff.profile?.documents?.passport_expiry || ''}
                                />
                              </div>
                              <div>
                                <Label htmlFor="dbs">DBS Number</Label>
                                <Input
                                  name="dbs"
                                  defaultValue={editingStaff.profile?.documents?.dbs_number || ''}
                                  placeholder="DBS Certificate Number"
                                />
                              </div>
                              <div>
                                <Label htmlFor="dbs_date">DBS Issue Date</Label>
                                <Input
                                  name="dbs_date"
                                  type="date"
                                  defaultValue={editingStaff.profile?.documents?.dbs_date || ''}
                                />
                              </div>
                              <div>
                                <Label htmlFor="dbs_expiry">DBS Expiry Date</Label>
                                <Input
                                  name="dbs_expiry"
                                  type="date"
                                  defaultValue={editingStaff.profile?.documents?.dbs_expiry || ''}
                                />
                              </div>
                              <div>
                                <Label htmlFor="nmc">NMC Registration (Nurses)</Label>
                                <Input
                                  name="nmc"
                                  defaultValue={editingStaff.profile?.documents?.nmc_registration || ''}
                                  placeholder="NMC Registration Number"
                                />
                              </div>
                              <div>
                                <Label htmlFor="nmc_expiry">NMC Expiry Date</Label>
                                <Input
                                  name="nmc_expiry"
                                  type="date"
                                  defaultValue={editingStaff.profile?.documents?.nmc_expiry || ''}
                                />
                              </div>
                            </div>
                          </div>

                          <div>
                            <h4 className="text-lg font-semibold mb-4">Employment Details</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <Label htmlFor="interview_date">Interview Date</Label>
                                <Input
                                  name="interview_date"
                                  type="date"
                                  defaultValue={editingStaff.profile?.employment?.interview_date || ''}
                                />
                              </div>
                              <div>
                                <Label htmlFor="start_date">Start Date</Label>
                                <Input
                                  name="start_date"
                                  type="date"
                                  defaultValue={editingStaff.profile?.employment?.start_date || ''}
                                />
                              </div>
                            </div>
                          </div>

                          <div>
                            <h4 className="text-lg font-semibold mb-4">Verification</h4>
                            <div className="flex items-center space-x-6">
                              <label className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  name="right_to_work"
                                  defaultChecked={editingStaff.profile?.documents?.right_to_work || false}
                                />
                                <span className="text-sm">Right to Work Verified</span>
                              </label>
                              <label className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  name="references"
                                  defaultChecked={editingStaff.profile?.employment?.references_collected || false}
                                />
                                <span className="text-sm">References Collected</span>
                              </label>
                            </div>
                          </div>
                          
                          <div id="training-records-section">
                            <h4 className="text-lg font-semibold mb-4">Training Records</h4>
                            
                            {/* Training Compliance Summary */}
                            <div className="mb-4 p-3 rounded-lg bg-slate-100">
                              <div className="flex items-center justify-between">
                                <span className="font-medium">Training Compliance:</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm">
                                    {editingStaff.profile?.training_records?.filter(t => t.certificate_path).length || 0} / {mandatoryTrainings.filter(t => t.is_active).length} completed with certificates
                                  </span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Show ALL Mandatory Trainings - easy management view */}
                            <div className="mt-6 border-t pt-4">
                              <h5 className="font-semibold mb-3 text-purple-800">All Mandatory Trainings - Quick Add</h5>
                              <p className="text-sm text-slate-500 mb-4">Click to add training records with completion date and certificate</p>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {mandatoryTrainings.map((mt) => {
                                  const existingRecord = editingStaff.profile?.training_records?.find(tr => tr.training_name === mt.name);
                                  const isComplete = existingRecord && existingRecord.certificate_path;
                                  const isExpired = existingRecord?.expiry_date && new Date(existingRecord.expiry_date) < new Date();
                                  
                                  return (
                                    <div 
                                      key={mt.id} 
                                      className={`p-3 rounded border cursor-pointer transition-all ${
                                        isComplete && !isExpired ? 'bg-green-50 border-green-300' :
                                        isExpired ? 'bg-red-50 border-red-300' :
                                        existingRecord ? 'bg-yellow-50 border-yellow-300' :
                                        'bg-slate-50 border-slate-200 hover:border-purple-400'
                                      }`}
                                      onClick={() => {
                                        if (!existingRecord) {
                                          // Add new training record
                                          const completedDate = window.prompt(`Enter completion date for ${mt.name} (DD/MM/YYYY):`);
                                          if (completedDate) {
                                            const isoDate = parseDMYtoISO(completedDate) || completedDate;
                                            const expiryYears = mt.default_validity_years || 1;
                                            const expiry = new Date(isoDate);
                                            expiry.setFullYear(expiry.getFullYear() + expiryYears);
                                            
                                            const newTraining = {
                                              id: Date.now().toString(),
                                              training_name: mt.name,
                                              training_type: mt.type?.toLowerCase() || 'online',
                                              training_date: isoDate,
                                              expiry_date: expiry.toISOString().split('T')[0],
                                              provider: '',
                                              certificate_path: null
                                            };
                                            
                                            const updatedTraining = [...(editingStaff.profile?.training_records || []), newTraining];
                                            apiCall(`/staff-profiles/${editingStaff.user.id}`, 'PUT', {
                                              user_id: editingStaff.user.id,
                                              training_records: updatedTraining
                                            }, token).then(() => {
                                              toast.success(`${mt.name} added - now upload certificate`);
                                              refetchData();
                                              setEditingStaff({
                                                ...editingStaff,
                                                profile: { ...editingStaff.profile, training_records: updatedTraining }
                                              });
                                            }).catch(() => toast.error('Failed to add training'));
                                          }
                                        }
                                      }}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                          <span className={isComplete && !isExpired ? 'text-green-700' : isExpired ? 'text-red-600' : existingRecord ? 'text-yellow-700' : 'text-slate-600'}>
                                            {isComplete && !isExpired ? '✓' : isExpired ? '⚠️' : existingRecord ? '📋' : '○'} {mt.name}
                                          </span>
                                          <Badge variant="outline" className="text-xs">
                                            {mt.type || 'Online'}
                                          </Badge>
                                        </div>
                                        {existingRecord && (
                                          <span className="text-xs text-slate-500">
                                            {existingRecord.certificate_path ? 'Has cert' : 'No cert'}
                                          </span>
                                        )}
                                      </div>
                                      {existingRecord && (
                                        <div className="text-xs mt-1 text-slate-500">
                                          Done: {formatDateDMY(existingRecord.training_date)} | 
                                          Exp: {formatDateDMY(existingRecord.expiry_date)}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                            
                            <div className="space-y-3 mt-6">
                              <h5 className="font-semibold text-slate-700">Existing Training Records</h5>
                              {editingStaff.profile?.training_records?.map((training, index) => (
                                <div key={index} className={`border rounded-lg p-4 ${
                                  !training.certificate_path ? 'bg-yellow-50 border-yellow-300' :
                                  training.expiry_date && new Date(training.expiry_date) < new Date() ? 'bg-red-50 border-red-300' :
                                  'bg-green-50 border-green-300'
                                }`}>
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <h5 className="font-medium">{training.training_name}</h5>
                                        <Badge variant="outline" className={
                                          training.training_type === 'practical' ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'
                                        }>
                                          {training.training_type === 'practical' ? '🏋️ Practical' : '💻 Online'}
                                        </Badge>
                                      </div>
                                      <div className="text-sm text-slate-600 mt-2 grid grid-cols-2 gap-2">
                                        <p><span className="font-medium">Completed:</span> {formatDateDMY(training.training_date)}</p>
                                        {training.expiry_date && (
                                          <p className={`font-medium ${
                                            new Date(training.expiry_date) < new Date() 
                                              ? 'text-red-600' 
                                              : new Date(training.expiry_date) < new Date(Date.now() + 30*24*60*60*1000)
                                              ? 'text-orange-600'
                                              : 'text-green-600'
                                          }`}>
                                            <span className="font-medium">Expiry:</span> {formatDateDMY(training.expiry_date)}
                                            {new Date(training.expiry_date) < new Date() && ' ⚠️ EXPIRED'}
                                          </p>
                                        )}
                                        {training.provider && <p><span className="font-medium">Provider:</span> {training.provider}</p>}
                                        <p>
                                          <span className="font-medium">Certificate:</span>{' '}
                                          {training.certificate_path ? (
                                            <a 
                                              href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${training.certificate_path.replace(/^uploads\//, '')}?token=${token}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-blue-600 hover:underline"
                                            >
                                              View ✓
                                            </a>
                                          ) : (
                                            <span className="text-red-600">Not uploaded ⚠️</span>
                                          )}
                                        </p>
                                      </div>
                                      
                                      {/* Certificate Upload if missing */}
                                      {!training.certificate_path && (
                                        <div className="mt-3">
                                          <input
                                            type="file"
                                            id={`cert-upload-${index}`}
                                            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                                            className="hidden"
                                            onChange={async (e) => {
                                              const file = e.target.files[0];
                                              if (!file) return;
                                              
                                              const formData = new FormData();
                                              formData.append('file', file);
                                              
                                              try {
                                                const response = await fetch(
                                                  `${process.env.REACT_APP_BACKEND_URL}/api/staff-profiles/${editingStaff.user.id}/trainings/${training.id}/certificate`,
                                                  {
                                                    method: 'POST',
                                                    headers: { 'Authorization': `Bearer ${token}` },
                                                    body: formData
                                                  }
                                                );
                                                if (response.ok) {
                                                  toast.success('Certificate uploaded');
                                                  refreshEditingStaff(editingStaff.user.id);
                                                } else {
                                                  toast.error('Failed to upload certificate');
                                                }
                                              } catch (error) {
                                                toast.error('Upload failed');
                                              }
                                            }}
                                          />
                                          <Button
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            onClick={() => document.getElementById(`cert-upload-${index}`).click()}
                                          >
                                            📎 Upload Certificate
                                          </Button>
                                        </div>
                                      )}
                                      
                                      {/* Admin Edit Training Dates */}
                                      <div className="mt-3 pt-3 border-t flex gap-2">
                                        <Button
                                          type="button"
                                          size="sm"
                                          variant="outline"
                                          onClick={async () => {
                                            const newExpiryDate = window.prompt(
                                              `Edit expiry date for ${training.training_name} (DD/MM/YYYY):`,
                                              training.expiry_date ? formatDateDMY(training.expiry_date) : new Date().toLocaleDateString('en-GB')
                                            );
                                            if (!newExpiryDate) return;
                                            
                                            try {
                                              await apiCall(`/staff-profiles/${editingStaff.user.id}/trainings/${training.id}`, 'PUT', {
                                                expiry_date: parseDMYtoISO(newExpiryDate)
                                              }, token);
                                              toast.success('Expiry date updated');
                                              refreshEditingStaff(editingStaff.user.id);
                                            } catch (error) {
                                              toast.error('Failed to update expiry date');
                                            }
                                          }}
                                        >
                                          ✏️ Edit Expiry
                                        </Button>
                                        <Button
                                          type="button"
                                          size="sm"
                                          variant="outline"
                                          onClick={async () => {
                                            const newCompletionDate = window.prompt(
                                              `Edit completion date for ${training.training_name} (DD/MM/YYYY):`,
                                              training.training_date ? formatDateDMY(training.training_date) : new Date().toLocaleDateString('en-GB')
                                            );
                                            if (!newCompletionDate) return;
                                            
                                            try {
                                              await apiCall(`/staff-profiles/${editingStaff.user.id}/trainings/${training.id}`, 'PUT', {
                                                training_date: parseDMYtoISO(newCompletionDate)
                                              }, token);
                                              toast.success('Completion date updated');
                                              refreshEditingStaff(editingStaff.user.id);
                                            } catch (error) {
                                              toast.error('Failed to update completion date');
                                            }
                                          }}
                                        >
                                          ✏️ Edit Completion
                                        </Button>
                                        {(training.pending_admin_review || training.pending_admin_verification) && (
                                          <>
                                            <Badge className="bg-orange-500 ml-2">Pending Review</Badge>
                                            <Button
                                              type="button"
                                              size="sm"
                                              data-testid={`confirm-training-btn-${index}`}
                                              className="bg-green-600 hover:bg-green-700 text-white"
                                              onClick={async () => {
                                                try {
                                                  await apiCall(`/staff-profiles/${editingStaff.user.id}/trainings/${training.id}`, 'PUT', {
                                                    verified: true,
                                                    pending_admin_review: false
                                                  }, token);
                                                  toast.success(`${training.training_name} verified`);
                                                  refreshEditingStaff(editingStaff.user.id);
                                                } catch (error) {
                                                  toast.error('Failed to verify training');
                                                }
                                              }}
                                            >
                                              ✓ Confirm
                                            </Button>
                                          </>
                                        )}
                                        {!training.pending_admin_review && !training.pending_admin_verification && training.verified_by_admin && (
                                          <Badge className="bg-green-600 ml-2">✓ Verified</Badge>
                                        )}
                                      </div>
                                    </div>
                                    <Button 
                                      type="button"
                                      variant="destructive" 
                                      size="sm"
                                      onClick={async () => {
                                        const updatedTraining = editingStaff.profile.training_records.filter((_, i) => i !== index);
                                        try {
                                          await apiCall(`/staff-profiles/${editingStaff.user.id}`, 'PUT', {
                                            user_id: editingStaff.user.id,
                                            address: editingStaff.profile?.address || null,
                                            phone_number: editingStaff.profile?.phone_number || null,
                                            date_of_birth: editingStaff.profile?.date_of_birth || null,
                                            documents: editingStaff.profile?.documents || null,
                                            employment: editingStaff.profile?.employment || null,
                                            training_records: updatedTraining
                                          }, token);
                                          toast.success('Training record deleted');
                                          refreshEditingStaff(editingStaff.user.id);
                                        } catch (error) {
                                          toast.error('Failed to delete training record');
                                        }
                                      }}
                                    >
                                      Delete
                                    </Button>
                                  </div>
                                </div>
                              ))}
                              
                              <div className="border-2 border-dashed border-blue-300 rounded-lg p-4 bg-blue-50">
                                <h5 className="font-medium mb-3">Add New Training</h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                  <div>
                                    <Label>Training Name *</Label>
                                    <Select
                                      onValueChange={(value) => {
                                        const selectedTraining = mandatoryTrainings.find(t => t.name === value);
                                        if (selectedTraining) {
                                          document.getElementById('new_training_type').value = selectedTraining.training_type;
                                        }
                                      }}
                                    >
                                      <SelectTrigger id="new_training_select">
                                        <SelectValue placeholder="Select training" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {mandatoryTrainings.map((t) => (
                                          <SelectItem key={t.id} value={t.name}>
                                            {t.name} {t.training_type === 'practical' ? '(Practical)' : ''}
                                          </SelectItem>
                                        ))}
                                        <SelectItem value="__custom__">+ Other (Custom)</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div>
                                    <Label>Custom Name (if Other)</Label>
                                    <Input
                                      id="new_training_name"
                                      placeholder="Enter custom training name"
                                    />
                                  </div>
                                  <div>
                                    <Label>Type *</Label>
                                    <select
                                      id="new_training_type"
                                      className="w-full h-10 rounded-md border border-input bg-background px-3 py-2"
                                      defaultValue="online"
                                    >
                                      <option value="online">Online</option>
                                      <option value="practical">Practical</option>
                                    </select>
                                  </div>
                                  <div>
                                    <Label>Completion Date *</Label>
                                    <Input
                                      id="new_training_date"
                                      type="date"
                                    />
                                  </div>
                                  <div>
                                    <Label>Expiry Date</Label>
                                    <Input
                                      id="new_training_expiry"
                                      type="date"
                                    />
                                  </div>
                                  <div>
                                    <Label>Provider</Label>
                                    <Input
                                      id="new_training_provider"
                                      placeholder="e.g., Blue Stream Academy"
                                    />
                                  </div>
                                </div>
                                <div className="mt-3">
                                  <Label>Certificate (PDF or Image) *</Label>
                                  <Input
                                    id="new_training_certificate"
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                                    className="mt-1"
                                  />
                                </div>
                                <Button 
                                  type="button"
                                  variant="secondary"
                                  size="sm"
                                  className="mt-3"
                                  onClick={async () => {
                                    const selectEl = document.getElementById('new_training_select');
                                    const selectedValue = selectEl?.querySelector('[data-state="checked"]')?.textContent || '';
                                    const customName = document.getElementById('new_training_name').value;
                                    const trainingType = document.getElementById('new_training_type').value;
                                    const date = document.getElementById('new_training_date').value;
                                    const expiry = document.getElementById('new_training_expiry').value;
                                    const provider = document.getElementById('new_training_provider').value;
                                    const certFile = document.getElementById('new_training_certificate').files[0];
                                    
                                    // Get selected training name from the select dropdown
                                    let trainingName = customName;
                                    const selectTrigger = document.querySelector('#new_training_select + button span');
                                    if (selectTrigger && selectTrigger.textContent !== 'Select training') {
                                      const selectedText = selectTrigger.textContent;
                                      if (selectedText !== '+ Other (Custom)') {
                                        trainingName = selectedText.replace(' (Practical)', '');
                                      }
                                    }
                                    
                                    if (!trainingName || !date) {
                                      toast.error('Please select/enter training name and completion date');
                                      return;
                                    }
                                    
                                    if (!certFile) {
                                      toast.error('Certificate upload is mandatory');
                                      return;
                                    }
                                    
                                    // Generate a unique ID for the training
                                    const trainingId = `tr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                                    
                                    const newTraining = {
                                      id: trainingId,
                                      training_name: trainingName,
                                      training_type: trainingType,
                                      training_date: date,
                                      expiry_date: expiry || null,
                                      provider: provider || null,
                                      certificate_path: null // Will be set after upload
                                    };
                                    
                                    const updatedTraining = [...(editingStaff.profile?.training_records || []), newTraining];
                                    
                                    try {
                                      // First save the training record
                                      await apiCall(`/staff-profiles/${editingStaff.user.id}`, 'PUT', {
                                        user_id: editingStaff.user.id,
                                        address: editingStaff.profile?.address || null,
                                        phone_number: editingStaff.profile?.phone_number || null,
                                        date_of_birth: editingStaff.profile?.date_of_birth || null,
                                        documents: editingStaff.profile?.documents || null,
                                        employment: editingStaff.profile?.employment || null,
                                        training_records: updatedTraining
                                      }, token);
                                      
                                      // Then upload the certificate
                                      const formData = new FormData();
                                      formData.append('file', certFile);
                                      
                                      const uploadResponse = await fetch(
                                        `${process.env.REACT_APP_BACKEND_URL}/api/staff-profiles/${editingStaff.user.id}/trainings/${trainingId}/certificate`,
                                        {
                                          method: 'POST',
                                          headers: { 'Authorization': `Bearer ${token}` },
                                          body: formData
                                        }
                                      );
                                      
                                      if (uploadResponse.ok) {
                                        toast.success('Training and certificate added successfully');
                                      } else {
                                        toast.success('Training added, but certificate upload failed');
                                      }
                                      
                                      // Clear the input fields
                                      document.getElementById('new_training_name').value = '';
                                      document.getElementById('new_training_date').value = '';
                                      document.getElementById('new_training_expiry').value = '';
                                      document.getElementById('new_training_provider').value = '';
                                      document.getElementById('new_training_certificate').value = '';
                                      
                                      // Refresh data (stay in this staff's profile)
                                      refreshEditingStaff(editingStaff.user.id);
                                    } catch (error) {
                                      console.error('Training add error:', error);
                                      toast.error(`Failed to add training: ${error.detail || error.message || 'Unknown error'}`);
                                    }
                                  }}
                                >
                                  Add Training
                                </Button>
                              </div>
                            </div>
                          </div>
                          
                          <div className="sticky bottom-0 bg-white flex items-center space-x-2 pt-4 pb-2 border-t z-10">
                            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="save-profile-btn">💾 Save Profile</Button>
                            <Button type="button" variant="outline" onClick={() => setEditingStaff(null)}>
                              Cancel
                            </Button>
                            <span className="text-xs text-slate-400">Save applies all changes above (incl. Job Role)</span>
                          </div>
                        </form>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              )}

              {showAddStaff && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                  <Card className="w-full max-w-md">
                    <CardHeader>
                      <div className="flex justify-between items-center">
                        <CardTitle>Add New Staff Member</CardTitle>
                        <Button variant="ghost" onClick={() => setShowAddStaff(false)}>×</Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleAddStaff} className="space-y-4">
                        <div>
                          <Label htmlFor="staff_name">Full Name</Label>
                          <Input
                            id="staff_name"
                            value={newStaffData.name}
                            onChange={(e) => setNewStaffData({...newStaffData, name: e.target.value})}
                            placeholder="John Doe"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="staff_email">Email</Label>
                          <Input
                            id="staff_email"
                            type="email"
                            value={newStaffData.email}
                            onChange={(e) => setNewStaffData({...newStaffData, email: e.target.value})}
                            placeholder="john@example.com"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="staff_password">Temporary Password</Label>
                          <Input
                            id="staff_password"
                            type="password"
                            value={newStaffData.password}
                            onChange={(e) => setNewStaffData({...newStaffData, password: e.target.value})}
                            placeholder="Minimum 6 characters"
                            required
                            minLength={6}
                          />
                          <p className="text-xs text-slate-500 mt-1">Staff can change this after first login</p>
                        </div>
                        <div className="flex space-x-2">
                          <Button type="submit">Add Staff Member</Button>
                          <Button type="button" variant="outline" onClick={() => setShowAddStaff(false)}>
                            Cancel
                          </Button>
                        </div>
                      </form>
                    </CardContent>
                  </Card>
                </div>
              )}

            </TabsContent>

            <TabsContent value="archived" className="space-y-4">
              <div className="space-y-4">
                <h3 className="text-xl font-semibold">Archived Staff Records</h3>
                <p className="text-sm text-slate-600">
                  Staff accounts that have been deleted are archived here for compliance and record-keeping purposes.
                </p>
              </div>

              <div className="grid gap-4">
                {archivedStaff.map((archived) => (
                  <Card key={archived.user_id} className="bg-slate-50">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-3">
                            <h4 className="font-semibold text-lg">{archived.user?.name || 'Unknown'}</h4>
                            <Badge variant="secondary">Archived</Badge>
                            <Badge variant="outline">
                              {archived.user?.role || 'staff'}
                            </Badge>
                          </div>
                          <p className="text-slate-600 text-sm">{archived.user?.email || 'No email'}</p>
                          <div className="flex space-x-4 mt-2 text-xs text-slate-500">
                            <span>Archived On: {formatDateDMY(archived.archived_at)}</span>
                            {archived.user?.created_at && (
                              <span>Originally Joined: {formatDateDMY(archived.user.created_at)}</span>
                            )}
                          </div>
                          
                          {archived.profile && (
                            <div className="mt-4 space-y-3">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                <div>
                                  <span className="text-slate-500">Address:</span>
                                  <p className="font-medium">{archived.profile.address || 'Not provided'}</p>
                                </div>
                                <div>
                                  <span className="text-slate-500">Phone:</span>
                                  <p className="font-medium">{archived.profile.phone_number || 'Not provided'}</p>
                                </div>
                                <div>
                                  <span className="text-slate-500">DOB:</span>
                                  <p className="font-medium">{archived.profile.date_of_birth ? formatDateDMY(archived.profile.date_of_birth) : 'Not provided'}</p>
                                </div>
                              </div>
                              
                              <div className="p-3 bg-white rounded border">
                                <h5 className="font-medium mb-2">Documents</h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                  <div>Passport: {archived.profile.documents?.passport_number || 'Not provided'}</div>
                                  <div>Right to Work: {archived.profile.documents?.right_to_work ? '✅ Yes' : '❌ No'}</div>
                                  <div>DBS Check: {archived.profile.documents?.dbs_check ? '✅ Yes' : '❌ No'}</div>
                                  <div>
                                    DBS Issue: {archived.profile.documents?.dbs_issue_date || 'N/A'}
                                  </div>
                                </div>
                              </div>

                              <div className="p-3 bg-white rounded border">
                                <h5 className="font-medium mb-2">Employment Details</h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                  <div>NI Number: {archived.profile.employment?.ni_number || 'Not provided'}</div>
                                  <div>Start Date: {archived.profile.employment?.start_date ? formatDateDMY(archived.profile.employment.start_date) : 'Not provided'}</div>
                                  <div>Job Title: {archived.profile.employment?.job_title || 'Not provided'}</div>
                                  <div>Contract Type: {archived.profile.employment?.contract_type || 'Not provided'}</div>
                                </div>
                              </div>

                              {archived.profile.training_logs && archived.profile.training_logs.length > 0 && (
                                <div className="p-3 bg-white rounded border">
                                  <h5 className="font-medium mb-2">Training Records</h5>
                                  <div className="space-y-2">
                                    {archived.profile.training_logs.map((training, idx) => (
                                      <div key={idx} className="text-sm p-2 bg-slate-50 rounded">
                                        <div className="font-medium">{training.training_name}</div>
                                        <div className="text-xs text-slate-500 mt-1">
                                          Completed: {training.completion_date ? formatDateDMY(training.completion_date) : 'N/A'} | 
                                          Expires: {training.expiry_date ? formatDateDMY(training.expiry_date) : 'N/A'}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {archived.timesheets_count !== undefined && (
                            <div className="mt-3 text-sm text-slate-600">
                              <strong>Total Timesheets Submitted:</strong> {archived.timesheets_count}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {archivedStaff.length === 0 && (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <p className="text-slate-500">No archived staff records.</p>
                      <p className="text-xs text-slate-400 mt-1">When staff accounts are deleted, their data is archived here for record-keeping.</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            <TabsContent value="invoices" className="space-y-4">
              <div className="space-y-4">
                <h3 className="text-xl font-semibold">Invoice Management</h3>
                
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-4">
                    <h4 className="font-semibold mb-3">Generate New Invoice</h4>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                      <div>
                        <Label className="text-sm">Select Client</Label>
                        <select 
                          id="invoice-client-select"
                          className="w-full p-2 border rounded"
                        >
                          <option value="">Choose client...</option>
                          {clients.map(client => (
                            <option key={client.id} value={client.id}>
                              {client.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label className="text-sm">From (period start)</Label>
                        <Input
                          id="invoice-start-date"
                          type="date"
                          data-testid="invoice-start-date"
                        />
                        <p className="text-[10px] text-slate-400 mt-0.5">Leave empty for weekly (7 days before "To")</p>
                      </div>
                      <div>
                        <Label className="text-sm">To (invoice end date)</Label>
                        <Input
                          id="invoice-week-ending"
                          type="date"
                          data-testid="invoice-end-date"
                          defaultValue={new Date().toISOString().split('T')[0]}
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Invoice no. (optional)</Label>
                        <Input
                          id="invoice-custom-number"
                          type="number"
                          min="1"
                          placeholder="Auto"
                          data-testid="invoice-custom-number"
                        />
                        <p className="text-[10px] text-slate-400 mt-0.5">Leave empty to auto-number</p>
                      </div>
                      <div className="flex items-end">
                        <Button 
                          className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                          onClick={() => {
                            const clientId = document.getElementById('invoice-client-select').value;
                            const weekEnding = document.getElementById('invoice-week-ending').value;
                            const startDate = document.getElementById('invoice-start-date').value;
                            const customNumber = document.getElementById('invoice-custom-number').value;
                            
                            if (!clientId) {
                              toast.error('Please select a client');
                              return;
                            }
                            if (!weekEnding) {
                              toast.error('Please select the invoice end date');
                              return;
                            }
                            if (startDate && startDate > weekEnding) {
                              toast.error('"From" date must be before the "To" date');
                              return;
                            }
                            
                            generateInvoice(clientId, weekEnding, startDate || null, customNumber || null);
                          }}
                        >
                          Generate Invoice
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      Invoice will include all approved timesheets for the selected client in the chosen period — leave "From" empty for a weekly invoice, or set both dates for a custom/monthly period.
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid gap-4 mt-4">
                {invoices.map((invoice) => (
                  <Card key={invoice.id}>
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h4 className="font-semibold text-lg">Invoice #{invoice.invoice_number || 'N/A'}</h4>
                            {renumberInvoice?.id === invoice.id ? (
                              <div className="flex items-center space-x-1">
                                <Input
                                  type="number"
                                  min="1"
                                  className="h-7 w-24 text-sm"
                                  value={renumberInvoice.value}
                                  data-testid={`renumber-input-${invoice.id}`}
                                  onChange={(e) => setRenumberInvoice({ id: invoice.id, value: e.target.value })}
                                />
                                <Button size="sm" className="h-7 px-2 bg-blue-600 hover:bg-blue-700"
                                  data-testid={`renumber-save-${invoice.id}`}
                                  onClick={async () => {
                                    const n = parseInt(renumberInvoice.value, 10);
                                    if (!n || n < 1) { toast.error('Enter a valid number'); return; }
                                    try {
                                      const res = await apiCall(`/invoices/${invoice.id}/number`, 'PUT', { invoice_number: n }, token);
                                      toast.success(res.message);
                                      setRenumberInvoice(null);
                                      refetchData();
                                    } catch (e) {
                                      toast.error(e.detail || 'Failed to change number');
                                    }
                                  }}>
                                  Save
                                </Button>
                                <Button size="sm" variant="outline" className="h-7 px-2"
                                  onClick={() => setRenumberInvoice(null)}>✕</Button>
                              </div>
                            ) : (
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-blue-600"
                                data-testid={`renumber-btn-${invoice.id}`}
                                onClick={() => setRenumberInvoice({ id: invoice.id, value: invoice.invoice_number || '' })}>
                                ✏️ Change no.
                              </Button>
                            )}
                          </div>
                          <p className="text-slate-600 mt-1">Client: {invoice.client_name || 'Unknown'}</p>
                          <div className="flex items-center space-x-4 mt-2 text-sm text-slate-500">
                            <span>Invoice Date: {invoice.invoice_date ? formatDateDMY(invoice.invoice_date) : 'N/A'}</span>
                            <span>Week Ending: {invoice.week_ending ? formatDateDMY(invoice.week_ending) : 'N/A'}</span>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Badge variant="default">Generated</Badge>
                          <Button 
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700"
                            onClick={() => {
                              // Build invoice HTML with escaped values, then open via Blob URL (no document.write)
                              const invoiceHtml = `
                                <html>
                                  <head>
                                    <meta charset="utf-8" />
                                    <title>Invoice #${escapeHtml(invoice.invoice_number)}</title>
                                    <style>
                                      body { font-family: Arial, sans-serif; padding: 20px; }
                                      .logo-header { display: flex; align-items: center; margin-bottom: 20px; border-bottom: 3px solid #1e40af; padding-bottom: 15px; }
                                      .logo-header img { height: 60px; width: auto; margin-right: 20px; }
                                      .logo-header h1 { color: #1e40af; margin: 0; }
                                      h1 { color: #1e40af; }
                                      table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                                      th, td { padding: 12px; text-align: left; border: 1px solid #ddd; }
                                      th { background-color: #f1f5f9; font-weight: bold; }
                                      .header { margin-bottom: 20px; }
                                      .total-row { background-color: #f1f5f9; font-weight: bold; }
                                      .total-amount { color: #16a34a; font-size: 18px; }
                                      @media print { button { display: none; } }
                                    </style>
                                  </head>
                                  <body>
                                    <div class="logo-header">
                                      <img src="/koloth-logo.png" alt="Koloth Care Logo" />
                                      <div>
                                        <h1>INVOICE #${escapeHtml(invoice.invoice_number)}</h1>
                                      </div>
                                    </div>
                                    
                                    <div class="header">
                                      <p><strong>Client:</strong> ${escapeHtml(invoice.client_name)}</p>
                                      <p><strong>Address:</strong> ${escapeHtml(invoice.client_address) || 'N/A'}</p>
                                      <p><strong>Invoice Date:</strong> ${formatDateDMY(invoice.invoice_date)}</p>
                                      <p><strong>Week Ending:</strong> ${formatDateDMY(invoice.week_ending)}</p>
                                    </div>
                                    
                                    <h3>Timesheet Details</h3>
                                    <table>
                                      <thead>
                                        <tr>
                                          <th>Date</th>
                                          <th>Staff Name</th>
                                          <th>Role</th>
                                          <th>Start Time</th>
                                          <th>End Time</th>
                                          <th style="text-align: right;">Hours</th>
                                          <th style="text-align: right;">Rate (£/hr)</th>
                                          <th style="text-align: right;">Amount (£)</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        ${invoice.line_items?.map(item => `
                                          <tr>
                                            <td>${escapeHtml(item.date) || '-'}</td>
                                            <td>${escapeHtml(item.staff_name) || 'Unknown'}</td>
                                            <td style="text-transform: capitalize;">${escapeHtml(item.staff_type) || 'Staff'}</td>
                                            <td>${escapeHtml(item.time_period?.split(' - ')[0]) || '-'}</td>
                                            <td>${escapeHtml(item.time_period?.split(' - ')[1]) || '-'}</td>
                                            <td style="text-align: right;">${(item.hours || 0).toFixed(2)}</td>
                                            <td style="text-align: right;">${(item.rate || item.hourly_rate || 0).toFixed(2)}</td>
                                            <td style="text-align: right;">${(item.total || 0).toFixed(2)}</td>
                                          </tr>
                                        `).join('') || '<tr><td colspan="8">No items</td></tr>'}
                                        <tr class="total-row">
                                          <td colspan="5" style="text-align: right;"><strong>Total Hours:</strong></td>
                                          <td style="text-align: right;"><strong>${invoice.line_items?.reduce((sum, item) => sum + (item.hours || 0), 0).toFixed(2) || '0.00'}</strong></td>
                                          <td style="text-align: right;"><strong>Total Amount:</strong></td>
                                          <td style="text-align: right;" class="total-amount"><strong>£${(invoice.total || 0).toFixed(2)}</strong></td>
                                        </tr>
                                      </tbody>
                                    </table>
                                    
                                    <div style="margin-top: 40px;">
                                      <p><strong>Payment Terms:</strong> Due within 30 days</p>
                                      <p style="margin-top: 20px; font-size: 12px; color: #666;">Thank you for your business!</p>
                                    </div>
                                    
                                    <button onclick="window.print()" style="margin-top: 20px; padding: 10px 20px; background: #1e40af; color: white; border: none; border-radius: 5px; cursor: pointer;">Print Invoice</button>
                                  </body>
                                </html>
                              `;
                              const blobUrl = URL.createObjectURL(new Blob([invoiceHtml], { type: 'text/html;charset=utf-8' }));
                              window.open(blobUrl, '_blank', 'width=800,height=600');
                              setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
                            }}
                          >
                            Print/PDF
                          </Button>
                          <Button 
                            variant="destructive" 
                            size="sm"
                            onClick={async () => {
                              if (window.confirm('Delete this invoice?')) {
                                try {
                                  await apiCall(`/invoices/${invoice.id}`, 'DELETE', null, token);
                                  toast.success('Invoice deleted successfully');
                                  refetchData();
                                } catch (error) {
                                  toast.error('Failed to delete invoice');
                                }
                              }
                            }}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>

                      {/* Detailed Line Items */}
                      {invoice.line_items && invoice.line_items.length > 0 ? (
                        <div className="border-t pt-4">
                          <h5 className="font-semibold mb-3">Timesheet Details:</h5>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-slate-100">
                                <tr>
                                  <th className="p-2 text-left">Date</th>
                                  <th className="p-2 text-left">Staff Name</th>
                                  <th className="p-2 text-left">Role</th>
                                  <th className="p-2 text-left">Start Time</th>
                                  <th className="p-2 text-left">End Time</th>
                                  <th className="p-2 text-right">Hours</th>
                                  <th className="p-2 text-right">Rate (£/hr)</th>
                                  <th className="p-2 text-right">Amount (£)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {invoice.line_items.map((item, idx) => (
                                  <tr key={idx} className="border-b">
                                    <td className="p-2">{item.date || '-'}</td>
                                    <td className="p-2 font-medium">{item.staff_name || 'Unknown'}</td>
                                    <td className="p-2 capitalize">{item.staff_type || 'Staff'}</td>
                                    <td className="p-2">{item.time_period?.split(' - ')[0] || '-'}</td>
                                    <td className="p-2">{item.time_period?.split(' - ')[1] || '-'}</td>
                                    <td className="p-2 text-right font-medium">{(item.hours || 0).toFixed(2)}</td>
                                    <td className="p-2 text-right">{(item.rate || item.hourly_rate || 0).toFixed(2)}</td>
                                    <td className="p-2 text-right font-semibold">{(item.total || 0).toFixed(2)}</td>
                                  </tr>
                                ))}
                                <tr className="bg-slate-50 font-bold">
                                  <td colSpan="5" className="p-2 text-right">Total Hours This Week:</td>
                                  <td className="p-2 text-right">
                                    {invoice.line_items.reduce((sum, item) => sum + (item.hours || 0), 0).toFixed(2)}
                                  </td>
                                  <td className="p-2 text-right">Total Amount:</td>
                                  <td className="p-2 text-right text-green-600 text-lg">
                                    £{(invoice.total || 0).toFixed(2)}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ) : (
                        <div className="border-t pt-4">
                          <p className="text-slate-500 text-sm">No line items available for this invoice.</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
                {invoices.length === 0 && (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <p className="text-slate-500">No invoices generated yet.</p>
                      <p className="text-xs text-slate-400 mt-1">Complete shifts and approve timesheets to generate invoices.</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>
      
      {/* Document Upload Modal */}
      {showDocUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Upload Document</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Document Type Selection */}
              <div>
                <Label>Document Type *</Label>
                <select
                  value={docUploadType}
                  onChange={(e) => {
                    setDocUploadType(e.target.value);
                    setDocUploadDate('');
                    setDocUploadIsIssueDate(e.target.value === 'dbs');
                  }}
                  className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                >
                  <option value="">Select document type...</option>
                  <option value="dbs">DBS Certificate</option>
                  <option value="passport">Passport</option>
                  <option value="right_to_work">Right to Work</option>
                  <option value="share_code">Share Code</option>
                  <option value="training_certificate">Training Certificate</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              {/* File Selection */}
              <div>
                <Label>Select File *</Label>
                <Input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                  onChange={(e) => setDocUploadFile(e.target.files[0])}
                  className="cursor-pointer"
                />
                <p className="text-xs text-slate-500 mt-1">Supported: PDF, JPG, PNG, GIF, WebP (max 10MB)</p>
              </div>
              
              {/* Date Selection - shows based on document type */}
              {docUploadType && docUploadType !== 'other' && (
                <div>
                  <Label>
                    {docUploadType === 'dbs' ? 'Issue Date' : 'Expiry Date'}
                    {docUploadType !== 'training_certificate' && ' *'}
                  </Label>
                  <Input
                    type="date"
                    value={docUploadDate}
                    onChange={(e) => setDocUploadDate(e.target.value)}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {docUploadType === 'dbs' 
                      ? 'Enter the DBS issue date. Expiry will be calculated automatically (3 years from issue).'
                      : 'Enter the document expiry date for compliance tracking.'
                    }
                  </p>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowDocUploadModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    if (!docUploadType) {
                      toast.error('Please select a document type');
                      return;
                    }
                    if (!docUploadFile) {
                      toast.error('Please select a file');
                      return;
                    }
                    if (docUploadType !== 'other' && docUploadType !== 'training_certificate' && !docUploadDate) {
                      toast.error(`Please select ${docUploadType === 'dbs' ? 'an issue' : 'an expiry'} date`);
                      return;
                    }
                    
                    const formData = new FormData();
                    formData.append('file', docUploadFile);
                    
                    // Build query params
                    let queryParams = `document_type=${docUploadType}`;
                    if (docUploadDate) {
                      if (docUploadType === 'dbs') {
                        queryParams += `&issue_date=${docUploadDate}`;
                      } else {
                        queryParams += `&expiry_date=${docUploadDate}`;
                      }
                    }
                    
                    try {
                      const response = await fetch(
                        `${API}/staff-profiles/${docUploadStaffId}/documents/upload?${queryParams}`,
                        {
                          method: 'POST',
                          headers: {
                            'Authorization': `Bearer ${token}`
                          },
                          body: formData
                        }
                      );
                      
                      if (!response.ok) {
                        const error = await response.json();
                        throw new Error(error.detail || 'Upload failed');
                      }
                      
                      toast.success('Document uploaded successfully');
                      setShowDocUploadModal(false);
                      
                      // Refresh staff data
                      const updatedData = await apiCall('/staff-users', 'GET', null, token);
                      setStaffUsers(updatedData || []);
                      
                      // Refresh compliance dashboard
                      const compData = await apiCall('/compliance/dashboard', 'GET', null, token);
                      setComplianceDashboard(compData);
                    } catch (error) {
                      toast.error(error.message || 'Failed to upload document');
                    }
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Upload Document
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
    </MainLayout>
  );
};

// Staff Components
const StaffPanel = () => {
  const { token, user: current_user } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [myShifts, setMyShifts] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showCreateTimesheet, setShowCreateTimesheet] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  
  // Self-booking state
  const [selfBookings, setSelfBookings] = useState([]);
  const [clientsBasic, setClientsBasic] = useState([]);
  const [showSelfBookingForm, setShowSelfBookingForm] = useState(false);
  const [selfBookingData, setSelfBookingData] = useState({
    client_name: '',
    client_id: '',
    location: '',
    date: '',
    start_time: '',
    end_time: '',
    role: 'nurse',
    booked_by: '',
    notes: ''
  });
  
  // My compliance state
  const [myCompliance, setMyCompliance] = useState(null);
  const [loadingCompliance, setLoadingCompliance] = useState(false);
  const [activeTab, setActiveTab] = useState('available');
  
  // My Profile state
  const [myProfile, setMyProfile] = useState(null);
  const [editingMyProfile, setEditingMyProfile] = useState(false);
  const [myProfileData, setMyProfileData] = useState({
    phone_number: '',
    address: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: ''
  });

  // Load my profile data
  const loadMyProfile = useCallback(async () => {
    if (!token) return;
    try {
      const data = await apiCall('/my/profile', 'GET', null, token);
      setMyProfile(data);
      setMyProfileData({
        phone_number: data?.phone_number || '',
        address: data?.address || '',
        emergency_contact_name: data?.emergency_contact?.name || '',
        emergency_contact_phone: data?.emergency_contact?.phone || '',
        emergency_contact_relationship: data?.emergency_contact?.relationship || ''
      });
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  }, [token]);

  // Auto-load compliance data when My Compliance tab is activated
  const loadComplianceData = useCallback(async () => {
    if (!token) return;
    setLoadingCompliance(true);
    try {
      const data = await apiCall('/my/training-compliance', 'GET', null, token);
      setMyCompliance(data);
    } catch (error) {
      console.error('Error loading compliance:', error);
      toast.error('Failed to load compliance data');
    }
    setLoadingCompliance(false);
  }, [token]);

  // Auto-load compliance and profile when tab becomes active
  useEffect(() => {
    if (activeTab === 'my-compliance') {
      if (!myCompliance && !loadingCompliance) {
        loadComplianceData();
      }
      if (!myProfile) {
        loadMyProfile();
      }
    }
  }, [activeTab, myCompliance, loadingCompliance, loadComplianceData, myProfile, loadMyProfile]);

  const fetchData = useCallback(async () => {
    if (!token || !current_user) return;
    
    try {
      const [shiftsData, myShiftsData, timesheetsData, notificationsData, selfBookingsData] = await Promise.all([
        apiCall('/shifts', 'GET', null, token),
        apiCall('/shifts/my-shifts', 'GET', null, token),
        apiCall('/timesheets', 'GET', null, token),
        apiCall('/notifications', 'GET', null, token).catch(() => []),
        apiCall('/self-bookings', 'GET', null, token).catch(() => [])
      ]);
      apiCall('/clients/basic', 'GET', null, token).then((c) => setClientsBasic(c || [])).catch(() => {});
      setShifts(shiftsData);
      setMyShifts(myShiftsData);
      setTimesheets(timesheetsData);
      setNotifications(notificationsData || []);
      setSelfBookings(selfBookingsData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, [token, current_user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleShowInterest = async (shiftId) => {
    try {
      await apiCall('/shifts/show-interest', 'POST', { shift_id: shiftId }, token);
      toast.success('Interest registered! Admin will review and assign the shift.');
      fetchData();
    } catch (error) {
      toast.error(error.detail || 'Failed to register interest');
    }
  };

  const handleSubmitSelfBooking = async (e) => {
    e.preventDefault();
    try {
      await apiCall('/self-bookings', 'POST', selfBookingData, token);
      toast.success('Self-booking submitted! Admin will review it shortly.');
      setShowSelfBookingForm(false);
      setSelfBookingData({
        client_name: '',
        client_id: '',
        location: '',
        date: '',
        start_time: '',
        end_time: '',
        role: 'nurse',
        booked_by: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.detail || 'Failed to submit self-booking');
    }
  };

  // State for shift eligibility
  const [shiftEligibility, setShiftEligibility] = useState({});
  const [checkingEligibility, setCheckingEligibility] = useState({});

  // Check eligibility for a shift
  const checkShiftEligibility = async (shiftId) => {
    if (shiftEligibility[shiftId] || checkingEligibility[shiftId]) return;
    
    setCheckingEligibility(prev => ({...prev, [shiftId]: true}));
    try {
      const result = await apiCall(`/shifts/${shiftId}/eligibility/${current_user.id}`, 'GET', null, token);
      setShiftEligibility(prev => ({...prev, [shiftId]: result}));
    } catch (error) {
      console.error('Eligibility check failed:', error);
      // If API fails, assume eligible to not block workflow
      setShiftEligibility(prev => ({...prev, [shiftId]: { is_eligible: true, missing_trainings: [] }}));
    }
    setCheckingEligibility(prev => ({...prev, [shiftId]: false}));
  };

  const availableShifts = shifts.filter(shift => shift.status === "open");
  
  // Check eligibility for all available shifts on load
  useEffect(() => {
    if (availableShifts.length > 0 && current_user?.id) {
      availableShifts.forEach(shift => {
        checkShiftEligibility(shift.id);
      });
    }
  }, [availableShifts.length, current_user?.id]);

  return (
    <MainLayout>
      <Dashboard />
      
      {/* Notifications Banner */}
      {notifications.filter(n => !n.read).length > 0 && (
        <div className="mb-4 bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-yellow-700 font-medium">
                🔔 You have {notifications.filter(n => !n.read).length} new notification(s)
              </span>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowNotifications(!showNotifications)}
            >
              {showNotifications ? 'Hide' : 'View'}
            </Button>
          </div>
          
          {showNotifications && (
            <div className="mt-4 space-y-2">
              {notifications.filter(n => !n.read).map((notif) => (
                <div key={notif.id} className="bg-white p-3 rounded border border-yellow-200">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-semibold text-sm">{notif.title}</h4>
                      <p className="text-sm text-slate-600 mt-1">{notif.message}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        {new Date(notif.created_at).toLocaleString()}
                      </p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={async () => {
                        try {
                          await apiCall(`/notifications/${notif.id}/read`, 'PUT', null, token);
                          setNotifications(notifications.map(n => 
                            n.id === notif.id ? {...n, read: true} : n
                          ));
                        } catch (error) {
                          console.error('Mark read error:', error);
                        }
                      }}
                    >
                      Mark Read
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      <Tabs defaultValue="available" className="space-y-6" onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="available" data-testid="available-shifts-tab" className="text-xs sm:text-sm">Shifts</TabsTrigger>
          <TabsTrigger value="self-book" data-testid="self-book-tab" className="text-xs sm:text-sm">Self-Book</TabsTrigger>
          <TabsTrigger value="my-shifts" data-testid="my-shifts-tab" className="text-xs sm:text-sm">My Shifts</TabsTrigger>
          <TabsTrigger value="timesheets" data-testid="my-timesheets-tab" className="text-xs sm:text-sm">Timesheets</TabsTrigger>
          <TabsTrigger value="documents" data-testid="documents-tab" className="text-xs sm:text-sm">Documents</TabsTrigger>
          <TabsTrigger value="my-compliance" data-testid="my-profile-tab" className="text-xs sm:text-sm bg-purple-100">My Profile</TabsTrigger>
        </TabsList>

        <TabsContent value="documents" className="space-y-4">
          <MyAssignments token={token} apiCall={apiCall} />
          <DocumentsToSign token={token} apiCall={apiCall} />
        </TabsContent>

        {/* Self-Booking Tab */}
        <TabsContent value="self-book" className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-semibold text-slate-800">Self-Book a Shift</h2>
              <p className="text-slate-500 text-sm mt-1">Submit shifts you have arranged directly with a client for admin approval</p>
            </div>
            <Button 
              onClick={() => setShowSelfBookingForm(true)}
              className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
              data-testid="new-self-booking-btn"
            >
              + New Self-Booking
            </Button>
          </div>

          {/* Self-Booking Form */}
          {showSelfBookingForm && (
            <Card className="border-2 border-purple-200">
              <CardHeader>
                <CardTitle>Submit Self-Arranged Shift</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmitSelfBooking} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="client_name">Client / Hospital Name *</Label>
                      <select
                        id="client_select"
                        className="w-full h-10 px-3 border border-slate-200 rounded-md text-sm bg-white"
                        value={selfBookingData.client_id || (selfBookingData.client_name && !selfBookingData.client_id ? '__other__' : '')}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '__other__') {
                            setSelfBookingData({ ...selfBookingData, client_id: '', client_name: '' });
                          } else {
                            const c = clientsBasic.find((x) => x.id === v);
                            setSelfBookingData({ ...selfBookingData, client_id: v, client_name: c?.name || '' });
                          }
                        }}
                        required={!selfBookingData.client_name}
                        data-testid="self-booking-client"
                      >
                        <option value="">Select client…</option>
                        {clientsBasic.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                        <option value="__other__">Other (type name below)</option>
                      </select>
                      {!selfBookingData.client_id && (
                        <Input
                          className="mt-2"
                          placeholder="Type client/hospital name"
                          value={selfBookingData.client_name}
                          onChange={(e) => setSelfBookingData({...selfBookingData, client_name: e.target.value})}
                          required
                          data-testid="self-booking-client-other"
                        />
                      )}
                    </div>
                    <div>
                      <Label htmlFor="location">Location *</Label>
                      <Input
                        id="location"
                        placeholder="e.g. Ward 4, Main Building"
                        value={selfBookingData.location}
                        onChange={(e) => setSelfBookingData({...selfBookingData, location: e.target.value})}
                        required
                        data-testid="self-booking-location"
                      />
                    </div>
                    <div>
                      <Label htmlFor="booked_by">Booked By (Client Representative) *</Label>
                      <Input
                        id="booked_by"
                        placeholder="Name of the client staff who booked you"
                        value={selfBookingData.booked_by}
                        onChange={(e) => setSelfBookingData({...selfBookingData, booked_by: e.target.value})}
                        required
                        data-testid="self-booking-booked-by"
                      />
                    </div>
                    <div>
                      <Label htmlFor="date">Date *</Label>
                      <Input
                        id="date"
                        type="date"
                        value={selfBookingData.date}
                        onChange={(e) => setSelfBookingData({...selfBookingData, date: e.target.value})}
                        required
                        data-testid="self-booking-date"
                      />
                    </div>
                    <div>
                      <Label htmlFor="role">Your Role *</Label>
                      <Select 
                        value={selfBookingData.role} 
                        onValueChange={(value) => setSelfBookingData({...selfBookingData, role: value})}
                      >
                        <SelectTrigger data-testid="self-booking-role">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="nurse">Nurse</SelectItem>
                          <SelectItem value="carer">Carer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="start_time">Start Time *</Label>
                      <Input
                        id="start_time"
                        type="time"
                        value={selfBookingData.start_time}
                        onChange={(e) => setSelfBookingData({...selfBookingData, start_time: e.target.value})}
                        required
                        data-testid="self-booking-start"
                      />
                    </div>
                    <div>
                      <Label htmlFor="end_time">End Time *</Label>
                      <Input
                        id="end_time"
                        type="time"
                        value={selfBookingData.end_time}
                        onChange={(e) => setSelfBookingData({...selfBookingData, end_time: e.target.value})}
                        required
                        data-testid="self-booking-end"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="notes">Notes (optional)</Label>
                    <Input
                      id="notes"
                      placeholder="Any additional information for admin"
                      value={selfBookingData.notes}
                      onChange={(e) => setSelfBookingData({...selfBookingData, notes: e.target.value})}
                      data-testid="self-booking-notes"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" className="bg-purple-600 hover:bg-purple-700" data-testid="submit-self-booking">
                      Submit for Approval
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => {
                        setShowSelfBookingForm(false);
                        setSelfBookingData({
                          client_name: '', location: '', date: '', 
                          start_time: '', end_time: '', role: 'nurse', notes: ''
                        });
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* My Self-Bookings List */}
          <div>
            <h3 className="text-lg font-semibold text-slate-700 mb-4">My Self-Booking Requests</h3>
            {selfBookings.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <p className="text-slate-500">No self-booking requests yet.</p>
                  <p className="text-sm text-slate-400 mt-2">Click &quot;New Self-Booking&quot; to submit a shift you have arranged.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {selfBookings.map((booking) => (
                  <Card key={booking.id} className={`border-l-4 ${
                    booking.status === 'pending' ? 'border-l-orange-500' :
                    booking.status === 'approved' ? 'border-l-green-500' : 'border-l-red-500'
                  }`}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{booking.client_name}</span>
                            <Badge variant={
                              booking.status === 'pending' ? 'secondary' :
                              booking.status === 'approved' ? 'default' : 'destructive'
                            } className={
                              booking.status === 'pending' ? 'bg-orange-100 text-orange-800' :
                              booking.status === 'approved' ? 'bg-green-600' : ''
                            }>
                              {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600 mt-1">
                            {booking.location} • {formatDateDMY(booking.date)} • {booking.start_time} - {booking.end_time}
                          </p>
                          {booking.admin_notes && (
                            <p className="text-sm text-blue-600 mt-1">
                              Admin note: {booking.admin_notes}
                            </p>
                          )}
                          {booking.status === 'approved' && booking.hourly_rate && (
                            <p className="text-sm text-green-600 font-medium mt-1">
                              Approved at £{booking.hourly_rate}/hr
                            </p>
                          )}
                        </div>
                        <span className="text-xs text-slate-400">
                          {new Date(booking.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="available" className="space-y-6">
          <h2 className="text-2xl font-semibold text-slate-800">Available Shifts</h2>
          
          <div className="grid gap-4">
            {availableShifts.map((shift) => {
              const eligibility = shiftEligibility[shift.id];
              const isChecking = checkingEligibility[shift.id];
              const isEligible = eligibility?.is_eligible !== false;
              
              return (
                <Card key={shift.id} className={!isEligible ? 'border-orange-300 bg-orange-50' : ''}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg" data-testid={`available-shift-title-${shift.id}`}>{shift.title}</h3>
                        <p className="text-slate-600 mt-1">{shift.location}</p>
                        <div className="flex items-center space-x-4 mt-2 text-sm text-slate-500">
                          <span>{formatDateDMY(shift.date)}</span>
                          <span>{shift.start_time} - {shift.end_time}</span>
                        </div>
                        {shift.description && (
                          <p className="text-sm text-slate-600 mt-2">{shift.description}</p>
                        )}
                        
                        {/* Eligibility Warning */}
                        {!isEligible && eligibility?.missing_trainings?.length > 0 && (
                          <div className="mt-3 p-3 bg-orange-100 rounded-lg">
                            <p className="text-sm font-medium text-orange-800 mb-2">
                              ⚠️ Missing required trainings for this shift:
                            </p>
                            <ul className="text-sm text-orange-700 space-y-1">
                              {eligibility.missing_trainings.map((mt, i) => (
                                <li key={i} className="flex items-center gap-2">
                                  <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                                  <span>
                                    {mt.training} 
                                    <span className="text-xs ml-1 text-orange-600">
                                      ({mt.type === 'client_specific' ? `Required by ${mt.client}` : mt.reason})
                                    </span>
                                  </span>
                                </li>
                              ))}
                            </ul>
                            <p className="text-xs text-orange-600 mt-2">
                              Please complete these trainings with valid certificates to apply for this shift.
                            </p>
                          </div>
                        )}
                      </div>
                      <div className="text-right ml-4">
                        {isChecking ? (
                          <Badge variant="secondary">Checking eligibility...</Badge>
                        ) : shift.interested_staff.includes(current_user?.id) ? (
                          <Badge variant="secondary">Interest Registered</Badge>
                        ) : !isEligible ? (
                          <Badge variant="destructive" className="bg-orange-600">
                            Not Eligible
                          </Badge>
                        ) : (
                          <Button 
                            onClick={() => handleShowInterest(shift.id)}
                            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                            data-testid={`show-interest-${shift.id}`}
                          >
                            Show Interest
                          </Button>
                        )}
                        <div className="mt-2 text-sm text-slate-600">
                          £{shift.hourly_pay}/hr ({shift.staff_type})
                        </div>
                        {isEligible && !shift.interested_staff.includes(current_user?.id) && (
                          <Badge variant="outline" className="mt-2 bg-green-50 text-green-700 border-green-300">
                            ✓ Eligible
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {availableShifts.length === 0 && (
              <Card>
                <CardContent className="p-8 text-center">
                  <p className="text-slate-500">No available shifts at the moment.</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="my-shifts" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold text-slate-800">My Booked Shifts</h2>
            <Button 
              onClick={() => setShowCreateTimesheet(true)}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              data-testid="create-timesheet-button"
            >
              Create Timesheet
            </Button>
          </div>
          
          <div className="grid gap-4">
            {myShifts.map((shift) => (
              <Card key={shift.id} className="border-green-200 bg-green-50">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg" data-testid={`my-shift-title-${shift.id}`}>{shift.title}</h3>
                      <p className="text-slate-600 mt-1">{shift.location}</p>
                      <div className="flex items-center space-x-4 mt-2 text-sm text-slate-500">
                        <span>{formatDateDMY(shift.date)}</span>
                        <span>{shift.start_time} - {shift.end_time}</span>
                        <span className="font-medium text-green-600">£{shift.hourly_pay}/hr</span>
                      </div>
                      {shift.description && (
                        <p className="text-sm text-slate-600 mt-2">{shift.description}</p>
                      )}
                    </div>
                    <div className="flex flex-col space-y-2">
                      <Badge variant="default">Assigned</Badge>
                      <Button 
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={() => {
                          setSelectedShift(shift);
                          setShowCreateTimesheet(true);
                        }}
                      >
                        Submit Timesheet
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {myShifts.length === 0 && (
              <Card>
                <CardContent className="p-8 text-center">
                  <p className="text-slate-500">You have not been assigned any shifts yet.</p>
                  <p className="text-xs text-slate-400 mt-1">Show interest in available shifts to get assigned.</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="timesheets" className="space-y-6">
          <h2 className="text-2xl font-semibold text-slate-800">My Timesheets</h2>
          
          <div className="grid gap-4">
            {timesheets.map((timesheet) => (
              <Card key={timesheet.id}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <h3 className="font-semibold">Timesheet #{timesheet.id.slice(-8)}</h3>
                        <Badge variant={timesheet.status === 'pending' ? 'secondary' : timesheet.status === 'approved' ? 'default' : 'destructive'}>
                          {timesheet.status}
                        </Badge>
                      </div>
                      <div className="flex items-center space-x-4 text-sm mb-2">
                        <div className="flex items-center space-x-2">
                          <span className="text-slate-500">Staff:</span>
                          <span className="font-medium text-blue-700">{timesheet.staff_name || 'Unknown'}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-slate-500">Role:</span>
                          <span className="font-medium capitalize">{timesheet.staff_role || 'Staff'}</span>
                        </div>
                        {timesheet.client_name && (
                          <div className="flex items-center space-x-2">
                            <span className="text-slate-500">Client:</span>
                            <span className="font-medium text-green-700">{timesheet.client_name}</span>
                          </div>
                        )}
                        {timesheet.location && (
                          <div className="flex items-center space-x-2">
                            <span className="text-slate-500">Location:</span>
                            <span className="font-medium">{timesheet.location}</span>
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-slate-500">Date:</span>
                          <p className="font-medium">{formatDateDMY(timesheet.date)}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Time:</span>
                          <p className="font-medium">{timesheet.start_time} - {timesheet.end_time}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Break:</span>
                          <p className="font-medium">{timesheet.break_minutes} mins</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Total Hours:</span>
                          <p className="font-medium">{timesheet.total_hours}h</p>
                        </div>
                      </div>
                      {timesheet.comments && (
                        <div className="mb-2">
                          <span className="text-slate-500 text-sm">Comments:</span>
                          <p className="text-sm bg-amber-50 border border-amber-200 rounded p-2 mt-1">{timesheet.comments}</p>
                        </div>
                      )}
                      {timesheet.client_signature && (
                        <div>
                          <span className="text-slate-500 text-sm">Client Signature:</span>
                          {(timesheet.signer_name || timesheet.signer_designation) && (
                            <p className="text-sm font-medium">
                              Signed by: {timesheet.signer_name || 'N/A'}{timesheet.signer_designation ? ` — ${timesheet.signer_designation}` : ''}
                            </p>
                          )}
                          <div className="mt-1">
                            <img 
                              src={timesheet.client_signature} 
                              alt="Client Signature" 
                              className="border rounded max-h-20"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {timesheets.length === 0 && (
              <Card>
                <CardContent className="p-8 text-center">
                  <p className="text-slate-500">No timesheets created yet.</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* My Compliance Tab */}
        <TabsContent value="my-compliance" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold text-slate-800">My Profile</h2>
            <Button
              variant="outline"
              onClick={() => { loadComplianceData(); loadMyProfile(); }}
              disabled={loadingCompliance}
            >
              {loadingCompliance ? 'Loading...' : '🔄 Refresh'}
            </Button>
          </div>

          {/* Personal Information Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Personal Information</span>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setEditingMyProfile(!editingMyProfile)}
                >
                  {editingMyProfile ? 'Cancel' : '✏️ Edit'}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {editingMyProfile ? (
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    await apiCall('/my/profile', 'PUT', {
                      phone_number: myProfileData.phone_number,
                      address: myProfileData.address,
                      emergency_contact: {
                        name: myProfileData.emergency_contact_name,
                        phone: myProfileData.emergency_contact_phone,
                        relationship: myProfileData.emergency_contact_relationship
                      }
                    }, token);
                    toast.success('Profile updated successfully');
                    setEditingMyProfile(false);
                    loadMyProfile();
                  } catch (error) {
                    toast.error('Failed to update profile');
                  }
                }} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Full Name</Label>
                      <Input value={myProfile?.name || current_user?.name || ''} disabled className="bg-slate-100" />
                      <p className="text-xs text-slate-500 mt-1">Contact admin to change name</p>
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input value={myProfile?.email || current_user?.email || ''} disabled className="bg-slate-100" />
                    </div>
                    <div>
                      <Label>Phone Number *</Label>
                      <Input 
                        value={myProfileData.phone_number}
                        onChange={(e) => setMyProfileData({...myProfileData, phone_number: e.target.value})}
                        placeholder="e.g. 07712345678"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Address</Label>
                      <Input 
                        value={myProfileData.address}
                        onChange={(e) => setMyProfileData({...myProfileData, address: e.target.value})}
                        placeholder="Your full address"
                      />
                    </div>
                  </div>
                  
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-semibold mb-3">Emergency Contact</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label>Contact Name *</Label>
                        <Input 
                          value={myProfileData.emergency_contact_name}
                          onChange={(e) => setMyProfileData({...myProfileData, emergency_contact_name: e.target.value})}
                          placeholder="Full name"
                        />
                      </div>
                      <div>
                        <Label>Contact Phone *</Label>
                        <Input 
                          value={myProfileData.emergency_contact_phone}
                          onChange={(e) => setMyProfileData({...myProfileData, emergency_contact_phone: e.target.value})}
                          placeholder="Phone number"
                        />
                      </div>
                      <div>
                        <Label>Relationship</Label>
                        <Input 
                          value={myProfileData.emergency_contact_relationship}
                          onChange={(e) => setMyProfileData({...myProfileData, emergency_contact_relationship: e.target.value})}
                          placeholder="e.g. Spouse, Parent"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setEditingMyProfile(false)}>Cancel</Button>
                    <Button type="submit">Save Changes</Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-slate-500">Full Name</span>
                      <p className="font-medium">{myProfile?.name || current_user?.name || 'Not set'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-slate-500">Email</span>
                      <p className="font-medium">{myProfile?.email || current_user?.email || 'Not set'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-slate-500">Phone Number</span>
                      <p className="font-medium">{myProfile?.phone_number || 'Not set'}</p>
                    </div>
                    <div>
                      <span className="text-sm text-slate-500">Address</span>
                      <p className="font-medium">{myProfile?.address || 'Not set'}</p>
                    </div>
                  </div>
                  
                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-2 text-slate-700">Emergency Contact</h4>
                    {myProfile?.emergency_contact ? (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <span className="text-sm text-slate-500">Name</span>
                          <p className="font-medium">{myProfile.emergency_contact.name || 'Not set'}</p>
                        </div>
                        <div>
                          <span className="text-sm text-slate-500">Phone</span>
                          <p className="font-medium">{myProfile.emergency_contact.phone || 'Not set'}</p>
                        </div>
                        <div>
                          <span className="text-sm text-slate-500">Relationship</span>
                          <p className="font-medium">{myProfile.emergency_contact.relationship || 'Not set'}</p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-slate-500">No emergency contact set. Please add one.</p>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Documents Section */}
          <Card>
            <CardHeader>
              <CardTitle>Documents</CardTitle>
              <p className="text-sm text-slate-500">Your DBS and Share Code documents (managed by admin)</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* DBS */}
                <div className="p-4 border rounded-lg">
                  <h4 className="font-semibold mb-3">DBS Certificate</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">DBS Number:</span>
                      <span className="font-medium">{myProfile?.documents?.dbs_number || 'Not provided'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Issue Date:</span>
                      <span className="font-medium">{myProfile?.documents?.dbs_date ? formatDateDMY(myProfile.documents.dbs_date) : 'Not provided'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Expiry Date:</span>
                      <span className="font-medium">{myProfile?.documents?.dbs_expiry ? formatDateDMY(myProfile.documents.dbs_expiry) : 'Not provided'}</span>
                    </div>
                  </div>
                </div>
                
                {/* Share Code */}
                <div className="p-4 border rounded-lg">
                  <h4 className="font-semibold mb-3">Share Code (Right to Work)</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Share Code:</span>
                      <span className="font-medium">{myProfile?.documents?.share_code || 'Not provided'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Expiry Date:</span>
                      <span className="font-medium">{myProfile?.documents?.share_code_expiry ? formatDateDMY(myProfile.documents.share_code_expiry) : 'Not provided'}</span>
                    </div>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">Get your share code at gov.uk/prove-right-to-work</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Training Profile Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Training Profile</span>
                <div className="flex items-center gap-2">
                  {myCompliance && (
                    <Badge className={myCompliance.is_fully_compliant ? 'bg-green-600' : 'bg-red-600'}>
                      {myCompliance.summary.compliant}/{myCompliance.summary.total_required} Complete
                    </Badge>
                  )}
                </div>
              </CardTitle>
              <p className="text-sm text-slate-500">Add your training dates and upload certificates. Admin will verify.</p>
            </CardHeader>
            <CardContent>
              {!myCompliance ? (
                <div className="text-center py-8">
                  <Button onClick={loadComplianceData} disabled={loadingCompliance}>
                    {loadingCompliance ? 'Loading...' : 'Load Training Status'}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {myCompliance.training_details?.map((training, index) => (
                    <div 
                      key={index} 
                      className={`p-4 rounded-lg border ${
                        training.status === 'compliant' ? 'bg-green-50 border-green-200' :
                        training.status === 'expiring_soon' ? 'bg-yellow-50 border-yellow-200' :
                        training.status === 'pending_verification' ? 'bg-orange-50 border-orange-200' :
                        'bg-red-50 border-red-200'
                      }`}
                    >
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`font-medium ${
                              training.status === 'compliant' ? 'text-green-800' :
                              training.status === 'expiring_soon' ? 'text-yellow-800' :
                              training.status === 'pending_verification' ? 'text-orange-800' :
                              'text-red-800'
                            }`}>
                              {training.status === 'compliant' ? '✓' : training.status === 'expiring_soon' ? '⚠️' : training.status === 'pending_verification' ? '⏳' : '✗'} {training.training_name}
                            </span>
                            <Badge variant="outline" className="text-xs">
                              {training.training_type === 'practical' ? '🏋️ Practical' : '💻 Online'}
                            </Badge>
                          </div>
                          {training.completion_date && (
                            <p className="text-xs text-slate-600 mt-1">
                              Completed: {formatDateDMY(training.completion_date)} | Expires: {formatDateDMY(training.expiry_date)}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge
                            variant={training.status === 'compliant' ? 'default' : training.status === 'pending_verification' ? 'outline' : 'destructive'}
                            className={training.status === 'compliant' ? 'bg-green-600' : training.status === 'pending_verification' ? 'bg-orange-500 text-white border-orange-500' : ''}
                            data-testid={`my-training-status-${index}`}
                          >
                            {training.message}
                          </Badge>
                          
                          {/* Add/Update Training Button */}
                          {(training.status === 'missing' || training.status === 'not_completed' || training.status === 'no_certificate' || training.status === 'expired') && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs"
                              onClick={async () => {
                                const completionDate = window.prompt(
                                  `Enter completion date for ${training.training_name} (DD/MM/YYYY):`,
                                  new Date().toLocaleDateString('en-GB')
                                );
                                if (!completionDate) return;
                                
                                const expiryDate = window.prompt(
                                  `Enter expiry date (DD/MM/YYYY):`,
                                  (() => {
                                    const d = new Date();
                                    d.setFullYear(d.getFullYear() + 1);
                                    return d.toLocaleDateString('en-GB');
                                  })()
                                );
                                if (!expiryDate) return;
                                
                                try {
                                  await apiCall('/my/training', 'POST', {
                                    training_name: training.training_name,
                                    training_date: parseDMYtoISO(completionDate),
                                    expiry_date: parseDMYtoISO(expiryDate)
                                  }, token);
                                  toast.success('Training added! Please upload certificate.');
                                  loadComplianceData();
                                } catch (error) {
                                  toast.error('Failed to add training');
                                }
                              }}
                            >
                              ➕ Add Dates
                            </Button>
                          )}
                          
                          {/* Upload Certificate Button */}
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files[0];
                                if (!file) return;
                                
                                let completionDate = training.completion_date;
                                if (!completionDate) {
                                  completionDate = window.prompt(
                                    `Enter completion date for ${training.training_name} (DD/MM/YYYY):`,
                                    new Date().toLocaleDateString('en-GB')
                                  );
                                  if (!completionDate) return;
                                  completionDate = parseDMYtoISO(completionDate);
                                }
                                
                                // Ask for expiry date
                                const expiryDateInput = window.prompt(
                                  `Enter expiry date for ${training.training_name} (DD/MM/YYYY):`,
                                  (() => {
                                    const d = new Date();
                                    d.setFullYear(d.getFullYear() + 3); // Default 3 years
                                    return d.toLocaleDateString('en-GB');
                                  })()
                                );
                                if (!expiryDateInput) return;
                                
                                const formData = new FormData();
                                formData.append('file', file);
                                formData.append('training_name', training.training_name);
                                formData.append('completion_date', completionDate);
                                formData.append('expiry_date', parseDMYtoISO(expiryDateInput));
                                
                                try {
                                  const API_URL = process.env.REACT_APP_BACKEND_URL;
                                  const response = await fetch(`${API_URL}/api/staff/upload-training-certificate`, {
                                    method: 'POST',
                                    headers: { 'Authorization': `Bearer ${token}` },
                                    body: formData
                                  });
                                  
                                  if (response.ok) {
                                    toast.success('Certificate uploaded! Pending admin review.');
                                    loadComplianceData();
                                  } else {
                                    const error = await response.json();
                                    toast.error(error.detail || 'Upload failed');
                                  }
                                } catch (error) {
                                  toast.error('Failed to upload certificate');
                                }
                              }}
                            />
                            <Button size="sm" variant="outline" className="text-xs" asChild>
                              <span>📤 Upload Cert</span>
                            </Button>
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {showCreateTimesheet && (
        <TimesheetModal 
          shifts={myShifts}
          onClose={() => setShowCreateTimesheet(false)}
          onSuccess={() => {
            setShowCreateTimesheet(false);
            fetchData();
          }}
        />
      )}
    </MainLayout>
  );
};

// Timesheet Modal Component
const TimesheetModal = ({ shifts, onClose, onSuccess }) => {
  const { token } = useAuth();
  const [formData, setFormData] = useState({
    shift_id: '',
    date: '',
    start_time: '',
    end_time: '',
    break_minutes: 0,
    client_signature: null,
    signer_name: '',
    signer_designation: '',
    comments: ''
  });
  const [signatureData, setSignatureData] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate signature is present
    if (!signatureData) {
      toast.error('Client signature is required. Please have the client sign before submitting.');
      return;
    }
    if (!formData.signer_name.trim()) {
      toast.error('Please enter the name of the person signing.');
      return;
    }
    if (!formData.signer_designation.trim()) {
      toast.error('Please enter the designation of the person signing.');
      return;
    }
    
    try {
      const timesheetData = {
        ...formData,
        break_minutes: formData.break_minutes === '' ? 0 : formData.break_minutes,
        client_signature: signatureData
      };
      
      await apiCall('/timesheets', 'POST', timesheetData, token);
      toast.success('Timesheet created successfully!');
      onSuccess();
    } catch (error) {
      toast.error(error.detail || 'Failed to create timesheet');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold">Create Timesheet</h2>
            <Button variant="ghost" onClick={onClose} data-testid="close-timesheet-modal">
              ×
            </Button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="shift_id">Select Shift</Label>
              <Select value={formData.shift_id} onValueChange={(value) => {
                const s = shifts.find((x) => x.id === value);
                setFormData({
                  ...formData,
                  shift_id: value,
                  date: s?.date || formData.date,
                  start_time: s?.start_time || formData.start_time,
                  end_time: s?.end_time || formData.end_time,
                });
              }}>
                <SelectTrigger data-testid="timesheet-shift-select">
                  <SelectValue placeholder="Select a shift" />
                </SelectTrigger>
                <SelectContent>
                  {shifts.map((shift) => (
                    <SelectItem key={shift.id} value={shift.id}>
                      {shift.title} - {formatDateDMY(shift.date)} ({shift.start_time} - {shift.end_time})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  data-testid="timesheet-date-input"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="break_minutes">Break Time (minutes)</Label>
                <Input
                  id="break_minutes"
                  type="number"
                  min="0"
                  data-testid="timesheet-break-input"
                  value={formData.break_minutes}
                  onChange={(e) => setFormData({...formData, break_minutes: e.target.value === '' ? '' : parseInt(e.target.value) || 0})}
                  onFocus={(e) => {
                    // Clear the field if it's 0 when focused
                    if (e.target.value === '0') {
                      setFormData({...formData, break_minutes: ''});
                    }
                  }}
                  placeholder="0"
                />
              </div>
              <div>
                <Label htmlFor="start_time">Start Time</Label>
                <Input
                  id="start_time"
                  type="time"
                  data-testid="timesheet-start-time-input"
                  value={formData.start_time}
                  onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label htmlFor="end_time">End Time</Label>
                <Input
                  id="end_time"
                  type="time"
                  data-testid="timesheet-end-time-input"
                  value={formData.end_time}
                  onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                  required
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="comments">Comments (optional)</Label>
              <Textarea
                id="comments"
                data-testid="timesheet-comments-input"
                placeholder="e.g. No break taken, stayed an extra hour..."
                value={formData.comments}
                onChange={(e) => setFormData({...formData, comments: e.target.value})}
                rows={2}
              />
            </div>
            
            <div>
              <Label className="text-red-600 font-semibold">Client Signature (Required) *</Label>
              <p className="text-xs text-slate-600 mb-2">Client must sign to verify hours worked</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                <div>
                  <Label htmlFor="signer_name">Signer's Name *</Label>
                  <Input
                    id="signer_name"
                    data-testid="timesheet-signer-name-input"
                    placeholder="Full name of person signing"
                    value={formData.signer_name}
                    onChange={(e) => setFormData({...formData, signer_name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="signer_designation">Signer's Designation *</Label>
                  <Input
                    id="signer_designation"
                    data-testid="timesheet-signer-designation-input"
                    placeholder="e.g. Ward Manager, Nurse in Charge"
                    value={formData.signer_designation}
                    onChange={(e) => setFormData({...formData, signer_designation: e.target.value})}
                    required
                  />
                </div>
              </div>
              <div className="mt-2 border-2 border-blue-300 rounded p-4 bg-blue-50">
                <SignaturePad onSignatureChange={setSignatureData} />
              </div>
              {!signatureData && (
                <p className="text-xs text-red-600 mt-1">⚠️ Signature is required before submitting</p>
              )}
            </div>
            
            <div className="flex space-x-2">
              <Button type="submit" data-testid="submit-timesheet-button">Create Timesheet</Button>
              <Button type="button" variant="outline" onClick={onClose} data-testid="cancel-timesheet-button">
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Simple Signature Pad Component with Mobile Support
const SignaturePad = ({ onSignatureChange }) => {
  const canvasRef = React.useRef(null);
  const [isDrawing, setIsDrawing] = React.useState(false);

  // Get coordinates from mouse or touch event
  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Handle touch events
    if (e.touches && e.touches.length > 0) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top
      };
    }
    
    // Handle mouse events
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    e.preventDefault(); // Prevent scrolling on touch
    setIsDrawing(true);
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e);
    
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault(); // Prevent scrolling on touch
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e);
    
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#000';
    
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
  };

  const stopDrawing = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    setIsDrawing(false);
    
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL();
    onSignatureChange(dataUrl);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onSignatureChange('');
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={400}
        height={150}
        className="border-2 border-gray-400 rounded cursor-crosshair bg-white touch-none"
        style={{ touchAction: 'none', maxWidth: '100%' }}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
        data-testid="signature-canvas"
      />
      <Button 
        type="button" 
        variant="outline" 
        size="sm" 
        className="mt-2" 
        onClick={clearSignature}
        data-testid="clear-signature-button"
      >
        Clear Signature
      </Button>
    </div>
  );
};

// PWA Install Prompt Component
const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      toast.success('App installed successfully!');
    }
    
    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };

  if (!showInstallPrompt) return null;

  return (
    <div className="fixed bottom-4 right-4 z-40">
      <Card className="bg-white shadow-lg border-2 border-blue-200 max-w-sm">
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-slate-800">Install Koloth Care</h4>
              <p className="text-sm text-slate-600 mt-1">
                Add to your home screen for quick access and offline use
              </p>
              <div className="flex space-x-2 mt-3">
                <Button size="sm" onClick={handleInstallClick} data-testid="install-pwa-button">
                  Install
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => setShowInstallPrompt(false)}
                  data-testid="dismiss-install-prompt"
                >
                  Not Now
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Main App Component
function App() {
  const { user, loading } = useAuth();

  // Register Service Worker for PWA
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js')
          .then((registration) => {
            console.log('SW registered: ', registration);
          })
          .catch((registrationError) => {
            console.log('SW registration failed: ', registrationError);
          });
      });
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <div className="App">
        <Routes>
          <Route path="/privacy" element={<PrivacyNoticeStandalone />} />
          <Route path="/" element={
            user ? (
              user.role === 'admin' ? <AdminPanel /> : <StaffPanel />
            ) : (
              <Login />
            )
          } />
        </Routes>
        
        {/* PWA Install Prompt */}
        {user && <PWAInstallPrompt />}
        
        {/* Cookie Consent Banner */}
        <CookieConsent />
        
        <Toaster position="top-right" />
      </div>
    </BrowserRouter>
  );
}

export default function AppWithAuth() {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}